import { fetchLatestClinicalIntakeData } from '../../services/clinicalIntake.service';
import templateLanguageService from '../../services/templateLanguage.service';
import { canonicalizeM11AppendixSectionTitle } from '../../utils/protocolExportContent';
import { mergeStudyPartsNotesForGeneration } from '../../constants/studyPartNotesMerge';

// Clinical Intake Data Structure - UPDATED TO NEW STRUCTURE
export const CLINICAL_INTAKE_DATA = {
  study_identification: {
    protocol_number: '',
    preferred_short_title: '',
    preferred_study_identifier: '',
    date_of_project_creation: '',
    comments: ''
  },
  study_overview: {
    study_type_dropdown: '',
    study_design: {},
    number_of_study_parts_new: 0,
    study_parts_cohorts: {},
    study_parts_part_names: {},
    study_parts_cohort_names: {},
    study_parts: {},
    comments: ''
  },
  endpoints_objectives: {
    primary_objective_endpoints: '',
    key_secondary_objectives_endpoints: '',
    secondary_objectives_endpoints: '',
    exploratory_objectives_endpoints: ''
  },
  target_population: {
    conditions_related_to_primary_disease: '',
    tissue_sample_procedure_compliance: '',
    patient_performance_status: '',
    life_expectancy: '',
    organ_function_lab_parameters: '',
    concomitant_meds_washout: '',
    comorbidities_infections: '',
    reproductive_status_contraception: '',
    eligibility_criteria: ''
  },
  study_treatments: {
    num_regimens: 0,
    num_agents: 0,
    additional_information: [],
    treatment_table: {},
    regimens_per_part: {},
    regimen_arms: {},
    control_regimen: '',
    concomitant_medications_allowed: '',
    concomitant_medications_prohibited: '',
    fda_pregnancy_risk_categories: ''
  },
  discontinuation_rules: {
    discontinuation_trial_intervention_individual: '',
    permanent_discontinuation_trial_intervention: '',
    temporary_discontinuation_trial_intervention: '',
    rechallenge: '',
    participant_discontinuation_withdrawal: '',
    management_loss_to_followup: '',
    screen_failure_rescreening_rules: '',
    additional_information: []
  },
  study_assessments: {
    screening_baseline_assessments: '',
    efficacy_assessments: '',
    safety_assessments_general: '',
    physical_examination: '',
    vital_signs: '',
    electrocardiograms: '',
    clinical_laboratory_assessment: '',
    pregnancy_testing: '',
    suicidal_ideation_risk_monitoring: '',
    pharmacokinetics: '',
    biomarkers: '',
    genetics_pharmacogenomics: '',
    pharmacodynamic_biomarkers: '',
    other_biomarkers: '',
    comments: ''
  },
  statistical_considerations: {
    analysis_sets: [],
    primary_objective_analysis: '',
    secondary_objective_analysis: '',
    exploratory_analysis: '',
    safety_analysis: '',
    other_analyses: '',
    interim_analyses: '',
    sample_size_determination: ''
  },
  regulatory_requirements: {
    regulatory_ethical_considerations: '',
    investigator_responsibilities: '',
    sponsor_responsibilities: '',
    informed_consent_rescreening: '',
    informed_consent_remaining_samples: '',
    committees: '',
    insurance_indemnity: '',
    risk_based_quality_management: '',
    data_governance: '',
    data_protection: '',
    source_data: '',
    protocol_deviations: '',
    early_site_closure: '',
    data_dissemination: '',
    comments: ''
  },
  template_language_fields: {
    definitions_childbearing_potential: '', // 5.4.1
    contraception_requirements: '', // 5.4.2
    liver_event_stopping_criteria: '', // 7.1.1.1
    liver_event_stopping_flowchart_mermaid: '', // 7.1.1.1 flowchart
    liver_restart_rechallenge: '', // 7.1.3.1
    definition_adverse_event: '', // 9.1.1
    definition_serious_adverse_event: '', // 9.1.2
    definitions_product_complaints: '', // 9.1.3
    ae_reporting_timing_procedures: '', // 9.2
    ae_reporting_timing: '', // 9.2.1
    ae_reporting_collection_procedure: '', // 9.2.2
    ae_reporting_reporting: '', // 9.2.3
    ae_reporting_special_interest: '', // 9.2.4
    ae_reporting_disease_related_events: '', // 9.2.5
    pregnancy_postpartum_information: '', // 9.3
    participants_pregnant_during_trial: '', // 9.3.1
    special_safety_situations: '', // 9.4
    comments: ''
  },
  background_information: {
    disease_epidemiology: '',
    current_standard_of_care: '',
    outcomes_current_soc: '',
    benefits_investigational: '',
    risks_investigational: '',
    purpose_of_trial: '',
    rationale_for_estimands: '',
    rationale_for_interventional_type: '',
    rationale_for_control_method: '',
    rationale_for_trial_duration: '',
    rationale_for_adaptive_design: '',
    rationale_for_interim_analysis: '',
    rationale_for_other_design_aspects: '',
    rationale_for_trial_population: ''
  },
  study_monitoring_logistics: {
    data_collection_method: '',
    monitoring_frequency: '',
    on_site_or_remote: '',
    key_contacts: {
      sponsor_contact: '',
      cro_contact: ''
    }
  },
  document_uploads: {},
  trialSchemaSvg: '',
  appendices: [],
  additional_comments: '',
  fieldTemplateSelections: {},
  section9TemplateSelections: {}
};

/** Compact document summaries for AI section generation */
export const summarizeDocumentUploadsForGeneration = (documentUploads) => {
  if (!documentUploads || typeof documentUploads !== 'object') return null;
  const uploaded = documentUploads.uploaded_files || {};
  const summaries = [];
  Object.entries(uploaded).forEach(([docType, files]) => {
    (Array.isArray(files) ? files : []).forEach((file) => {
      const summary = String(file?.contentSummary || '').trim();
      if (!summary) return;
      summaries.push({
        documentType: docType,
        filename: file.originalname || file.name || file.filename || '',
        summary,
        intakeSectionsForAutoPopulate: Array.isArray(file.intakeSectionsForAutoPopulate)
          ? file.intakeSectionsForAutoPopulate
          : [],
        additionalDetails: file.additionalDetails || '',
      });
    });
  });
  return {
    document_table: documentUploads.document_table || [],
    summaries,
    comments: documentUploads.comments || {},
  };
};

// Field mapping for section-specific AI generation
export const SECTION_FIELD_MAPPINGS = {
  '1. PROTOCOL SUMMARY': {
    // Protocol Identification
    'protocol_number': ['study_identification.protocol_number'],
    'preferred_short_title': ['study_identification.preferred_short_title'],
    'protocol_title': ['study_identification.protocol_title'],
    'sponsor_name': ['study_identification.sponsor_name'],
    'compound_code': ['study_identification.compound_code'],
    'investigational_product_name': ['study_identification.investigational_product_name'],
    'development_phase': ['study_identification.development_phase'],
    
    // Primary and Secondary Objectives (1.1.1)
    'primary_objectives_endpoints': ['endpoints_objectives.primary_objective_endpoints'],
    'key_secondary_objectives_endpoints': ['endpoints_objectives.key_secondary_objectives_endpoints'],
    'secondary_objectives_endpoints': ['endpoints_objectives.secondary_objectives_endpoints'],
    'exploratory_objectives_endpoints': ['endpoints_objectives.exploratory_objectives_endpoints'],
    
    // Overall Design - Intervention & Population (1.1.2)
    'intervention_treatments': [
      'study_treatments.regimen_arms',
      'study_treatments.num_regimens',
      'study_treatments.num_agents',
      'study_treatments.treatment_table',
      'study_treatments.control_regimen',
      'study_treatments.additional_information'
    ],
    
    'population_diagnosis_condition': [
      'target_population.conditions_related_to_primary_disease',
      'target_population.eligibility_criteria'
    ],
    
    'population_characteristics': [
      'target_population.tissue_sample_procedure_compliance',
      'target_population.patient_performance_status',
      'target_population.life_expectancy',
      'target_population.organ_function_lab_parameters',
      'target_population.concomitant_meds_washout',
      'target_population.comorbidities_infections',
      'target_population.reproductive_status_contraception'
    ],
    
    // Study Design (Complete)
    'study_design_complete': [
      'study_overview.study_type_dropdown',
      'study_overview.study_phase',
      'study_overview.number_of_study_parts_new',
      'study_overview.study_parts_cohorts',
      'study_overview.study_parts',
      'study_overview.comments',
      //'study_overview.template_selection',
      'study_overview.study_design.single_arm',
      'study_overview.study_design.multi_arm_randomized',
      'study_overview.study_design.multi_arm_non_randomized',
      'study_overview.study_design.crossover',
      'study_overview.study_design.multi_part_study',
      'study_overview.study_design.single_part_multi_cohort',
      'study_overview.study_design.multi_part_multi_cohort',
      'study_overview.study_design.platform_umbrella_basket',
      'study_overview.study_design.adaptive'
    ],
    
    // Trial Schema Configuration (parts, cohorts, regimens, treatment table)
    'trial_schema_configuration': [
      'study_overview.number_of_study_parts_new',
      'study_overview.trial_schema_configuration',
      'study_overview.study_parts_cohorts',
      'study_overview.study_parts_part_names',
      'study_overview.study_parts_cohort_names',
      'study_overview.study_parts',
      'study_treatments.num_regimens',
      'study_treatments.regimens_per_part',
      'study_treatments.regimen_arms',
      'study_treatments.treatment_table',
      'trialSchemaSvg',
    ],
    
    // Assessments Summary
    'assessments_overview': [
      'study_assessments.screening_baseline_assessments',
      'study_assessments.efficacy_assessments',
      'study_assessments.safety_assessments_general',
      'study_assessments.pharmacokinetics',
      'study_assessments.biomarkers'
    ],
    
    // Statistical Design
    'statistical_overview': [
      'statistical_considerations.analysis_sets',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis',
      'statistical_considerations.interim_analyses',
      'statistical_considerations.sample_size_determination'
    ],
    
    // Discontinuation Rules
    'discontinuation_overview': [
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'discontinuation_rules.temporary_discontinuation_trial_intervention'
    ],
    
    // FDA Pregnancy Categories
    'pregnancy_risk': [
      'study_treatments.fda_pregnancy_risk_categories'
    ],
    
    // Regulatory & Committees
    'regulatory_committees': [
      'regulatory_requirements.committees',
      'regulatory_requirements.investigator_responsibilities',
      'regulatory_requirements.sponsor_responsibilities',
      'regulatory_requirements.insurance_indemnity'
    ],
    
    // Concomitant Medications
    'concomitant_medications': [
      'study_treatments.concomitant_medications_allowed',
      'study_treatments.concomitant_medications_prohibited'
    ]
  },
  '2. INTRODUCTION': {
    // CSV 2.1 / 2.2.*: map explicit intro subheadings to Background Information
    'purpose_of_trial': [
      'background_information.disease_epidemiology',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc',
      'background_information.purpose_of_trial'
    ],
    'assessment_of_risks_and_benefits': [
      'background_information.benefits_investigational',
      'background_information.risks_investigational',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc'
    ],
    'risk_summary_and_mitigation': [
      'background_information.risks_investigational',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc'
    ],
    'benefit_summary': [
      'background_information.benefits_investigational',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc'
    ],
    'overall_risk_benefit_assessment': [
      'background_information.benefits_investigational',
      'background_information.risks_investigational',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc'
    ],
    'Investigational_Product_Name': [
      // Complete Section 6 - Study Treatments
      'study_treatments.regimen_arms',
      'study_treatments.control_regimen',
      'study_treatments.concomitant_medications_allowed',
      'study_treatments.concomitant_medications_prohibited',
      'study_treatments.fda_pregnancy_risk_categories',
      'study_treatments.additional_information',
      'study_treatments.treatment_table',
      'study_treatments.num_regimens',
      'study_treatments.num_agents'
    ],
    'Indication': [
      'target_population.conditions_related_to_primary_disease',
      'target_population.eligibility_criteria',
      'target_population.patient_performance_status',
      'target_population.organ_function_lab_parameters',
      'target_population.concomitant_meds_washout',
      'target_population.comorbidities_infections',
      'target_population.reproductive_status_contraception',
      'target_population.tissue_sample_procedure_compliance',
      'target_population.life_expectancy',
      'background_information.disease_epidemiology',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc'
    ],
    'Primary_Research_Questions': [
      // Complete Section 3 - Study Overview and Design - ALL FIELDS
      'study_overview.study_type_dropdown',
      'study_overview.study_phase',
      'study_overview.number_of_study_parts_new',
      'study_overview.study_parts_cohorts',
      'study_overview.study_parts',
      'study_overview.comments',
      // Complete Study Design Table - ALL fields
      'study_overview.study_design.single_arm',
      'study_overview.study_design.multi_arm_randomized',
      'study_overview.study_design.multi_arm_non_randomized',
      'study_overview.study_design.crossover',
      'study_overview.study_design.multi_part_study',
      'study_overview.study_design.single_part_multi_cohort',
      'study_overview.study_design.multi_part_multi_cohort',
      'study_overview.study_design.platform_umbrella_basket',
      'study_overview.study_design.adaptive',
      'study_overview.study_parts_cohort_names',
      'study_overview.study_parts_part_names',
      //'study_overview.template_selection',
      // Endpoints and Objectives
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.key_secondary_objectives_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'endpoints_objectives.exploratory_objectives_endpoints'
    ],
    'Key_Treatment_Risks': [
      'background_information.risks_investigational',
      'background_information.outcomes_current_soc',
      'background_information.current_standard_of_care'
    ],
    'Key_Procedural_Risks_e.g.,_biopsy': [
      'background_information.risks_investigational',
      'background_information.purpose_of_trial',
      'study_overview.study_design.single_arm',
      'study_overview.study_design.multi_arm_randomized'
    ],
    'Anticipated_Benefits': [
      'background_information.benefits_investigational',
      'background_information.outcomes_current_soc',
      'background_information.current_standard_of_care'
    ],
    'Basis_for_Risk_Benefit_Assessment': [
      'background_information.rationale_for_estimands',
      'background_information.rationale_for_interventional_type',
      'background_information.outcomes_current_soc',
      'background_information.current_standard_of_care',
      'background_information.disease_epidemiology',
      'background_information.risks_investigational',
      'background_information.benefits_investigational'
    ]
  },
  '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS': {
    'primary_objectives_endpoints': [
      'endpoints_objectives.primary_objective_endpoints'
    ],
    'key_secondary_objectives_endpoints': [
      'endpoints_objectives.key_secondary_objectives_endpoints'
    ],
    'secondary_objectives_endpoints': [
      'endpoints_objectives.secondary_objectives_endpoints'
    ],
    'exploratory_objectives_endpoints': [
      'endpoints_objectives.exploratory_objectives_endpoints'
    ],
    'target_population': [
      // Complete Trial Population
      'target_population.conditions_related_to_primary_disease',
      'target_population.tissue_sample_procedure_compliance',
      'target_population.patient_performance_status',
      'target_population.life_expectancy',
      'target_population.organ_function_lab_parameters',
      'target_population.concomitant_meds_washout',
      'target_population.comorbidities_infections',
      'target_population.reproductive_status_contraception',
      'target_population.eligibility_criteria'
    ],
    'arm_1_investigational': [
      // Complete Section 6 - Study Treatments
      'study_treatments.regimen_arms',
      'study_treatments.control_regimen',
      'study_treatments.concomitant_medications_allowed',
      'study_treatments.concomitant_medications_prohibited',
      'study_treatments.fda_pregnancy_risk_categories',
      'study_treatments.additional_information',
      'study_treatments.treatment_table',
      'study_treatments.num_regimens',
      'study_treatments.num_agents'
    ],
    'arm_2_control': [
      'study_treatments.control_regimen',
      'study_treatments.concomitant_medications_allowed',
      'study_treatments.concomitant_medications_prohibited'
    ],
    'primary_analyses': [
      'statistical_considerations.primary_objective_analysis'
    ],
    'secondary_analyses': [
      'statistical_considerations.secondary_objective_analysis'
    ],
    'intercurrent_events_handling': [
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis'
    ],
    'complete_study_overview_and_design': [
      // Complete Section 3 - Study Overview and Design - ALL FIELDS
      'study_overview.study_type_dropdown',
      'study_overview.study_phase',
      'study_overview.number_of_study_parts_new',
      'study_overview.study_parts_cohorts',
      'study_overview.study_parts',
      'study_overview.comments',
      // Complete Study Design Table - ALL fields
      'study_overview.study_design.single_arm',
      'study_overview.study_design.multi_arm_randomized',
      'study_overview.study_design.multi_arm_non_randomized',
      'study_overview.study_design.crossover',
      'study_overview.study_design.multi_part_study',
      'study_overview.study_design.single_part_multi_cohort',
      'study_overview.study_design.multi_part_multi_cohort',
      'study_overview.study_design.platform_umbrella_basket',
      'study_overview.study_design.adaptive',
      'study_overview.study_parts_cohort_names',
      'study_overview.study_parts_part_names',
      //'study_overview.template_selection',
      // Endpoints and Objectives
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.key_secondary_objectives_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'endpoints_objectives.exploratory_objectives_endpoints'
    ],
    'complete_statistical_considerations': [
      // Complete Statistical Considerations
      'statistical_considerations.analysis_sets',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis',
      'statistical_considerations.exploratory_analysis',
      'statistical_considerations.safety_analysis',
      'statistical_considerations.other_analyses',
      'statistical_considerations.interim_analyses',
      'statistical_considerations.sample_size_determination'
    ]
  },
  '4. TRIAL DESIGN': {
    'trial_design_description': [
      'study_overview.study_type_dropdown',
      'study_overview.study_phase',
      'study_overview.study_design',
      'study_overview.number_of_study_parts_new',
      'study_overview.study_parts_cohorts',
      'study_overview.study_parts',
      'study_overview.comments',
      'regulatory_requirements.committees',
      'statistical_considerations.interim_analyses',
      'study_treatments.treatment_table',
      'study_identification.preferred_short_title'
    ],
    'stakeholder_input': [
      // Stakeholder input into design is captured on the study-parts matrix (per cohort)
      'study_overview.study_parts',
      'regulatory_requirements.comments',
      'study_overview.comments'
    ],
    'rationale_estimands': [
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis',
      'target_population.conditions_related_to_primary_disease',
      'background_information.rationale_for_estimands',
    ],
    'rationale_intervention_model': [
      'study_overview.study_type_dropdown',
      'study_overview.study_design',
      'study_treatments.treatment_table',
      'study_overview.number_of_study_parts_new',
      'background_information.rationale_for_interventional_type'
    ],
    'rationale_control_type': [
      'study_treatments.control_regimen',
      'background_information.current_standard_of_care',
      'background_information.disease_epidemiology',
      'background_information.outcomes_current_soc',
      'background_information.rationale_for_control_method',
    ],
    'rationale_trial_duration': [
      'study_overview.study_parts',
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'study_assessments.efficacy_assessments',
      'background_information.rationale_for_trial_duration',
    ],
    'rationale_adaptive_design': [
      'study_overview.study_design',
      'background_information.rationale_for_adaptive_design'
    ],
    'rationale_interim_analysis': [
      'statistical_considerations.interim_analyses',
      'background_information.rationale_for_interim_analysis'
    ],
    'rationale_other_design': [
      'background_information.rationale_for_other_design_aspects',
      'study_overview.comments'
    ],
    'trial_stopping_rules': [
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'discontinuation_rules.temporary_discontinuation_trial_intervention',
      'discontinuation_rules.discontinuation_trial_intervention_individual'
    ],
    'trial_start_end': [
      'regulatory_requirements',
      'study_overview'
    ],
    'access_after_trial': [
      'study_treatments.additional_information',
      'study_overview.comments'
    ]
  },
  '5. TRIAL POPULATION': {
    'population_description': [
      'study_overview.study_parts',
      'target_population.conditions_related_to_primary_disease',
      'target_population.patient_performance_status',
      'target_population.life_expectancy',
      'target_population.organ_function_lab_parameters',
      'target_population.prior_therapy_requirements',
      'target_population.eligibility_criteria',
      'background_information.rationale_for_trial_population',
    ],
    'inclusion_criteria': [
      'study_overview.study_parts',
      'target_population.conditions_related_to_primary_disease',
      'target_population.tissue_sample_procedure_compliance',
      'target_population.patient_performance_status',
      'target_population.organ_function_lab_parameters',
      'target_population.concomitant_meds_washout',
      'target_population.reproductive_status_contraception',
      'target_population.eligibility_criteria'
    ],
    'exclusion_criteria': [
      'study_overview.study_parts',
      'target_population.comorbidities_infections',
      'target_population.concomitant_meds_washout',
      'target_population.reproductive_status_contraception',
      'target_population.eligibility_criteria'
    ],
    'contraception_definitions': [
      'target_population.reproductive_status_contraception',
      'study_overview.study_parts'
    ],
    'contraception_requirements': [
      'target_population.reproductive_status_contraception',
      'study_overview.study_parts'
    ],
    'lifestyle_restrictions': [
      'study_treatments.concomitant_medications_prohibited',
      'study_treatments.concomitant_medications_allowed',
      'study_assessments'
    ],
    // CSV 5.5.1–5.5.4: these are stored per cohort inside study_overview.study_parts
    'meals_and_dietary_restrictions': [
      'study_overview.study_parts'
    ],
    'caffeine_alcohol_tobacco_and_other_restrictions': [
      'study_overview.study_parts'
    ],
    'physical_activity_restrictions': [
      'study_overview.study_parts'
    ],
    'other_activity_restrictions': [
      'study_overview.study_parts'
    ],
    'screen_failure_rescreening': [
      'discontinuation_rules.screen_failure_rescreening_rules',
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'discontinuation_rules.temporary_discontinuation_trial_intervention',
      'discontinuation_rules.participant_discontinuation_withdrawal'
    ]
  },
  '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY': {
    'investigational_intervention_description': [
      'study_identification.preferred_short_title',
      'study_overview.study_type_dropdown',
      'study_overview.study_design',
      'study_treatments.regimen_arms',
      'study_treatments.treatment_table',
      'study_treatments.num_agents',
      'study_treatments.num_regimens'
    ],
    'dose_regimen_rationale': [
      'background_information.purpose_of_trial',
      'background_information.rationale_for_interventional_type',
      'background_information.benefits_investigational',
      'study_treatments.treatment_table'
    ],
    'intervention_administration': [
      'study_treatments.treatment_table',
      'study_treatments.concomitant_medications_allowed',
      'discontinuation_rules.discontinuation_trial_intervention_individual'
    ],
    'dose_modification': [
      'study_treatments.treatment_table',
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'discontinuation_rules.temporary_discontinuation_trial_intervention',
      'discontinuation_rules.rechallenge'
    ],
    'overdose_management': [
      'study_treatments.treatment_table',
      'study_treatments.fda_pregnancy_risk_categories'
    ],
    'preparation_storage_accountability': [
      'study_treatments.treatment_table',
      'study_treatments.additional_information'
    ],
    // CSV has separate 6.6.1/6.6.2/6.6.3 headings; these live inside treatment_table per regimen.
    'preparation_of_investigational_trial_intervention': [
      'study_treatments.treatment_table'
    ],
    'storage_and_handling_of_investigational_trial_intervention': [
      'study_treatments.treatment_table'
    ],
    'accountability_of_investigational_trial_intervention': [
      'study_treatments.treatment_table'
    ],
    'assignment_randomization_blinding': [
      'study_overview.study_design',
      'study_overview.study_parts',
      'background_information.rationale_for_other_design_aspects',
      'study_overview.comments'
    ],
    'intervention_adherence': [
      'study_assessments.pharmacokinetics',
      'study_assessments.comments',
      'statistical_considerations.other_analyses'
    ],
    'noninvestigational_intervention': [
      'study_treatments.treatment_table',
      'study_treatments.concomitant_medications_allowed',
      'study_treatments.additional_information'
    ],
    // CSV 6.9.1 and 6.9.3: stored inside treatment_table.rescue_medication (JSON/string) when enabled
    'background_trial_intervention': [
      'study_treatments.treatment_table'
    ],
    'other_noninvestigational_trial_intervention': [
      'study_treatments.treatment_table'
    ],
    // CSV calls out Rescue Therapy and its optional sub-rows; this is stored under treatment_table.rescue_medication (JSON/string).
    'rescue_therapy': [
      'study_treatments.treatment_table'
    ],
    'concomitant_therapy_prohibited': [
      'study_treatments.concomitant_medications_prohibited',
      'study_treatments.treatment_table'
    ],
    'concomitant_therapy_permitted': [
      'study_treatments.concomitant_medications_allowed',
      'study_treatments.treatment_table'
    ]
  },
  '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL': {
    'permanent_discontinuation': [
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'template_language_fields.liver_event_stopping_criteria',
      'template_language_fields.liver_event_stopping_flowchart_mermaid',
      'template_language_fields.liver_patient_library_decisions',
      'study_assessments.efficacy_assessments',
      'study_assessments.safety_assessments_general',
      'endpoints_objectives.primary_objective_endpoints',
      'statistical_considerations.safety_analysis',
      'statistical_considerations.analysis_sets'
    ],
    'temporary_discontinuation': [
      'discontinuation_rules.temporary_discontinuation_trial_intervention',
      'discontinuation_rules.temporary_discontinuation_decisions',
      'study_treatments.concomitant_medications_allowed',
      'study_assessments'
    ],
    'rechallenge': [
      'discontinuation_rules.rechallenge',
      'discontinuation_rules.rechallenge_decisions',
      'template_language_fields.liver_restart_rechallenge',
      'discontinuation_rules.temporary_discontinuation_trial_intervention'
    ],
    'participant_withdrawal': [
      'discontinuation_rules.participant_discontinuation_withdrawal',
      'regulatory_requirements.informed_consent_rescreening',
      'study_assessments',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis'
    ],
    'loss_to_followup': [
      'discontinuation_rules.management_loss_to_followup',
      'study_assessments.efficacy_assessments',
      'study_assessments.safety_assessments_general'
    ]
  },
  '8. TRIAL ASSESSMENTS AND PROCEDURES': {
    'assessments_considerations': [
      'study_assessments',
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'study_overview.study_parts'
    ],
    'screening_baseline_assessments': [
      'study_assessments.screening_baseline_assessments',
      'target_population.eligibility_criteria',
      'target_population.conditions_related_to_primary_disease',
      'target_population.tissue_sample_procedure_compliance',
      'target_population.patient_performance_status',
      'target_population.organ_function_lab_parameters',
      'target_population.concomitant_meds_washout',
      'target_population.comorbidities_infections',
      'target_population.reproductive_status_contraception',
      'study_overview.study_parts'
    ],
    'efficacy_assessments': [
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'endpoints_objectives.exploratory_objectives_endpoints',
      'study_assessments.efficacy_assessments',
      'study_overview.study_parts'
    ],
    'safety_assessments': [
      'study_assessments.physical_examination',
      'study_assessments.vital_signs',
      'study_assessments.electrocardiograms',
      'study_assessments.clinical_laboratory_assessment',
      'study_assessments.pregnancy_testing',
      'study_assessments.suicidal_ideation_risk_monitoring',
      'study_assessments.safety_assessments_general',
      'study_treatments.treatment_table',
      'statistical_considerations.safety_analysis',
      'target_population.comorbidities_infections',
      'target_population.reproductive_status_contraception'
    ],
    'pharmacokinetics': [
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'study_assessments.pharmacokinetics',
      'statistical_considerations.other_analyses'
    ],
    'biomarkers': [
      'endpoints_objectives.exploratory_objectives_endpoints',
      'endpoints_objectives.primary_objective_endpoints',
      'study_assessments.biomarkers',
      'study_assessments.genetics_pharmacogenomics',
      'study_assessments.pharmacodynamic_biomarkers',
      'study_assessments.other_biomarkers'
    ],
    'immunogenicity': [
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints',
      'study_assessments.biomarkers'
    ],
    'health_economics': [
      'endpoints_objectives.exploratory_objectives_endpoints',
      'study_assessments.comments'
    ]
  },
  '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS': {
    'ae_sae_definitions': [
      'study_overview.study_type_dropdown',
      'study_identification.preferred_short_title',
      'target_population.conditions_related_to_primary_disease',
      'study_overview.study_parts',
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'study_treatments.fda_pregnancy_risk_categories',
      'study_treatments.treatment_table'
    ],
    'collection_reporting_procedures': [
      'study_overview.study_parts',
      'study_assessments.safety_assessments_general',
      'statistical_considerations.safety_analysis',
      'statistical_considerations.interim_analyses',
      'discontinuation_rules.discontinuation_trial_intervention_individual'
    ],
    'pregnancy': [
      'target_population.reproductive_status_contraception',
      'study_treatments.fda_pregnancy_risk_categories',
      'study_treatments.treatment_table',
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'discontinuation_rules.permanent_discontinuation_trial_intervention'
    ],
    'other_safety_situations': [
      'discontinuation_rules.temporary_discontinuation_trial_intervention',
      'discontinuation_rules.permanent_discontinuation_trial_intervention',
      'study_treatments.treatment_table',
      'study_treatments.concomitant_medications_prohibited',
      'background_information.risks_investigational'
    ]
  },
  '10. STATISTICAL CONSIDERATIONS': {
    'general_considerations': [
      // Intake "General Considerations" row lives on study_overview.study_parts; top-level stats below
      'study_overview.study_parts',
      'statistical_considerations.analysis_sets',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.interim_analyses'
    ],
    'analysis_sets': [
      'statistical_considerations.analysis_sets',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis',
      'statistical_considerations.safety_analysis',
      'statistical_considerations.exploratory_analysis'
    ],
    'demographics_baseline': [
      'study_assessments.screening_baseline_assessments',
      'target_population.patient_performance_status',
      'statistical_considerations.secondary_objective_analysis',
      'study_overview.study_parts'
    ],
    'primary_objective_analyses': [
      'endpoints_objectives.primary_objective_endpoints',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.analysis_sets',
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'background_information.rationale_for_estimands'
    ],
    'secondary_objective_analyses': [
      'endpoints_objectives.secondary_objectives_endpoints',
      'endpoints_objectives.key_secondary_objectives_endpoints',
      'statistical_considerations.secondary_objective_analysis'
    ],
    'exploratory_objective_analyses': [
      'endpoints_objectives.exploratory_objectives_endpoints',
      'statistical_considerations.exploratory_analysis',
      'study_assessments.biomarkers',
      'study_assessments.genetics_pharmacogenomics',
      'study_assessments.pharmacodynamic_biomarkers',
      'study_assessments.other_biomarkers'
    ],
    'safety_analyses': [
      'statistical_considerations.safety_analysis',
      'study_assessments.physical_examination',
      'study_assessments.vital_signs',
      'study_assessments.electrocardiograms',
      'study_assessments.clinical_laboratory_assessment',
      'study_assessments.safety_assessments_general',
      'statistical_considerations.secondary_objective_analysis'
    ],
    'other_analyses_subgroups': [
      'study_overview.study_parts',
      'target_population.patient_performance_status',
      'target_population.conditions_related_to_primary_disease',
      'statistical_considerations.other_analyses'
    ],
    'interim_analyses': [
      'statistical_considerations.interim_analyses',
      'regulatory_requirements.committees'
    ],
    'multiplicity_adjustments': [
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.secondary_objective_analysis',
      'endpoints_objectives.primary_objective_endpoints',
      'endpoints_objectives.secondary_objectives_endpoints'
    ],
    'sample_size_determination': [
      'statistical_considerations.sample_size_determination',
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'study_assessments.efficacy_assessments',
      'endpoints_objectives.primary_objective_endpoints',
      'background_information.disease_epidemiology',
      'background_information.outcomes_current_soc',
      'background_information.current_standard_of_care'
    ]
  },
  '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS': {
    'regulatory_ethical_considerations': [
      'regulatory_requirements.regulatory_ethical_considerations',
      'regulatory_requirements.informed_consent_rescreening'
    ],
    'investigator_responsibilities': [
      'regulatory_requirements.investigator_responsibilities'
    ],
    'sponsor_responsibilities': [
      'regulatory_requirements.sponsor_responsibilities'
    ],
    'informed_consent_process': [
      'regulatory_requirements.informed_consent_rescreening',
      'regulatory_requirements.informed_consent_remaining_samples',
      'target_population.reproductive_status_contraception'
    ],
    'committees': [
      'regulatory_requirements.committees',
      'study_overview.study_parts',
      'statistical_considerations.interim_analyses'
    ],
    'insurance_indemnity': [
      'regulatory_requirements.insurance_indemnity'
    ],
    'risk_based_quality_management': [
      'regulatory_requirements.risk_based_quality_management',
      'study_monitoring_logistics'
    ],
    'data_governance': [
      'regulatory_requirements.data_governance',
      'regulatory_requirements.data_protection',
      'study_monitoring_logistics'
    ],
    'data_protection': [
      'regulatory_requirements.data_protection'
    ],
    'source_data': [
      'regulatory_requirements.source_data',
      'study_assessments'
    ],
    'protocol_deviations': [
      'regulatory_requirements.protocol_deviations'
    ],
    'early_site_closure': [
      'regulatory_requirements.early_site_closure',
      'discontinuation_rules.discontinuation_trial_intervention_individual',
      'statistical_considerations.primary_objective_analysis',
      'statistical_considerations.safety_analysis'
    ],
    'data_dissemination': [
      'regulatory_requirements.data_dissemination',
      'study_identification.preferred_short_title'
    ]
  },
  '12. APPENDIX: SUPPORTING DETAILS': {
    'clinical_laboratory_tests': [
      'study_assessments.safety_assessments_general',
      'study_assessments.efficacy_assessments'
    ],
    'country_region_differences': [
      'regulatory_requirements.regulatory_ethical_considerations',
      'target_population.reproductive_status_contraception'
    ],
    'prior_protocol_amendments': [
      'study_identification.version_number',
      'study_identification.version_date'
    ]
  },
  '13. APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS': {
    'glossary': [
      'study_identification',
      'endpoints_objectives',
      'study_design',
      'study_assessments'
    ]
  },
  '14. APPENDIX: REFERENCES': {
    'references': [
      'background_information.disease_epidemiology',
      'background_information.current_standard_of_care',
      'background_information.outcomes_current_soc'
    ]
  }
};

// /** Collapse AI fan-out where every analysis sub-key repeats the same text. */
// const collapseAnalysisJsonDuplicates = (obj) => {
//   if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
//   const keysWithText = ANALYSIS_JSON_OPTIONAL_KEYS.filter((k) => {
//     const v = obj[k];
//     return v != null && String(v).trim() !== '' && String(v).trim() !== 'Not Provided';
//   });
//   if (keysWithText.length < 2) return obj;
//   const texts = keysWithText.map((k) => String(obj[k]).trim());
//   const canonical = texts[0];
//   if (!texts.every((t) => t === canonical)) return obj;
//   const summaryTrim = obj.summary == null ? '' : String(obj.summary).trim();
//   if (summaryTrim && summaryTrim !== canonical && summaryTrim !== 'Not Provided') return obj;
//   return { summary: canonical };
// };

// /**
//  * Strip optional analysis sub-keys from analyses_primary/secondary_objective values
//  * (stored as JSON strings or plain objects) when the user has not enabled that sub-field.
//  */
// const pruneAnalysisJsonValue = (rawValue, enabledMap) => {
//   if (rawValue === undefined || rawValue === null || rawValue === '') return rawValue;
//   let obj;
//   let asString = typeof rawValue === 'string';
//   if (asString) {
//     try {
//       const parsed = JSON.parse(rawValue);
//       if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return rawValue;
//       obj = collapseAnalysisJsonDuplicates({ ...parsed });
//     } catch {
//       return rawValue;
//     }
//   } else if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
//     obj = collapseAnalysisJsonDuplicates({ ...rawValue });
//     asString = false;
//   } else {
//     return rawValue;
//   }
//   ANALYSIS_JSON_OPTIONAL_KEYS.forEach((key) => {
//     if (enabledMap[key] === false) delete obj[key];
//   });
//   if (asString) {
//     const cleaned = Object.fromEntries(
//       Object.entries(obj).filter(([, v]) => (typeof v === 'string' ? v.trim() !== '' : v != null))
//     );
//     return Object.keys(cleaned).length ? JSON.stringify(cleaned) : '';
//   }
//   return obj;
// };

// const pruneRescueMedicationValue = (rawValue, rescueSelections) => {
//   if (rawValue === undefined || rawValue === null || rawValue === '') return rawValue;
//   let obj;
//   let asString = typeof rawValue === 'string';
//   if (asString) {
//     try {
//       const parsed = JSON.parse(rawValue);
//       if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return rawValue;
//       obj = { ...parsed };
//     } catch {
//       return rawValue;
//     }
//   } else if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
//     obj = { ...rawValue };
//     asString = false;
//   } else {
//     return rawValue;
//   }
//   if (rescueSelections.backgroundTrialIntervention === false) {
//     delete obj.background_trial_intervention;
//   }
//   if (rescueSelections.otherNonInvestigationalIntervention === false) {
//     delete obj.other_noninvestigational_trial_intervention;
//   }
//   if (asString) {
//     const cleaned = Object.fromEntries(
//       Object.entries(obj).filter(([, v]) => (typeof v === 'string' ? v.trim() !== '' : v != null))
//     );
//     return Object.keys(cleaned).length ? JSON.stringify(cleaned) : '';
//   }
//   return obj;
// };

// /**
//  * Remove optional study-parts / treatment-table content that the UI hides when
//  * "View guidance" sub-fields are off, so section generation matches the form.
//  */
// const pruneOptionalStudyFieldsForGeneration = (filteredData, selections) => {
//   if (!selections || typeof selections !== 'object') return;

//   const lr = selections.lifestyle_restrictions;
//   const prim = selections.analyses_primary_objective;
//   const sec = selections.analyses_secondary_objective;
//   const iih = selections.investigational_intervention_handling;
//   const rm = selections.rescue_medication;

//   const hasLr = lr && typeof lr === 'object';
//   const hasPrim = prim && typeof prim === 'object';
//   const hasSec = sec && typeof sec === 'object';
//   const hasIih = iih && typeof iih === 'object';
//   const hasRm = rm && typeof rm === 'object';

//   const studyParts = filteredData.study_overview?.study_parts;
//   if (studyParts && typeof studyParts === 'object' && (hasLr || hasPrim || hasSec)) {
//     const sp = cloneIntakeSubtree(studyParts);
//     Object.values(sp).forEach((part) => {
//       if (!part || typeof part !== 'object') return;
//       Object.values(part).forEach((cohort) => {
//         if (!cohort || typeof cohort !== 'object') return;
//         if (hasLr) {
//           if (lr.meals === false) delete cohort.meals_dietary_restrictions;
//           if (lr.caffeine === false) delete cohort.caffeine_alcohol_tobacco_other_restrictions;
//           if (lr.physical === false) delete cohort.physical_activity_restrictions;
//           if (lr.other === false) delete cohort.other_activity_restrictions;
//         }
//         if (hasPrim && cohort.analyses_primary_objective !== undefined) {
//           cohort.analyses_primary_objective = pruneAnalysisJsonValue(cohort.analyses_primary_objective, prim);
//         }
//         if (hasSec && cohort.analyses_secondary_objective !== undefined) {
//           cohort.analyses_secondary_objective = pruneAnalysisJsonValue(cohort.analyses_secondary_objective, sec);
//         }
//       });
//     });
//     filteredData.study_overview.study_parts = sp;
//   }

//   const treatmentTable = filteredData.study_treatments?.treatment_table;
//   if (treatmentTable && typeof treatmentTable === 'object' && (hasIih || hasRm)) {
//     const tt = cloneIntakeSubtree(treatmentTable);
//     Object.values(tt).forEach((regimen) => {
//       if (!regimen || typeof regimen !== 'object') return;
//       if (hasIih) {
//         if (iih.preparation === false) delete regimen.preparation_of_investigational_trial_intervention;
//         if (iih.storageHandling === false) delete regimen.storage_handling_of_investigational_trial_intervention;
//         if (iih.accountability === false) delete regimen.accountability_of_investigational_trial_intervention;
//       }
//       if (hasRm && regimen.rescue_medication !== undefined) {
//         regimen.rescue_medication = pruneRescueMedicationValue(regimen.rescue_medication, rm);
//       }
//     });
//     filteredData.study_treatments.treatment_table = tt;
//   }
// };

const attachStudyTypeAndDesignForAllSections = (data, filteredData) => {
  if (!data?.study_overview) return;

  if (!filteredData.study_overview) {
    filteredData.study_overview = {};
  }

  if (data.study_overview.study_type_dropdown !== undefined) {
    filteredData.study_overview.study_type_dropdown = data.study_overview.study_type_dropdown;
  }

  if (data.study_overview.study_design && typeof data.study_overview.study_design === 'object') {
    filteredData.study_overview.study_design = {
      ...filteredData.study_overview.study_design,
      ...data.study_overview.study_design,
    };
  }
};

/** Attach study-parts context for section generation (part/cohort data only — not regimens). */
const attachTrialSchemaTablesForGeneration = (data, filteredData) => {
  if (!data) return;
  if (data.study_overview?.study_parts) {
    if (!filteredData.study_overview) filteredData.study_overview = {};
    filteredData.study_overview.study_parts = mergeStudyPartsNotesForGeneration(
      data.study_overview.study_parts,
      data.study_overview?.comments
    );
    if (data.study_overview.number_of_study_parts_new !== undefined) {
      filteredData.study_overview.number_of_study_parts_new = data.study_overview.number_of_study_parts_new;
    }
    if (data.study_overview.study_parts_cohorts) {
      filteredData.study_overview.study_parts_cohorts = data.study_overview.study_parts_cohorts;
    }
    if (data.study_overview.study_parts_part_names) {
      filteredData.study_overview.study_parts_part_names = data.study_overview.study_parts_part_names;
    }
    if (data.study_overview.study_parts_cohort_names) {
      filteredData.study_overview.study_parts_cohort_names = data.study_overview.study_parts_cohort_names;
    }
    if (data.study_overview.trial_schema_configuration) {
      filteredData.study_overview.trial_schema_configuration = data.study_overview.trial_schema_configuration;
    }
  }
  // Treatment table + regimens_per_part are attached only when the section mapping already
  // includes study_treatments (e.g. Trial Intervention, Adverse Events) — not globally.
  if (data.study_treatments && filteredData.study_treatments) {
    const st = data.study_treatments;
    if (st.treatment_table && filteredData.study_treatments.treatment_table === undefined) {
      filteredData.study_treatments.treatment_table = st.treatment_table;
    }
    if (st.num_regimens !== undefined && filteredData.study_treatments.num_regimens === undefined) {
      filteredData.study_treatments.num_regimens = st.num_regimens;
    }
    if (st.regimens_per_part && filteredData.study_treatments.regimens_per_part === undefined) {
      filteredData.study_treatments.regimens_per_part = st.regimens_per_part;
    }
    if (st.regimen_arms && filteredData.study_treatments.regimen_arms === undefined) {
      filteredData.study_treatments.regimen_arms = st.regimen_arms;
    }
  }
};

// Function to filter clinical intake data for specific section
export const findPredefinedSection = (predefinedSections, sectionTitle) => {
  if (!Array.isArray(predefinedSections) || !sectionTitle) return null;

  const canonicalTitle = canonicalizeM11AppendixSectionTitle(sectionTitle);
  const byCanonical = predefinedSections.find((section) => section.title === canonicalTitle);
  if (byCanonical) return byCanonical;

  const byExact = predefinedSections.find((section) => section.title === sectionTitle);
  if (byExact) return byExact;

  const sectionNum = String(sectionTitle).trim().match(/^(\d+)/)?.[1];
  if (!sectionNum) return null;

  return predefinedSections.find((section) => {
    const predefinedNum = String(section?.title || '').trim().match(/^(\d+)/)?.[1];
    return predefinedNum === sectionNum && (sectionNum === '13' || sectionNum === '14');
  }) || null;
};

export const filterClinicalIntakeForSection = (data, sectionTitle) => {
  if (!data || !sectionTitle) return data;

  const canonicalTitle = canonicalizeM11AppendixSectionTitle(sectionTitle);
  const mapping =
    SECTION_FIELD_MAPPINGS[canonicalTitle] || SECTION_FIELD_MAPPINGS[sectionTitle];
  if (!mapping) return data; // Return full data if no mapping exists
  
  const filteredData = {};
  
  // Extract only the fields needed for this section
  Object.values(mapping).flat().forEach(fieldPath => {
    const fieldParts = fieldPath.split('.');
    let currentData = data;
    let currentFiltered = filteredData;
    
    // Navigate to the field and copy it
    for (let i = 0; i < fieldParts.length - 1; i++) {
      const part = fieldParts[i];
      if (currentData[part] && typeof currentData[part] === 'object') {
        if (!currentFiltered[part]) {
          currentFiltered[part] = Array.isArray(currentData[part]) ? [] : {};
        }
        currentData = currentData[part];
        currentFiltered = currentFiltered[part];
      }
    }
    
    const finalField = fieldParts[fieldParts.length - 1];
    if (currentData[finalField] !== undefined) {
      currentFiltered[finalField] = currentData[finalField];
    }
  });

  // ALWAYS include study type dropdown + study design checkboxes for every protocol section
  attachStudyTypeAndDesignForAllSections(data, filteredData);

  // Include trial schema tables (study parts + treatment table grouped by part) for generation context
  attachTrialSchemaTablesForGeneration(data, filteredData);
  
  // ALWAYS include template selections so AI can use template data
  if (data.fieldTemplateSelections) {
    filteredData.fieldTemplateSelections = data.fieldTemplateSelections;
  }
  
  if (data.section9TemplateSelections) {
    filteredData.section9TemplateSelections = data.section9TemplateSelections;
  }

   // ALWAYS include Template Language Fields so section generation can reuse
  // any saved/generated standard text (e.g., 9.2–9.2.5 blocks) as context.
  if (data.template_language_fields) {
    filteredData.template_language_fields = data.template_language_fields;
  }

  // ALWAYS include full discontinuation rules for Section 7 (decisions + assembled text)
  if (
    data.discontinuation_rules &&
    (String(sectionTitle || '').match(/^7\b/) ||
      String(canonicalTitle || '').includes('DISCONTINUATION'))
  ) {
    filteredData.discontinuation_rules = data.discontinuation_rules;
  }
  
  // ALWAYS include trial schema SVG if available
  if (data.trialSchemaSvg) {
    filteredData.trialSchemaSvg = data.trialSchemaSvg;
  }

  // Source document summaries for section generation (uploaded on Source Documents tab)
  const docSummaries = summarizeDocumentUploadsForGeneration(data.document_uploads);
  if (docSummaries?.summaries?.length) {
    filteredData.document_uploads = docSummaries;
  }

  // Section 12: include full appendices (title, description, uploaded contentHtml)
  const isSection12 =
    String(sectionTitle || '').match(/^12\b/) ||
    String(canonicalTitle || '').includes('APPENDIX: SUPPORTING DETAILS');
  if (isSection12 && Array.isArray(data.appendices)) {
    filteredData.appendices = data.appendices;
  }
  
  return filteredData;
};

/**
 * Build HTML blocks from intake appendices that have uploaded contentHtml.
 * Used to seed / append into protocol section 12 output (12.1, 12.2, …).
 */
export const buildSection12HtmlFromAppendices = (appendices) => {
  if (!Array.isArray(appendices) || !appendices.length) return '';
  const parts = [];
  appendices.forEach((appendix) => {
    if (!appendix || typeof appendix !== 'object') return;
    const title = String(appendix.title || '').trim();
    const html = String(appendix.contentHtml || '').trim();
    const description = String(appendix.description || '').trim();
    if (!html && !description) return;
    if (title) {
      const safeTitle = title.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      // Prefer h2 for numbered appendix subsections (12.1, 12.2, …)
      if (/^12(\.\d+)+\b/.test(title)) {
        parts.push(`<h2>${safeTitle}</h2>`);
      } else {
        parts.push(`<h3>${safeTitle}</h3>`);
      }
    }
    if (description) {
      parts.push(`<p>${description.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`);
    }
    if (html) {
      parts.push(html);
    }
  });
  return parts.join('\n');
};

const UPLOADED_APPENDIX_BLOCK_RE =
  /\n?<hr\s*\/?>\s*<h2>\s*Uploaded Appendix Materials\s*<\/h2>[\s\S]*$/i;

/** Apply uploaded appendix HTML into section 12 (replace prior upload block; seed empty/NA sections). */
export const mergeAppendicesIntoSection12Output = (generatedHtml, appendices) => {
  const uploaded = buildSection12HtmlFromAppendices(appendices);
  if (!uploaded) return generatedHtml || '';
  let base = String(generatedHtml || '').trim();
  base = base.replace(UPLOADED_APPENDIX_BLOCK_RE, '').trim();
  const plain = base
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const naOnly =
    !plain ||
    /^not applicable\.?$/i.test(plain) ||
    /^uploaded appendix materials$/i.test(plain);
  if (naOnly) {
    return `<h1>12. List of Appendices</h1>\n${uploaded}`;
  }
  return `${base}\n<hr/>\n<h2>Uploaded Appendix Materials</h2>\n${uploaded}`;
};

// Function to format clinical intake data for context
const formatClinicalIntakeForContext = (data) => {
  if (!data) return '';

  const formatSection = (section, title) => {
    if (!section) return '';
    return `${title}:\n${Object.entries(section)
      .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      .map(([key, value]) => {
        // Special handling for regimen_arms object
        if (key === 'regimen_arms' && typeof value === 'object') {
          if (Object.keys(value).length === 0) {
            return `${key}: No regimen arms defined`;
          } else {
            return `${key}: ${Object.entries(value).map(([armNum, description]) => `Arm ${armNum}: ${description}`).join(', ')}`;
          }
        }
        // Handle arrays
        if (Array.isArray(value)) {
          return `${key}: ${value.join(', ')}`;
        }
        // Handle other objects
        if (typeof value === 'object' && value !== null) {
          return `${key}: ${JSON.stringify(value)}`;
        }
        // Handle simple values
        return `${key}: ${value}`;
      })
      .join('\n')}\n\n`;
  };

  const formatNestedSection = (section, title) => {
    if (!section) return '';
    let result = `${title}:\n`;
    
    Object.entries(section).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result += formatSection(value, key);
      } else if (value !== '' && value !== null && value !== undefined) {
        result += `${key}: ${Array.isArray(value) ? value.join(', ') : value}\n`;
      }
    });
    
    return result + '\n';
  };

  let context = '';

  // Format each section
  context += formatSection(data.study_identification, 'Study Identification');
  context += formatSection(data.study_overview, 'Study Overview');
  context += formatSection(data.endpoints_objectives, 'Endpoints and Objectives');
  context += formatSection(data.target_population, 'Target Population');
  context += formatSection(data.study_treatments, 'Study Treatments');
  context += formatSection(data.discontinuation_rules, 'Discontinuation Rules');
  context += formatNestedSection(data.study_assessments, 'Study Assessments');
  context += formatSection(data.statistical_considerations, 'Statistical Considerations');
  context += formatSection(data.regulatory_requirements, 'Regulatory Requirements');
  
  // Add background information section (NEW)
  if (data.background_information) {
    context += formatSection(data.background_information, 'Background Information');
  }
  
  // Add study monitoring and logistics section (NEW)
  if (data.study_monitoring_logistics) {
    context += formatSection(data.study_monitoring_logistics, 'Study Monitoring and Logistics');
  }
  
  // Add document uploads section (NEW)
  if (data.document_uploads) {
    context += formatSection(data.document_uploads, 'Document Uploads');
  }
  
  // Add trial schema section (NEW)
  if (data.trialSchemaSvg) {
    context += `Trial Schema: Available (SVG diagram)\n\n`;
  }
  
  // Add appendices with new structure
  if (data.appendices && Array.isArray(data.appendices)) {
    context += 'Appendices:\n';
    if (data.appendices.length === 0) {
      context += 'No appendices defined\n\n';
    } else {
      data.appendices.forEach((appendix, index) => {
        if (appendix && appendix.title) {
          context += `${index + 1}. ${appendix.title}: ${appendix.description || 'No description provided'}\n`;
          if (String(appendix.contentHtml || '').trim()) {
            context += `Uploaded appendix body (authoritative HTML — preserve images and text; do not rewrite away):\n${appendix.contentHtml}\n`;
          }
        }
      });
      context += '\n';
    }
  }

  if (data.additional_comments) {
    context += `Additional Comments:\n${data.additional_comments}\n\n`;
  }
  
  // Add template selections (NEW)
  if (data.fieldTemplateSelections && Object.keys(data.fieldTemplateSelections).length > 0) {
    context += 'Field Template Selections:\n';
    Object.entries(data.fieldTemplateSelections).forEach(([field, selected]) => {
      if (selected) {
        context += `- ${field}: Using template\n`;
      }
    });
    context += '\n';
  }
  
  if (data.section9TemplateSelections && Object.keys(data.section9TemplateSelections).length > 0) {
    context += 'Section 9 Template Selections:\n';
    Object.entries(data.section9TemplateSelections).forEach(([field, selected]) => {
      if (selected) {
        context += `- ${field}: Using template\n`;
      }
    });
    context += '\n';
  }

  return context.trim();
};

// Function to log clinical intake context
export const logClinicalIntakeContext = async (projectId) => {
  try {
    if (!projectId) {
      console.error('logClinicalIntakeContext requires projectId');
      return null;
    }
    const latestData = await fetchLatestClinicalIntakeData(projectId);
    const formattedContext = formatClinicalIntakeForContext(latestData);

    console.log('Raw Clinical Intake Data:', JSON.stringify(latestData, null, 2));
    console.log('Formatted Context:', formattedContext);

    return { latestData, formattedContext };
  } catch (error) {
    console.error('Error logging clinical intake context:', error);
    return null;
  }
};

// Default sections with prompts
const DEFAULT_SECTIONS = [
  // {
  //   title: '1. PROTOCOL SUMMARY',
  //   // context: '',
  //   // output: '',
  //   // prompt: 'Write a comprehensive protocol summary for a clinical trial. Include brief information about the trial design, primary objectives, study population, intervention details, and key endpoints. Keep it concise but informative for quick reference.'
  // },
  {
    title: '1. PROTOCOL SUMMARY',
    context: '',
    output: '',
    prompt: `You are an expert medical writer specializing in clinical trial protocol writing aligned with ICH M11 conventions.

# Task
Generate Section 1: PROTOCOL SUMMARY for a clinical trial protocol. Use the information in the provided protocol context to generate the summary.

# Constraints
- Do not use any information outside of the provided context.
- Do not make assumptions.

# Output Structure
Generate the output using the exact structure and headings below.

1 PROTOCOL SUMMARY
No text is intended here (heading only).

1.1 Protocol Synopsis
No text is intended here (heading only).

1.1.1 Primary and Secondary Objectives and Estimands
In natural, non-technical language, summarize the most important primary and secondary objectives and their associated estimands (the treatment effect or clinical question of interest). Refer to Section 3 for full technical details.

1.1.2 Overall Design
MANDATORY: Immediately after the heading, output exactly this one cross-reference sentence, then the table on the next line — no other sentences, no paraphrased variants, and do not also write a caption line (the caption is added automatically):
Table 1-1 summarizes the key aspects of the overall trial design.
MANDATORY: Output this subsection as an HTML <table> (two columns: Parameter, Details) — never as a markdown pipe table, and never as a prose paragraph list.
Use exactly this table format (one <tr> per field). Fill every Details cell from the clinical intake; use "Not applicable" or "Not provided" when unknown.

<table border="1" cellspacing="0" cellpadding="5" style="width: 100%; border-collapse: collapse;">
  <tr>
    <td style="width: 30%; background-color: #f2f2f2;"><strong>Parameter</strong></td>
    <td style="background-color: #f2f2f2;"><strong>Details</strong></td>
  </tr>
  <tr>
    <td>Intervention</td>
    <td>[Sponsor's IP Code(s) or Nonproprietary Name(s)]</td>
  </tr>
  <tr>
    <td>Population Type</td>
    <td>[Population type]</td>
  </tr>
  <tr>
    <td>Population Diagnosis or Condition</td>
    <td>[Using MedDRA PT if possible]</td>
  </tr>
  <tr>
    <td>Control Type</td>
    <td>[e.g., Active comparator, Placebo]</td>
  </tr>
  <tr>
    <td>Randomization</td>
    <td>[e.g., Randomized, Non-randomized, Not applicable]</td>
  </tr>
  <tr>
    <td>Control Description</td>
    <td>[Nonproprietary name, INN, or "Not applicable"]</td>
  </tr>
  <tr>
    <td>Population Age — Minimum</td>
    <td>[&lt;age&gt; &lt;units&gt;]</td>
  </tr>
  <tr>
    <td>Population Age — Maximum</td>
    <td>[&lt;age&gt; &lt;units&gt;]</td>
  </tr>
  <tr>
    <td>Site Distribution and Geographic Scope</td>
    <td>[Insert]</td>
  </tr>
  <tr>
    <td>Intervention Model</td>
    <td>[Parallel, crossover, etc.]</td>
  </tr>
  <tr>
    <td>Intervention Assignment Method</td>
    <td>[e.g., Randomized] (do not state block size)</td>
  </tr>
  <tr>
    <td>Master Protocol Indicator</td>
    <td>[Yes/No]</td>
  </tr>
  <tr>
    <td>Drug/Device Combination Product Indicator</td>
    <td>[Yes/No]</td>
  </tr>
  <tr>
    <td>Adaptive Trial Design Indicator</td>
    <td>[Yes/No]</td>
  </tr>
  <tr>
    <td>Number of Arms</td>
    <td>[Numeric value]</td>
  </tr>
  <tr>
    <td>Trial Blind Schema</td>
    <td>[Single-blind, Double-blind, Open-label]</td>
  </tr>
  <tr>
    <td>Blinded Roles</td>
    <td>[e.g., Participant, Investigator, Assessor] or "Not applicable (No blinding)"</td>
  </tr>
  <tr>
    <td>Number of Participants</td>
    <td>A target of &lt;number&gt; participants will be [enrolled/randomly assigned].</td>
  </tr>
  <tr>
    <td>Duration — Total planned duration of trial intervention for each participant</td>
    <td>&lt;duration&gt;</td>
  </tr>
  <tr>
    <td>Duration — Total planned duration of trial participation for each participant</td>
    <td>&lt;duration&gt; (if duration varies, briefly explain e.g. event-driven / adaptive)</td>
  </tr>
  <tr>
    <td>Committees</td>
    <td>List any committees with a very brief description of their functioning</td>
  </tr>
  <tr>
    <td>Independent Committees</td>
    <td>[e.g., Data Monitoring Committee, Dose Escalation Committee, Endpoint Adjudication Committee]</td>
  </tr>
</table>

Do not omit the table. Do not convert Overall Design into narrative paragraphs.`
  },
  {
    title: '1.2 TRIAL SCHEMA',
    context: '',
    output: '',
    prompt: `You are an expert medical writer specializing in clinical trial protocol writing aligned with ICH M11 conventions.

**TASK**
Generate Section 1.2: TRIAL SCHEMA — a concise visual-style summary of the trial design flow (screening through end of trial), presented as an HTML table so it renders correctly without an image.

**INPUT DATA**
- Trial Schema Diagram (if provided, describe/reference it and reproduce its content as a table): {{trialSchemaSvg}}
- Study Parts/Cohorts (arms, regimens, blinding, treatment allocation per part): {{study_overview.study_parts}}
- Number of Study Parts: {{study_overview.number_of_study_parts_new}}
- Study Design: {{study_overview.study_design}}
- Treatment Table: {{study_treatments.treatment_table}}
- Study Periods: {{study_overview.study_parts[part][cohort].study_periods}}

**OUTPUT STRUCTURE**
1.2 Trial Schema
No narrative paragraph. Present the schema as a single HTML <table> with one row per study part/cohort/arm, and columns: Part/Cohort, Arm/Regimen, Intervention, Allocation/Blinding, Period(s)/Duration, Key Transition (e.g., screening → treatment → follow-up).

If trialSchemaSvg is provided, note directly above the table: "A visual trial schema diagram is included in the protocol front matter." Then still populate the table from study_overview.study_parts and study_treatments.treatment_table so the content is available even where the diagram cannot be rendered.

If study part/arm data is insufficient to populate the table, state "Trial schema details to be finalized" and include whatever partial rows are available.

**CONSTRAINTS**
- Do not invent arms, doses, or periods not present in the input data.
- Output must be HTML (use <table>, <tr>, <td>). Do not use markdown tables or prose paragraphs.`
  },
  {
    title: '1.3 SCHEDULE OF ACTIVITIES',
    context: '',
    output: '',
    prompt: `You are an expert medical writer specializing in clinical trial protocol writing aligned with ICH M11 conventions.

**TASK**
Generate Section 1.3: SCHEDULE OF ACTIVITIES (SoA) — an HTML table mapping trial assessments/procedures to the visits or periods at which they are performed.

**INPUT DATA**
- Study Periods (per part/cohort): {{study_overview.study_parts[part][cohort].study_periods}}
- Study Parts/Cohorts: {{study_overview.study_parts}}
- Study Assessments (screening/baseline, efficacy, safety, PK, biomarkers, etc.): {{study_assessments}}
- Discontinuation/Early Termination Visit requirements: {{discontinuation_rules.discontinuation_trial_intervention_individual}}

**OUTPUT STRUCTURE**
1.3 Schedule of Activities
No narrative paragraph. Present a single HTML <table> with assessments/procedures as rows (grouped by category: Screening, Baseline, On-Treatment, Safety Follow-Up, End of Trial) and visits/periods as columns, marking with an "X" (or specific timing note) when each assessment is performed. Include a final row/column for Early Termination / Unscheduled visits.

If the granular visit-level timing is not available in the input data, state "Detailed visit-level Schedule of Activities to be finalized" and instead present the table grouped by study period (e.g., Screening, Treatment, Follow-Up) with the assessments performed in each period, sourced from study_assessments and study_periods.

**CONSTRAINTS**
- Do not invent assessments, visits, or timepoints not present in the input data.
- Output must be HTML (use <table>, <tr>, <td>). Do not use markdown tables or prose paragraphs.
- Keep consistent with the assessments detailed in Section 8 (Trial Assessments and Procedures).`
  },

  {
    title: '2. INTRODUCTION',
    context: '',
    output: '',
    prompt: `You are an expert medical writer specializing in oncology and clinical trial protocol writing, aligning with ICH M11 style and conventions.

## CONTEXT

Provide the following details about the clinical trial:

Investigational Product: {{Investigational_Product_Name}}
Indication: {{Indication}}
Research Question(s): {{Primary_Research_Questions}}
Key Trial-Specific Risks (Treatment): {{Key_Treatment_Risks}}
Key Trial-Specific Risks (Procedures): {{Key_Procedural_Risks_e.g.,_biopsy}}
Anticipated Participant Benefits: {{Anticipated_Benefits}}
Basis for Risk-Benefit Assessment: {{Basis_for_Risk_Benefit_Assessment}}

## BACKGROUND INFORMATION AND ARGUMENTS (Clinical Intake — Section D)
When populated in CLINICAL INTAKE DATA (background_information), treat these author-prepared arguments as primary source material. Integrate substantively; do not ignore or contradict them.

**Mapped to Section 2 subsections:**
- **2.1 Purpose of Trial:** disease background, current SOC, SOC outcomes, purpose of trial
- **2.2.1 Risk Summary:** risks with investigational agent/regimen
- **2.2.2 Benefit Summary:** benefits with investigational agent/regimen
- **2.2.3 Overall Risk-Benefit:** synthesize benefits, risks, and SOC context

- Disease Background: {{background_information.disease_epidemiology}}
- Current Standard of Care: {{background_information.current_standard_of_care}}
- Outcomes with Current Standard of Care: {{background_information.outcomes_current_soc}}
- Purpose of the Trial (author outline): {{background_information.purpose_of_trial}}
- Benefits with Investigational Agent/Regimen: {{background_information.benefits_investigational}}
- Risks with Investigational Agent/Regimen: {{background_information.risks_investigational}}

---

# TASK

Generate **Section 2: INTRODUCTION** of a clinical trial protocol following **ICH M11 conventions**.

Use the provided context as the primary source material.

Expand the information into a concise, scientifically justified narrative suitable for direct inclusion in a clinical trial protocol.

Where essential information is missing, supplement only with relevant evidence from authoritative sources, including:

Peer-reviewed publications
Clinical practice guidelines
Regulatory documents
Recognized scientific organizations (WHO, FDA, EMA, NCCN, ESMO, ASCO, NIH, CDC)

Do not create a comprehensive disease review. Include only information necessary to justify the proposed trial.

---

# STRICT LENGTH REQUIREMENT

The complete Section 2: INTRODUCTION must not exceed approximately **2 A4 pages**.

Target length:
**900–1,100 words excluding references**

Prioritize concise, high-value scientific information.

Approximate content allocation:

**2.1 Purpose of Trial:** 400–500 words
**2.2.1 Risk Summary and Mitigation Strategy:** 250–300 words
**2.2.2 Benefit Summary:** 150–200 words
**2.2.3 Overall Risk-Benefit Assessment:** 100–150 words

Do not exceed these limits.

---

# WRITING STYLE REQUIREMENTS

The Introduction should:

Be written as concise scientific narrative paragraphs.
Follow regulatory medical writing style.
Be suitable for an ICH M11 clinical trial protocol.
Avoid unnecessary background explanation.
Avoid repetition across sections.
Focus only on information required for regulatory understanding.
Avoid promotional statements.
Avoid detailed descriptions that belong in the Investigator's Brochure.

Use the Investigator's Brochure for detailed nonclinical and clinical evidence. Summarize only the evidence necessary to explain the rationale for the trial.

---

# CITATION REQUIREMENTS (AMA STYLE)

Use **AMA (American Medical Association) citation style** throughout the Introduction.

Citation requirements:

Use numbered citations in the order references first appear in the text.
Use superscript Arabic numerals for in-text citations where supported.

Examples:

"Non-small cell lung cancer remains a leading cause of cancer-related mortality worldwide.¹"

"Clinical studies have demonstrated improved outcomes with targeted therapies in selected molecular subgroups.²,³"

If superscript formatting is not available, use bracketed numbers:

"Non-small cell lung cancer remains a leading cause of cancer-related mortality worldwide [1]."

Do not use author-date citation formats.
Do not use Harvard-style citations.
Each reference number must correspond to only one reference.
Reuse the same reference number whenever the same source is cited again.

Use citations for:

Epidemiology
Disease burden
Current treatment recommendations
Clinical outcomes
Safety information
Mechanistic rationale
Published clinical evidence
Regulatory guidance

Do not fabricate citations or references.

Only include references from authentic scientific or regulatory sources.

**CRITICAL — Where references appear:**
- Use **in-text AMA numbered citations only** within Section 2 (superscript or bracketed numbers as above).
- Do **NOT** include a bibliographic reference list, "References" heading, Works Cited, Bibliography, or endnotes at the end of Section 2 (or anywhere else in Section 2).
- Do **NOT** append full citation details (authors, journal, year, DOI, URL, etc.) after the narrative.
- All full AMA-style numbered reference entries for citations used in Section 2 (and elsewhere in the protocol) must appear **only** in **Section 14: APPENDIX – REFERENCES**.
- Keep citation numbers sequential and consistent so Section 14 can list the matching full references in the same order.

---

# STRUCTURE AND CONTENT REQUIREMENTS

# 2 INTRODUCTION

No text required under this heading.

---

# 2.1 Purpose of Trial

Provide a concise scientific justification for why this trial is needed.

Include only the most relevant information:

Clinical significance of the disease
Key unmet medical need
Limitations of current treatment approaches
Scientific rationale for the investigational product
Existing evidence supporting clinical evaluation
Key uncertainty that the trial is designed to address
Rationale for the proposed patient population and study approach

Do not:

Restate study objectives
Repeat endpoint descriptions
Describe estimands
Provide excessive mechanism of action details
Reproduce Investigator's Brochure content

The section should explain why this trial is necessary and how it addresses an important clinical question.

Support scientific statements using AMA-style numbered citations.

---

# 2.2 Assessment of Risks and Benefits

Provide a concise, balanced assessment of trial-specific risks and anticipated benefits.

Focus only on risks and benefits relevant to this protocol.

---

## 2.2.1 Risk Summary and Mitigation Strategy

Summarize important risks and mitigation strategies under the following headings.

### Trial Intervention

Describe:

Key treatment-related risks relevant to this trial
Scientific basis of these risks
Available evidence supporting these risks
Monitoring and mitigation strategies

Avoid presenting a complete safety profile of the investigational product.

When background_information.risks_investigational is populated, use it as the primary basis for treatment risks.

### Trial Procedures

Describe:

Important procedural risks
Risks related to study design (for example, placebo/control assignment)
Participant protection measures

### Other Risks

Include only applicable risks related to:

Imaging agents
Devices
Companion diagnostics
Special populations
Other protocol-specific considerations

Avoid unnecessary detail.

Support relevant statements using AMA-style numbered citations.

---

## 2.2.2 Benefit Summary

Briefly describe:

Potential direct participant benefits
Immediate versus long-term potential benefits
Societal and scientific benefits

Clearly state if no individual participant benefit is expected.

Distinguish established evidence from anticipated benefits.

When background_information.benefits_investigational is populated, use it as the primary basis for anticipated participant benefits.

Support relevant statements using AMA-style numbered citations.

---

## 2.2.3 Overall Risk-Benefit Assessment

Provide a concise concluding assessment.

Summarize how:

Disease severity
Unmet medical need
Available evidence
Potential benefits
Identified risks
Risk mitigation measures

support the ethical and scientific justification for conducting the trial.

Avoid generic statements.

Synthesize benefits_investigational, risks_investigational, and SOC context when available.

Support relevant statements using AMA-style numbered citations.

---

# FINAL QUALITY CHECK

Before producing the final output, ensure:

✓ Total Section 2 narrative is ≤2 A4 pages (excluding references)
✓ Content is concise and protocol-ready
✓ No unnecessary disease background is included
✓ Risks are protocol-specific, not a generic drug safety summary
✓ Benefits are balanced and realistic
✓ AMA-style numbered citations are used throughout (in-text only)
✓ Citations appear in the order of first appearance
✓ Reference numbering is consistent throughout
✓ No reference list, bibliography, or full citation entries appear at the end of Section 2
✓ Full AMA reference list belongs only in Section 14 (APPENDIX: REFERENCES)
✓ In-text citation numbers are authentic and traceable to real sources (to be listed in Section 14)
✓ Writing aligns with ICH M11 clinical protocol expectations

IMPORTANT: Return only the Section 2 content (headings and narrative with in-text AMA numbered citations). Do NOT include a reference list. Do NOT wrap the output in markdown code fences.`
  },
  {
    title: '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
    context: '',
    output: '',
    prompt: `You are an expert medical writer specializing in clinical trial protocols.

Your task is to generate Section 3 'Trial Objectives and Associated Estimands' for a clinical trial protocol, following the ICH E9(R1) framework. Use the provided information to construct the full section with appropriate sub-headings and estimand tables. The output should be a single text block formatted with markdown for headings and tables.

IMPORTANT OVERRIDE FOR THIS STUDY:
- Even if any context file or example text you see shows only the primary objective with a full estimand table and defers secondary estimands to the SAP, you MUST NOT copy that pattern for this study.
- For THIS STUDY, you MUST generate an estimand table for EVERY primary and secondary efficacy objective (including, but not limited to, OS, PFS, ORR, DoR, etc.) whenever you have enough information in the provided inputs to define the estimand.
- Do NOT write generic phrases like "the corresponding estimand will be detailed in the Statistical Analysis Plan" when you have sufficient information (objective text, endpoint, population, treatment comparison, and at least partial ICE handling) to define and present the estimand in a table.

# Provided Information
Use the provided information below to generate the section. The information is organized by the type of data that would be found in a study design form.

## Objectives and Endpoints
- Primary Objectives/Endpoints: {{primary_objectives_endpoints}}
- Key Secondary Objectives/Endpoints: {{key_secondary_objectives_endpoints}}
- Secondary Objectives/Endpoints: {{secondary_objectives_endpoints}}
- Exploratory Objectives/Endpoints: {{exploratory_objectives_endpoints}}

## Population
- Target Population: {{target_population}}

## Treatments / Arms
- Investigational Regimen/Arm 1: {{arm_1_investigational}}
- Control Regimen/Arm 2: {{arm_2_control}}

## Statistical Considerations
- Analyses Supporting Primary Objective(s): {{primary_analyses}}
- Analyses Supporting Secondary Objective(s): {{secondary_analyses}}
- Handling of Intercurrent Events: {{intercurrent_events_handling}}

# Instructions

## 3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS
No text is intended here (heading only).

## 3.1 Primary Objective(s) and Associated Estimand(s)
No text is intended here (heading only) — do not write any paragraph directly under the 3.1 heading; content begins at the 3.1.x subsections.
For each primary objective found in {{primary_objectives_endpoints}}, create a subsection and define the associated estimand in a table. Use the following mapping:
- **Population**: Use the description from {{target_population}}.
- **Treatment**: Use the description from {{arm_1_investigational}} (and {{arm_2_control}} if applicable).
- **Endpoint**: Use the endpoint text from the objective itself.
- **Population-level Summary**: Infer the summary metric (e.g., difference in means, hazard ratio) from {{primary_analyses}}.
- **Intercurrent Events and Strategies**: List each event and its corresponding strategy from {{intercurrent_events_handling}} as separate rows in the table.

## 3.2 Secondary Objective(s) and Associated Estimand(s)
For EACH objective listed in {{key_secondary_objectives_endpoints}} and {{secondary_objectives_endpoints}}, you MUST create an estimand table using the same structure as 3.1. This includes:
- Efficacy objectives (PFS, ORR, DoR, etc.)
- Pharmacokinetic (PK) objectives
- Safety/tolerability objectives
- Cardiac/ECG objectives
- Exposure-response/correlation objectives
- Any other secondary objectives

For each secondary objective:
1) Create a numbered subsection (e.g., "### 3.2.1 [Objective Name]").
2) Restate the objective and define the endpoint clearly.
3) Construct an estimand table with:
   - **Population**: Use {{target_population}}.
   - **Treatment**: Use {{arm_1_investigational}} vs {{arm_2_control}}.
   - **Endpoint**: The endpoint from the objective text.
   - **Population-level Summary**: 
     - For PK objectives: Use "Descriptive statistics" or "PK parameters (Cmax, AUC, etc.)".
     - For safety objectives: Use "Incidence" or "Descriptive statistics".
     - For correlation/exposure-response: Use "Correlation coefficient" or "Association measure".
     - For efficacy endpoints: Infer from {{secondary_analyses}} (e.g., hazard ratio, difference in means).
   - **Intercurrent Events**: Use strategies from {{intercurrent_events_handling}} if available. If not, use appropriate default strategies (e.g., treatment policy for PK/safety endpoints).

DO NOT skip estimand tables for PK, safety, cardiac, or exposure-response objectives. If you have objective, endpoint, population, and treatment information, you MUST create an estimand table.

If no secondary objectives are provided, state: "Not applicable."

## 3.3 Exploratory Objective(s)
For EACH exploratory objective from {{exploratory_objectives_endpoints}}, you MUST create an estimand table if you have enough information. This includes:
- Biomarker correlation objectives
- Exploratory efficacy objectives
- Any other exploratory objectives

For each exploratory objective:
1) Create a numbered subsection (e.g., "### 3.3.1 [Exploratory Objective Name]").
2) Restate the objective and define the endpoint(s) clearly.
3) If you have objective, endpoint, population ({{target_population}}), and treatment ({{arm_1_investigational}} vs {{arm_2_control}}) information, construct an estimand table with:
   - **Population**: Use {{target_population}}.
   - **Treatment**: Use {{arm_1_investigational}} vs {{arm_2_control}}.
   - **Endpoint**: The correlation/relationship endpoint from the objective.
   - **Population-level Summary**: Use "Correlation coefficient", "Association measure", or "Descriptive statistics" as appropriate.
   - **Intercurrent Events**: Use strategies from {{intercurrent_events_handling}} if available, or appropriate default strategies.

DO NOT skip estimand tables for exploratory objectives. If you have sufficient information (objective, endpoint, population, treatment), you MUST create an estimand table, even if the context file examples show only efficacy objectives with tables.

If no exploratory objectives are provided, state: "Not applicable."

# Example

## Given the following input:
- **Primary Objectives/Endpoints**: To evaluate the efficacy of DrugX compared to placebo on Progression-Free Survival (PFS).
- **Target Population**: Adults with Stage IV NSCLC with EGFR mutations.
- **Investigational Regimen/Arm 1**: DrugX 100 mg orally once daily.
- **Control Regimen/Arm 2**: Placebo orally once daily.
- **Primary Analyses**: The primary analysis will be a log-rank test for PFS. The treatment effect will be estimated by the hazard ratio.
- **Intercurrent Events Handling**: Discontinuation of treatment due to adverse event: Treatment policy strategy. Death: Composite strategy.

## The generated output for that objective should be:

### 3.1 Primary Objective(s) and Associated Estimand(s)

#### 3.1.1 Estimand for the Primary Objective

| Estimand Characteristic | Description |
|---|---|
| **Population** | Adults with Stage IV NSCLC with EGFR mutations. |
| **Treatment** | DrugX 100 mg orally once daily versus Placebo orally once daily. |
| **Endpoint** | Progression-Free Survival (PFS). |
| **Population-level Summary** | Hazard Ratio. |
| **Intercurrent Event:** Discontinuation of treatment due to adverse event | **Strategy:** Treatment policy strategy. |
| **Intercurrent Event:** Death | **Strategy:** Composite strategy. |

### 3.2 Secondary Objective(s) and Associated Estimand(s)

#### 3.2.1 Estimand for the Secondary Objective

| Estimand Characteristic | Description |
|---|---|
| **Population** | Adults with Stage IV NSCLC with EGFR mutations. |
| **Treatment** | DrugX 100 mg orally once daily versus Placebo orally once daily. |
| **Endpoint** | Progression-Free Survival (PFS). |
| **Population-level Summary** | Hazard Ratio. |
| **Intercurrent Event:** Discontinuation of treatment due to adverse event | **Strategy:** Treatment policy strategy. |
| **Intercurrent Event:** Death | **Strategy:** Composite strategy. |



### 3.3 Exploratory Objective(s) and Associated Estimand(s)

#### 3.3.1 Estimand for the Exploratory Objective

| Estimand Characteristic | Description |
|---|---|
| **Population** | Adults with Stage IV NSCLC with EGFR mutations. |
| **Treatment** | DrugX 100 mg orally once daily versus Placebo orally once daily. |
| **Endpoint** | Progression-Free Survival (PFS). |
| **Population-level Summary** | Hazard Ratio. |
| **Intercurrent Event:** Discontinuation of treatment due to adverse event | **Strategy:** Treatment policy strategy. |
| **Intercurrent Event:** Death | **Strategy:** Composite strategy. |`
  },
  {
    title: '4. TRIAL DESIGN',
    context: '',
    output: '',
    useChunkedGeneration: true,
    chunks: {
      chunk1: `**ROLE**
You are an expert medical writer specializing in clinical trial protocol development. Your expertise lies in translating structured data from protocol planning forms into clear, compliant, and well-structured regulatory documents. You write with precision, using standard clinical terminology appropriate for a global audience of regulators, investigators, and clinical operations teams.

**TASK**
You will generate the complete text for "Section 4 – Trial Design" of a clinical trial protocol. You must use the provided input data and contextual information to construct a comprehensive and internally consistent document.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 1 (Protocol Synopsis and Schema):** {{section_1_summary}}
* **Section 3 (Trial Objectives and Estimands):** {{section_3_summary}}
* **Section 6 (Trial Intervention and Concomitant Therapy):** {{section_6_summary}}

**FIELD REFERENCES:**

**4.1 Description of Trial Design**
* Study Type: {{study_overview.study_type_dropdown}}
* Phase: {{study_overview.study_phase}}
* Study Design: {{study_overview.study_design}} (for single_arm, platform_umbrella_basket, adaptive)
* Number of Study Parts: {{study_overview.number_of_study_parts_new}}
* Study Parts/Cohorts: {{study_overview.study_parts_cohorts}}
* Study Periods: {{study_overview.study_parts[part][cohort].study_periods}}
* Study Parts (includes blinding, treatment allocation, committees per part/cohort): {{study_overview.study_parts}}
* Committees: {{regulatory_requirements.committees}}
* Interim Analysis: {{statistical_considerations.interim_analyses}}
* Treatment Table: {{study_treatments.treatment_table}}
* Preferred Short Title: {{study_identification.preferred_short_title}}

**4.1.1 Stakeholder Input into Design**
* Regulatory Comments: {{regulatory_requirements.comments}}
* Study Overview Comments: {{study_overview.comments}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**4. Trial Design**

**4.1 Description of Trial Design**
Using the input data, generate a concise, high-level summary of the trial design. Synthesize: StudyType, TrialInterventionModel, ControlMethod, TypeOfTrial, StudyPhase, Randomization, Blinding, NumberOfStudyArms, StudyPeriods, TrialDuration, GeographicScope, Committees, InterimAnalyses, and any adaptive/novel features.

**Example Format:**
This is a Phase [StudyPhase] randomized, [blinding], [intervention model], [type of trial], interventional trial conducted at [geographic scope] centers. Participants will be assigned to one of [N] treatment arms via a [randomization method] randomization process. The trial design includes a [list periods] with an expected total duration of approximately [X] weeks for each participant. A [Committee Name] will be established to oversee the trial. [Interim analysis details] as detailed in Section 6. The overall design was chosen to effectively evaluate the trial's primary and secondary objectives and endpoints.

**4.1.1 Stakeholder Input into Design**
Using the additional_notes field (merged with study_parts comments when present), describe how patient groups, investigators, or other stakeholders provided input and what changes were made in response. If no input was gathered, state that.

**Example:**
Stakeholder input was gathered from [patients/clinicians/advocacy groups] during protocol development. Feedback on [burden of procedures, duration, endpoint relevance] was incorporated into [modifying visit schedule/reducing procedures/adjusting eligibility criteria].

**CONSTRAINTS**
* **Tone:** Maintain a formal, objective, and scientific tone suitable for a regulatory filing.
* **Consistency:** Ensure all generated text is fully consistent with the information provided in the input data.
* **Clarity:** Write in clear, unambiguous language. Avoid jargon where simpler clinical terms suffice.
* **Completeness:** Address every subsection in the template. If information for a subsection is not available, state "Not applicable" or "No [feature] is planned for this trial."
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`,

      chunk2: `**ROLE**
You are an expert medical writer specializing in clinical trial protocol development. Your expertise lies in translating structured data from protocol planning forms into clear, compliant, and well-structured regulatory documents. You write with precision, using standard clinical terminology appropriate for a global audience of regulators, investigators, and clinical operations teams.

**TASK**
You will generate the complete text for "Section 4 – Trial Design" of a clinical trial protocol. You must use the provided input data and contextual information to construct a comprehensive and internally consistent document.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 1 (Protocol Synopsis and Schema):** {{section_1_summary}}
* **Section 3 (Trial Objectives and Estimands):** {{section_3_summary}}
* **Section 6 (Trial Intervention and Concomitant Therapy):** {{section_6_summary}}

**FIELD REFERENCES:**

**4.2 Rationale for Trial Design**

**4.2.1 Rationale for Estimand(s)**
* Primary Objectives & Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
* Secondary Objectives & Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
* Analysis of Primary Objective: {{statistical_considerations.primary_objective_analysis}}
* Analysis of Secondary Objectives: {{statistical_considerations.secondary_objective_analysis}}
* Target Population: {{target_population.conditions_related_to_primary_disease}}
* Author Rationale for Estimands: {{background_information.rationale_for_estimands}}

**4.2.2 Rationale for Intervention Model**
* Study Type: {{study_overview.study_type_dropdown}}
* Study Design: {{study_overview.study_design}}
* Treatment Table: {{study_treatments.treatment_table}}
* Number of Study Parts: {{study_overview.number_of_study_parts_new}}
* Author Rationale for Interventional Type: {{background_information.rationale_for_interventional_type}}

**4.2.3 Rationale for Control Type**
* Control Regimen: {{study_treatments.control_regimen}}
* Current Standard of Care: {{background_information.current_standard_of_care}}
* Disease Epidemiology: {{background_information.disease_epidemiology}}
* Author Rationale for Control Method: {{background_information.rationale_for_control_method}}

**4.2.4 Rationale for Trial Duration**
* Study Periods: {{study_overview.study_parts[part][cohort].study_periods}}
* Primary Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
* Secondary Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
* Efficacy Assessments: {{study_assessments.efficacy_assessments}}
* Author Rationale for Trial Duration: {{background_information.rationale_for_trial_duration}}

**4.2.5 Rationale for Adaptive or Novel Trial Design**
* Study Design (Adaptive): {{study_overview.study_design.adaptive}}
* Study Design (Platform/Umbrella/Basket): {{study_overview.study_design.platform_umbrella_basket}}
* Author Rationale for Adaptive Design: {{background_information.rationale_for_adaptive_design}}

**4.2.6 Rationale for Interim Analysis**
* Interim Analyses: {{statistical_considerations.interim_analyses}}
* Author Rationale for Interim Analysis: {{background_information.rationale_for_interim_analysis}}

**4.2.7 Rationale for Other Trial Design Aspects**
* Author Rationale for Other Design Aspects: {{background_information.rationale_for_other_design_aspects}}
* Comments: {{study_overview.comments}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**4.2 Rationale for Trial Design**
For each subsection below, provide a clear justification for the design choice. When the corresponding background_information author-rationale field is populated in CLINICAL INTAKE DATA, use it as the primary narrative basis and supplement with structured intake fields.

**4.2.1 Rationale for Estimand(s)**
Justify the chosen estimand strategy using rationale_for_estimands when provided. Explain clinical relevance of endpoint(s), validity of the population-level summary measure, and strategy for handling intercurrent events. Reference primary/secondary objectives and analyses fields.

**4.2.2 Rationale for Intervention Model**
Using rationale_for_interventional_type (when populated), trial intervention model, number of study arms, and treatment table, justify the selected intervention structure (e.g., parallel, crossover, multi-arm).

**4.2.3 Rationale for Control Type**
Using rationale_for_control_method (when populated), control regimen, and current standard of care, explain why the chosen control is appropriate. Address known limitations or regional SOC variations.

**4.2.4 Rationale for Trial Duration**
Using rationale_for_trial_duration (when populated), study periods, and endpoint timing, explain why the specified duration is sufficient for primary and key secondary outcomes.

**4.2.5 Rationale for Adaptive or Novel Trial Design**
If adaptive elements are indicated, use rationale_for_adaptive_design when populated to describe adaptive aspects and scientific/operational rationale.

**4.2.6 Rationale for Interim Analysis**
Using rationale_for_interim_analysis and interim_analyses fields, describe purpose, timing, and potential impact of interim analyses. State that details are in Section 10.

**4.2.7 Rationale for Other Trial Design Aspects**
Use rationale_for_other_design_aspects when populated for any key design features not covered above.

**CONSTRAINTS**
* **Tone:** Maintain a formal, objective, and scientific tone suitable for a regulatory filing.
* **Consistency:** Ensure all generated text is fully consistent with the information provided in the input data.
* **Clarity:** Write in clear, unambiguous language. Avoid jargon where simpler clinical terms suffice.
* **Completeness:** Address every subsection in the template. If information for a subsection is not available, state "Not applicable" or "No [feature] is planned for this trial."
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`,

      chunk3: `**ROLE**
You are an expert medical writer specializing in clinical trial protocol development. Your expertise lies in translating structured data from protocol planning forms into clear, compliant, and well-structured regulatory documents. You write with precision, using standard clinical terminology appropriate for a global audience of regulators, investigators, and clinical operations teams.

**TASK**
You will generate the complete text for "Section 4 – Trial Design" of a clinical trial protocol. You must use the provided input data and contextual information to construct a comprehensive and internally consistent document.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 1 (Protocol Synopsis and Schema):** {{section_1_summary}}
* **Section 3 (Trial Objectives and Estimands):** {{section_3_summary}}
* **Section 6 (Trial Intervention and Concomitant Therapy):** {{section_6_summary}}

**FIELD REFERENCES:**

**4.3 Trial Stopping Rules**
* Permanent Discontinuation of Trial Intervention: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
* Temporary Discontinuation of Trial Intervention: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
* Discontinuation of Trial Intervention (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}

**4.4 Start of Trial and End of Trial**
* Regulatory Requirements: {{regulatory_requirements}}
* Study Overview: {{study_overview}}

**4.5 Access to Trial Intervention After End of Trial**
* Additional Information: {{study_treatments.additional_information}}
* Comments: {{study_overview.comments}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**4.3 Trial Stopping Rules**
Using the discontinuation fields, describe the specific criteria that would trigger a temporary pause or permanent termination of the trial, an arm, or a cohort. Also describe any criteria for restarting enrollment if applicable.

**Example:**
The trial may be temporarily paused or permanently discontinued based on [safety signals/futility/external data]. Individual participants will be discontinued from trial intervention if [criteria]. The trial may be restarted following [sponsor review/DMC recommendation/protocol amendment].

**4.4 Start of Trial and End of Trial**
Define the start and end of the trial. Use standard definitions unless specified otherwise in the regulatory requirements fields.

**Example:**
The start of the trial is defined as the date when the first participant signs informed consent (or alternative definition if specified). The end of the trial is defined as the last participant's last visit (or alternative definition per local requirements).

**4.5 Access to Trial Intervention After End of Trial**
Using additional_information fields, describe if continued access to the investigational product or a rollover into an extension study is planned for participants who complete the trial.

**Example:**
Participants who complete the trial and meet eligibility criteria may continue to receive [investigational product] through [extension study/expanded access program/compassionate use]. Details of continued access will be provided separately.

**CONSTRAINTS**
* **Tone:** Maintain a formal, objective, and scientific tone suitable for a regulatory filing.
* **Consistency:** Ensure all generated text is fully consistent with the information provided in the input data.
* **Clarity:** Write in clear, unambiguous language. Avoid jargon where simpler clinical terms suffice.
* **Completeness:** Address every subsection in the template. If information for a subsection is not available, state "Not applicable" or "No [feature] is planned for this trial."
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
    },
    prompt: `**ROLE**
You are an expert medical writer specializing in clinical trial protocol development. Your expertise lies in translating structured data from protocol planning forms into clear, compliant, and well-structured regulatory documents. You write with precision, using standard clinical terminology appropriate for a global audience of regulators, investigators, and clinical operations teams.

**TASK**
You will generate the complete text for "Section 4 – Trial Design" of a clinical trial protocol. You must use the provided input data and contextual information to construct a comprehensive and internally consistent document.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 1 (Protocol Synopsis and Schema):** {{section_1_summary}}
* **Section 3 (Trial Objectives and Estimands):** {{section_3_summary}}
* **Section 6 (Trial Intervention and Concomitant Therapy):** {{section_6_summary}}

**FIELD REFERENCES:**

**4.1 Description of Trial Design**
- Study Type: {{study_overview.study_type_dropdown}}
- Study Design: {{study_overview.study_design}} (for single_arm, platform_umbrella_basket, adaptive)
- Number of Study Parts: {{study_overview.number_of_study_parts_new}}
- Study Parts/Cohorts: {{study_overview.study_parts_cohorts}}
- Study Periods: {{study_overview.study_parts[part][cohort].study_periods}}
- Study Parts (includes blinding, treatment allocation per part/cohort): {{study_overview.study_parts}}
- Committees: {{regulatory_requirements.committees}}
- Interim Analysis: {{statistical_considerations.interim_analyses}}
- Treatment Table: {{study_treatments.treatment_table}}
- Preferred Short Title: {{study_identification.preferred_short_title}}

**4.1.1 Stakeholder Input into Design**
- Regulatory Comments: {{regulatory_requirements.comments}}
- Study Overview Comments: {{study_overview.comments}}

**4.2 Rationale for Trial Design**

**4.2.1 Rationale for Estimand(s)**
- Primary Objectives & Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Objectives & Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Analysis of Primary Objective: {{statistical_considerations.primary_objective_analysis}}
- Analysis of Secondary Objectives: {{statistical_considerations.secondary_objective_analysis}}
- Target Population: {{target_population.conditions_related_to_primary_disease}}
- Author Rationale for Estimands: {{background_information.rationale_for_estimands}}

**4.2.2 Rationale for Intervention Model**
- Study Type: {{study_overview.study_type_dropdown}}
- Study Design: {{study_overview.study_design}}
- Treatment Table: {{study_treatments.treatment_table}}
- Number of Study Parts: {{study_overview.number_of_study_parts_new}}
- Author Rationale for Interventional Type: {{background_information.rationale_for_interventional_type}}

**4.2.3 Rationale for Control Type**
- Control Regimen: {{study_treatments.control_regimen}}
- Current Standard of Care: {{background_information.current_standard_of_care}}
- Disease Epidemiology: {{background_information.disease_epidemiology}}
- Author Rationale for Control Method: {{background_information.rationale_for_control_method}}

**4.2.4 Rationale for Trial Duration**
- Study Periods: {{study_overview.study_parts[part][cohort].study_periods}}
- Primary Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Efficacy Assessments: {{study_assessments.efficacy_assessments}}
- Author Rationale for Trial Duration: {{background_information.rationale_for_trial_duration}}

**4.2.5 Rationale for Adaptive or Novel Trial Design**
- Study Design (Adaptive): {{study_overview.study_design.adaptive}}
- Study Design (Platform/Umbrella/Basket): {{study_overview.study_design.platform_umbrella_basket}}
- Author Rationale for Adaptive Design: {{background_information.rationale_for_adaptive_design}}

**4.2.6 Rationale for Interim Analysis**
- Interim Analyses: {{statistical_considerations.interim_analyses}}
- Author Rationale for Interim Analysis: {{background_information.rationale_for_interim_analysis}}

**4.2.7 Rationale for Other Trial Design Aspects**
- Author Rationale for Other Design Aspects: {{background_information.rationale_for_other_design_aspects}}
- Comments: {{study_overview.comments}}

**4.3 Trial Stopping Rules**
- Permanent Discontinuation of Trial Intervention: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- Temporary Discontinuation of Trial Intervention: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
- Discontinuation of Trial Intervention (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}

**4.4 Start of Trial and End of Trial**
- Regulatory Requirements: {{regulatory_requirements}}
- Study Overview: {{study_overview}}

**4.5 Access to Trial Intervention After End of Trial**
- Additional Information: {{study_treatments.additional_information}}
- Comments: {{study_overview.comments}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**4. Trial Design**

**4.1 Description of Trial Design**
Using the input data, generate a concise, high-level summary of the trial design. Synthesize: StudyType, TrialInterventionModel, ControlMethod, TypeOfTrial, StudyPhase, Randomization, Blinding, NumberOfStudyArms, StudyPeriods, TrialDuration, GeographicScope, Committees, InterimAnalyses, and any adaptive/novel features
Include:
A dedicated paragraph describing any decentralized or hybrid trial elements, including but not limited to:

telemedicine or remote visits

eConsent

home health services

direct-to-patient IP shipment

remote safety or efficacy assessments

digital tools or wearable technologies

remote monitoring or ePRO data collection
If no decentralized elements are specified in the input data, state: “No decentralized or remote trial elements are planned for this study..

**Example Format:**
This is a Phase [StudyPhase] randomized, [blinding], [intervention model], [type of trial], interventional trial conducted at [geographic scope] centers. Participants will be assigned to one of [N] treatment arms via a [randomization method] randomization process. The trial design includes a [list periods] with an expected total duration of approximately [X] weeks for each participant. A [Committee Name] will be established to oversee the trial. [Interim analysis details] as detailed in Section 6. The overall design was chosen to effectively evaluate the trial's primary and secondary objectives and endpoints. Decentralized Trial Elements
This study incorporates decentralized and remote trial elements, including [telemedicine visits, eConsent, remote PRO data collection, home health visits, direct-to-patient investigational product shipment, remote safety assessments, wearable devices, digital tools, etc.], to reduce participant burden, improve accessibility, and enhance continuity of data collection. These decentralized components are integrated into the visit schedule and operational plan where appropriate and are conducted in accordance with local regulatory requirements and Good Clinical Practice (GCP). If a participant is unable to attend an on-site visit, approved remote procedures may be used when clinically appropriate and when permitted by site capabilities and regulations. If no decentralized elements are feasible at specific sites or regions, procedures will be conducted on-site per local requirements.

**4.1.1 Stakeholder Input into Design**
Using the additional_notes field (merged with study_parts comments when present), describe how patient groups, investigators, or other stakeholders provided input and what changes were made in response.If decentralized components were added based on stakeholder feedback (e.g., to reduce participant burden or improve accessibility), describe this here.
If no input was gathered, state that. If no input was gathered, state that.

**Example:**
Stakeholder input was gathered from [patients/clinicians/advocacy groups] during protocol development. Feedback on [burden of procedures, duration, endpoint relevance] was incorporated into [modifying visit schedule/reducing procedures/adjusting eligibility criteria].

**4.2 Rationale for Trial Design**
For each subsection below, provide a clear justification for the design choice. When the corresponding background_information author-rationale field is populated in CLINICAL INTAKE DATA, use it as the primary narrative basis and supplement with structured intake fields.

**4.2.1 Rationale for Estimand(s)**
Justify the chosen estimand strategy using rationale_for_estimands when provided. Explain clinical relevance of endpoint(s), validity of the population-level summary measure, and strategy for handling intercurrent events. Reference primary/secondary objectives and analyses fields.

**4.2.2 Rationale for Intervention Model**
Using rationale_for_interventional_type (when populated), trial intervention model, number of study arms, and treatment table, justify the selected intervention structure (e.g., parallel, crossover, multi-arm).

**4.2.3 Rationale for Control Type**
Using rationale_for_control_method (when populated), control regimen, and current standard of care, explain why the chosen control is appropriate. Address known limitations or regional SOC variations.

**4.2.4 Rationale for Trial Duration**
Using rationale_for_trial_duration (when populated), study periods, and endpoint timing, explain why the specified duration is sufficient for primary and key secondary outcomes.

**4.2.5 Rationale for Adaptive or Novel Trial Design**
If adaptive elements are indicated, use rationale_for_adaptive_design when populated to describe adaptive aspects and scientific/operational rationale.

**4.2.6 Rationale for Interim Analysis**
Using rationale_for_interim_analysis and interim_analyses fields, describe purpose, timing, and potential impact of interim analyses. State that details are in Section 10.

**4.2.7 Rationale for Other Trial Design Aspects**
Use rationale_for_other_design_aspects when populated for any key design features not covered above. In addition to other design features, this section must include justification for any decentralized or hybrid trial elements, such as:

improving recruitment in rare diseases

reducing travel burden for frail or geographically distributed populations

maintaining continuity of care

increasing data completeness or real-time monitoring

If no such elements are used, state: “No additional trial design features requiring justification are planned.”

**4.3 Trial Stopping Rules**
Using the discontinuation fields, describe the specific criteria that would trigger a temporary pause or permanent termination of the trial, an arm, or a cohort. Also describe any criteria for restarting enrollment if applicable.

**Example:**
The trial may be temporarily paused or permanently discontinued based on [safety signals/futility/external data]. Individual participants will be discontinued from trial intervention if [criteria]. The trial may be restarted following [sponsor review/DMC recommendation/protocol amendment].

**4.4 Start of Trial and End of Trial**
Define the start and end of the trial. Use standard definitions unless specified otherwise in the regulatory requirements fields.

**Example:**
The start of the trial is defined as the date when the first participant signs informed consent (or alternative definition if specified). The end of the trial is defined as the last participant's last visit (or alternative definition per local requirements).

**4.5 Access to Trial Intervention After End of Trial**
Using additional_information fields, describe if continued access to the investigational product or a rollover into an extension study is planned for participants who complete the trial.

**Example:**
Participants who complete the trial and meet eligibility criteria may continue to receive [investigational product] through [extension study/expanded access program/compassionate use]. Details of continued access will be provided separately.

**CONSTRAINTS**
* **Tone:** Maintain a formal, objective, and scientific tone suitable for a regulatory filing.
* **Consistency:** Ensure all generated text is fully consistent with the information provided in the input data.
* **Clarity:** Write in clear, unambiguous language. Avoid jargon where simpler clinical terms suffice.
* **Completeness:** Address every subsection in the template. If information for a subsection is not available, state "Not applicable" or "No [feature] is planned for this trial."
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
  },
  {
    title: '5. TRIAL POPULATION',
    context: '',
    output: '',
    prompt: `**ROLE**
You are an expert medical writer and prompt engineer specializing in oncology and clinical trial protocol writing. Your expertise lies in generating content that is fully compliant with ICH M11 style and conventions, as well as GCP, ICH E6, E8, and E9.

**TASK**
You will generate the complete text for "Section 5 – Trial Population" of a clinical trial protocol. You must use the provided input data and contextual information to construct a comprehensive and internally consistent document.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Trial Objectives and Estimands):** {{section_3_summary}}
* **Section 4 (Trial Design):** {{section_4_summary}}

**TEMPLATE LANGUAGE (AUTHORITATIVE BASE TEXT WHEN PRESENT)**
CRITICAL — these fields come from the CPT (TransCelerate) template library, which mixes two kinds of text in the same block: (a) actual content to copy, marked between \`<Start of common text>\`/\`<End of common text>\` or \`<Start of example text>\`/\`<End of example text>\`, and (b) plain instructional sentences above/between those markers (e.g. "State the importance of...", "Describe procedures for...", "If no committees are involved, state...") that are DIRECTIONS for what to write, not content to output. Copy ONLY the text inside the Start/End markers (tailored per the rules below) into the generated protocol text. NEVER copy the plain instructional sentences themselves into the output — follow them as instructions instead (e.g. an "If X, state Y" instruction means output Y when X applies, not the instruction sentence).
The clinical intake payload may include pre-written standard template language for Section 7 topics under "discontinuation_rules" (and may also include related standard text in "template_language_fields" for consistency across safety sections). When the following discontinuation_rules fields are non-empty, you MUST copy-paste them verbatim under the matching subsection heading (HTML format). Do NOT paraphrase or rewrite. Only lightly adjust bracketed placeholders or study-specific cross-references when required for internal consistency:
- For **5.4.1 Definitions Related to Childbearing Potential**, use EXACTLY (verbatim, no paraphrase): {{template_language_fields.definitions_childbearing_potential}}
- For **5.4.2 Contraception Requirements**, use EXACTLY (verbatim, no paraphrase): {{template_language_fields.contraception_requirements}}
These fields are assembled deterministically from the TransCelerate Patient Library decision tree. Do NOT rewrite, summarize, or merge Patient Library contraception option text (F1A, F1B, F2, F3, M1A, M1B). Study-specific reproductive requirements may appear only after the Patient Library blocks.
If these are empty, write standard regulator-aligned language consistent with ICH GCP and the rest of the protocol and use the reproductive-status inputs below to tailor bracket placeholders.


**FIELD REFERENCES:**

**5.1 Description of Trial Population and Rationale**
Brief Description of Trial Population: {{study_overview.study_parts[part][cohort].brief_description_of_trial_population}}
- General Description of Trial Population: {{study_overview.study_parts[part][cohort].general_description_of_trial_population}}
- Conditions (Primary Disease): {{study_overview.study_parts[part][cohort].conditions_primary_disease}}
- Conditions Related to Primary Disease: {{target_population.conditions_related_to_primary_disease}}
- Patient Performance Status: {{target_population.patient_performance_status}}
- Life Expectancy: {{target_population.life_expectancy}}
- Organ Function/Lab Parameters: {{target_population.organ_function_lab_parameters}}
- Prior Therapy Requirements: {{target_population.prior_therapy_requirements}}
- Eligibility Criteria: {{target_population.eligibility_criteria}}
- Eligibility Criteria (Cohort): {{study_overview.study_parts[part][cohort].eligibility_criteria}}
- Author Rationale for Trial Population: {{background_information.rationale_for_trial_population}}

**5.2 Inclusion Criteria**
- Eligibility Criteria (Cohort): {{study_overview.study_parts[part][cohort].eligibility_criteria}}
- Conditions Related to Primary Disease: {{target_population.conditions_related_to_primary_disease}}
- Tissue Sample/Procedure Compliance: {{target_population.tissue_sample_procedure_compliance}}
- Patient Performance Status: {{target_population.patient_performance_status}}
- Organ Function/Lab Parameters: {{target_population.organ_function_lab_parameters}}
- Concomitant Meds/Washout: {{target_population.concomitant_meds_washout}}
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}
- Eligibility Criteria: {{target_population.eligibility_criteria}}

**5.3 Exclusion Criteria**
- Eligibility Criteria (Cohort): {{study_overview.study_parts[part][cohort].eligibility_criteria}}
- Comorbidities & Infections: {{target_population.comorbidities_infections}}
- Concomitant Meds/Washout: {{target_population.concomitant_meds_washout}}
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}
- Eligibility Criteria: {{target_population.eligibility_criteria}}

**5.4 Contraception**
**5.4.1 Definitions Related to Childbearing Potential**
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}
- Reproductive Status (Cohort): {{study_overview.study_parts[part][cohort].reproductive_status}}

**5.4.2 Contraception Requirements**
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}
- Reproductive Status (Cohort): {{study_overview.study_parts[part][cohort].reproductive_status}}

**5.5 Lifestyle Restrictions**
- Concomitant Medications Prohibited: {{study_treatments.concomitant_medications_prohibited}}
- Concomitant Medications Allowed: {{study_treatments.concomitant_medications_allowed}}
- Additional lifestyle restrictions from safety assessments or PK requirements

**5.6 Screen Failure and Rescreening**
- Screen Failure/Rescreening Rules: {{discontinuation_rules.screen_failure_rescreening_rules}}
- Discontinuation of Trial Intervention (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Permanent Discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- Temporary Discontinuation: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
- Participant Discontinuation/Withdrawal: {{discontinuation_rules.participant_discontinuation_withdrawal}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**5. TRIAL POPULATION**

**5.1 Description of Trial Population and Rationale**
Using the input data, generate a concise narrative that includes:
1. A description of the intended population (age, disease condition, biomarker status, prior therapy, and other key eligibility features)
2. A justification for why this population is appropriate for evaluating the investigational product, referencing the estimand from Section 3 summary. When background_information.rationale_for_trial_population is populated in CLINICAL INTAKE DATA, use it as the primary basis for the rationale narrative and supplement with structured population fields.
3. Mention of specific subgroup characteristics if the estimand is targeted to a subpopulation
4. A statement on the inclusion of vulnerable or special populations, if applicable

**Example:**
The trial will enroll adult participants aged 18–75 years with histologically confirmed metastatic colorectal cancer expressing KRAS wild-type status. Participants must demonstrate ECOG performance status of 0–1 and adequate renal and hepatic function. Patients must have failed at least one prior line of systemic therapy. This population reflects the target population for future clinical use and aligns with the primary estimand defined in Section 3.

**5.2 Inclusion Criteria**
Using the eligibility_criteria fields, list clear, binary (yes/no) inclusion criteria. Number them sequentially.

**Format:**
To be eligible for this trial, participants must meet all of the following criteria:
1. [Criterion 1]
2. [Criterion 2]
3. [Criterion 3]
...

**Example:**
1. Age ≥18 and ≤75 years at time of screening
2. ECOG performance status 0–1
3. Histologically confirmed diagnosis of [disease]
4. Measurable disease per RECIST v1.1
5. Adequate organ function as defined by:
   - Creatinine clearance ≥ 60 mL/min
   - AST/ALT ≤ 2.5× ULN
6. Signed informed consent

**5.3 Exclusion Criteria**
Using the eligibility_criteria and comorbidities fields, list clear binary (yes/no) exclusion criteria. Number sequentially.

**Format:**
Participants meeting any of the following criteria will be excluded from the trial:
1. [Criterion 1]
2. [Criterion 2]
3. [Criterion 3]
...

**Example:**
1. Known active CNS metastases
2. Active uncontrolled infection requiring systemic therapy
3. History of allergic reaction to any study drug components
4. Receipt of another investigational agent within 28 days
5. Pregnant or breastfeeding

**5.4 Contraception**

**5.4.1 Definitions Related to Childbearing Potential**
When template_language_fields.definitions_childbearing_potential is non-empty, copy it verbatim as Section 5.4.1 (TransCelerate Patient Library CBP or NCBP block — never both). Do not paraphrase or merge pathways. If empty, write standard regulator-aligned language from reproductive_status_contraception intake only.

**5.4.2 Contraception Requirements**
When template_language_fields.contraception_requirements is non-empty, copy it verbatim as Section 5.4.2 (Patient Library F1A/F1B/F2/F3/M1A/M1B blocks and/or NCBP applicability statement). Do not rewrite, summarize, or invent contraception methods or durations. Study-specific reproductive requirements may appear only after the Patient Library content. If empty, describe only what is documented in reproductive_status_contraception — do not invent requirements.

**5.5 Lifestyle Restrictions**
No text is intended here (heading only) — populate the four numbered subsections below. If a subsection's category is entirely empty or not applicable, state "No restrictions of this type are required for this trial" under that subsection's own heading rather than omitting the heading.

**5.5.1 Meals and Dietary Restrictions**
Describe any required fasting, dietary exclusions, or meal-timing restrictions (e.g. relative to dosing or PK sampling).

**5.5.2 Caffeine, Alcohol, Tobacco, and Other Restrictions**
Describe restrictions on caffeine, alcohol, tobacco, and other substances.

**Example:**
Participants must refrain from caffeine intake for 12 hours prior to PK assessments and from alcohol consumption during the treatment period.

**5.5.3 Physical Activity Restrictions**
Describe any restrictions on strenuous or specific physical activity (e.g. exercise restrictions around dosing or procedures).

**5.5.4 Other Activity Restrictions**
Describe any other lifestyle restrictions not covered above (e.g. driving restrictions post-dose, blood/tissue donation).

**Example:**
Participants must refrain from driving for 4 hours post-dose on clinic visit days.

**5.6 Screen Failure and Rescreening**
Using the discontinuation_rules fields:
1. Define a screen failure as a participant who provides informed consent but does not meet all inclusion/exclusion criteria
2. Based on the screen_failure_rescreening_rules field, describe the rescreening policy
3. If rescreening is allowed, detail the conditions and time window
4. If not allowed, state that rescreening is not permitted

**Example:**
Participants who fail screening may be rescreened once if the cause of failure is transient (e.g., temporary lab abnormality). Rescreening must occur within 21 days of the initial screening and requires written documentation from the Investigator justifying the decision.

Alternatively: "Rescreening is not permitted for this trial. Participants who fail to meet eligibility criteria during screening will be considered screen failures and cannot be re-evaluated."

**CONSTRAINTS**
* **Adherence to Standards:** The entire output must strictly adhere to ICH M11 style and conventions, as well as GCP, ICH E6, E8, and E9.
* **Consistency:** Ensure the generated text is fully consistent with the provided contextual summaries for Section 3 and Section 4.
* **Completeness:** Generate all specified subsections from 5.1 to 5.6. Do not omit any part.
* **Clarity:** Use precise, unambiguous medical and regulatory language. All criteria must be binary (the patient either meets it or does not).
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
  },
  {
    title: '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
    context: '',
    output: '',
    prompt: `**ROLE**
You are an expert medical writer with deep expertise in creating clinical trial protocols compliant with ICH E6(R2), Transcelerate, and other global regulatory standards. Your writing is precise, formal, and scientifically accurate.

**TASK**
You will generate the complete text for "Section 6: Trial Intervention and Concomitant Therapy" of a clinical study protocol. This task involves transforming structured data from a protocol development form into a coherent, compliant narrative.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Trial Objectives and Estimands):** {{section_3_summary}}
* **Section 4 (Trial Design):** {{section_4_summary}}

**FIELD REFERENCES:**

**6.1 Description of Investigational Trial Intervention**
- Preferred Short Title: {{study_identification.preferred_short_title}}
- Study Type: {{study_overview.study_type_dropdown}}
- Study Design: {{study_overview.study_design}}
- Regimen Arms: {{study_treatments.regimen_arms}}
- Treatment Table: {{study_treatments.treatment_table}}
- Number of Agents: {{study_treatments.num_agents}}
- Number of Regimens: {{study_treatments.num_regimens}}

**6.2 Rationale for Investigational Trial Intervention Dose and Regimen**
- Purpose of Trial: {{background_information.purpose_of_trial}}
- Rationale for Interventional Type: {{background_information.rationale_for_interventional_type}}
- Benefits of Investigational: {{background_information.benefits_investigational}}
- Treatment Table: {{study_treatments.treatment_table}}

**6.3 Investigational Trial Intervention Administration**
- Treatment Table: {{study_treatments.treatment_table}}
- Concomitant Medications Allowed: {{study_treatments.concomitant_medications_allowed}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}

**6.4 Investigational Trial Intervention Dose Modification**
- Treatment Table: {{study_treatments.treatment_table}}
- Permanent Discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- Temporary Discontinuation: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
- Rechallenge: {{discontinuation_rules.rechallenge}}

**6.5 Management of Investigational Trial Intervention Overdose**
- Treatment Table: {{study_treatments.treatment_table}}
- FDA Pregnancy Risk Categories: {{study_treatments.fda_pregnancy_risk_categories}}

**6.6 Preparation, Storage, Handling, and Accountability**
- Treatment Table: {{study_treatments.treatment_table}}
- Additional Information: {{study_treatments.additional_information}}

**6.7 Investigational Trial Intervention Assignment, Randomization, and Blinding**
- Study Design: {{study_overview.study_design}}
- Study Parts (includes method_treatment_allocation, blinding_masking per cohort): {{study_overview.study_parts}}
- Rationale for Other Design Aspects: {{background_information.rationale_for_other_design_aspects}}
- Comments: {{study_overview.comments}}

**6.8 Investigational Trial Intervention Adherence**
- Pharmacokinetics: {{study_assessments.pharmacokinetics}}
- Study Assessments Comments: {{study_assessments.comments}}
- Other Analyses: {{statistical_considerations.other_analyses}}

**6.9 Description of Noninvestigational Trial Intervention (NIMP)**
- Treatment Table: {{study_treatments.treatment_table}}
- Concomitant Medications Allowed: {{study_treatments.concomitant_medications_allowed}}
- Additional Information: {{study_treatments.additional_information}}

**6.10 Concomitant Therapy**
**6.10.1 Prohibited Concomitant Therapy**
- Concomitant Medications Prohibited: {{study_treatments.concomitant_medications_prohibited}}
- Treatment Table: {{study_treatments.treatment_table}}

**6.10.2 Permitted Concomitant Therapy**
- Concomitant Medications Allowed: {{study_treatments.concomitant_medications_allowed}}
- Treatment Table: {{study_treatments.treatment_table}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**6. Trial Intervention and Concomitant Therapy**

**6.1 Description of Investigational Trial Intervention**
Using the regimen_arms and treatment_table fields, generate a descriptive paragraph for each treatment arm covering:
- Agent name and IMP/NIMP designation
- Pharmaceutical form, strength/dose
- Route of administration and frequency
- Total treatment duration
- Any special delivery devices or packaging details

**Example:**
This trial includes two treatment arms. Participants randomized to Arm A will receive Agenixol administered as a 200 mg oral tablet once daily (QD) for 24 weeks. Participants randomized to Arm B will receive matching placebo as an oral tablet once daily (QD) for 24 weeks. Both products are supplied in 30-count bottles appropriately labeled for clinical trial use.

**6.2 Rationale for Investigational Trial Intervention Dose and Regimen**
Using purpose_of_trial, rationale_for_interventional_type, and benefits_investigational fields, generate a narrative explaining:
- The scientific rationale for the selected dose, form, and regimen
- Basis in prior clinical/nonclinical data, PK/PD modeling, or pharmacological evidence
- Any adjustments made for the specific trial population
- Reference supporting documents (e.g., Investigator's Brochure)

**6.3 Investigational Trial Intervention Administration**
Using treatment_table and concomitant_medications_allowed fields, describe:
- Timing of doses (e.g., daily, weekly)
- Instructions regarding meals
- Whether self-administered or done in clinic
- Specific instructions for site personnel on preparation and handling
- Policy for managing missed or vomited doses

**6.4 Investigational Trial Intervention Dose Modification**
Using treatment_table and discontinuation fields, outline dose modification rules using bullet points:
- Specific conditions for dose reduction or escalation
- Criteria for temporary or permanent discontinuation
- Procedures for washout or rechallenge
- Minimum interval required between dose adjustments

**6.5 Management of Investigational Trial Intervention Overdose**
Using treatment_table fields, generate a paragraph that:
- Defines what constitutes an overdose for the IMP
- Describes potential signs and symptoms to monitor
- Outlines immediate actions the investigator must take
- References specific sections of the IB or product label for detailed guidance

**6.6 Preparation, Storage, Handling, and Accountability**

**6.6.1 Preparation:**
Using treatment_table, describe how and by whom the IMP is prepared (e.g., reconstitution, dilution, thawing), including stability and hold times post-preparation. If not provided, state: [Information to be supplied by sponsor]

**6.6.2 Storage and Handling:**
Specify required storage conditions (e.g., temperature, light protection) and any special handling instructions for the site. If not provided, state: [Information to be supplied by sponsor]

**6.6.3 Accountability:**
Describe the drug accountability process, including sourcing, distribution to sites, procedures for receipt and logging, and instructions for return or disposal of unused IMP. If not provided, state: [Information to be supplied by sponsor]

**6.7 Investigational Trial Intervention Assignment, Randomization, and Blinding**

**6.7.1 Participant Assignment:**
Using study_design and study_parts fields, describe:
- The randomization process (or state if open-label)
- When and how participant IDs are assigned
- All stratification factors
- Cross-reference the statistical section if the design is adaptive

**6.7.2 Randomization Method:**
Specify the system used for randomization (e.g., central IRT, sponsor-generated list) and identify who holds the randomization code. Reference study_parts for method_treatment_allocation details.

**6.7.3 Measures to Maintain Blinding:**
Using study_parts (blinding_masking), describe:
- Measures taken to maintain the blind (e.g., matching placebo, identical packaging)
- Plans and procedures for both interim and emergency unblinding

**6.7.4 Emergency Unblinding at Site:**
State the exact conditions under which emergency unblinding is permissible, who is authorized to perform it, and outline required documentation, notification, and reporting procedures.

**6.8 Investigational Trial Intervention Adherence**
Using pharmacokinetics and other_analyses fields, describe:
- How participant adherence will be measured and recorded (e.g., diaries, pill counts, PK sampling)
- Participant responsibilities
- Documentation required at each visit

**6.9 Description of Noninvestigational Trial Intervention (NIMP)**

**6.9.1 Background Intervention:**
Describe any pre-existing or background Standard of Care therapies continued during the trial.

**6.9.2 Rescue Therapy:**
List and describe specific medications allowed for urgent symptom relief, conditions for their administration, and if their use triggers participant discontinuation.

**6.9.3 Other Noninvestigational Interventions:**
List any other trial-mandated NIMPs, such as challenge agents or specific diagnostic tools.

**6.10 Concomitant Therapy**

**6.10.1 Prohibited Concomitant Therapy:**
Using concomitant_medications_prohibited field, list all medications, therapies, or food products that are disallowed during the trial due to potential for interaction or confounding effects. Use bullet points for clarity.

**6.10.2 Permitted Concomitant Therapy:**
Using concomitant_medications_allowed field, describe or list medications and treatments that are explicitly allowed or recommended as supportive care (e.g., hydration, antiemetics, analgesics). Also describe how all concomitant therapies will be collected and documented throughout the trial.

**CONSTRAINTS**
* **Tone:** Maintain a formal, objective, and scientific tone suitable for a regulatory submission.
* **Missing Data:** If a required data field is missing or empty, state [Information to be supplied by sponsor] in the relevant part of the generated text. Do not invent, infer, or omit the topic.
* **Referencing:** When instructed to reference a prior section, use the format (see Section X.Y).
* **Verbatim Data:** When incorporating specific data points like dose, route, or frequency, use the exact values provided in the input data.
* **Definitions:** Use standard definitions (IMP: Investigational Medicinal Product, NIMP: Non-Investigational Medicinal Product, PK/PD: Pharmacokinetics/Pharmacodynamics, SoC: Standard of Care, IB: Investigator's Brochure, IRT: Interactive Response Technology).
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`    
  },
  {
    title: '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
    context: '',
    output: '',
    prompt: `**ROLE**
You are a specialized AI assistant functioning as an expert medical writer. Your expertise lies in drafting clinical trial protocols for oncology studies, with a deep understanding of regulatory guidelines, specifically the ICH M11 style and conventions.

**TASK**
You will generate the complete and compliant text for "Section 7 (Participant Discontinuation)" of a clinical trial protocol, using the structured input data provided.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Intercurrent Events):** {{section_3_intercurrent_events}}
* **Section 6 (Intervention Rules):** {{section_6_intervention_rules}}
* **Schedule of Activities:** {{schedule_of_activities_soa}}

**TEMPLATE LANGUAGE (AUTHORITATIVE BASE TEXT WHEN PRESENT)**
CRITICAL — these fields come from the CPT (TransCelerate) template library, which mixes two kinds of text in the same block: (a) actual content to copy, marked between \`<Start of common text>\`/\`<End of common text>\` or \`<Start of example text>\`/\`<End of example text>\`, and (b) plain instructional sentences above/between those markers (e.g. "State the importance of...", "Describe procedures for...", "If no committees are involved, state...") that are DIRECTIONS for what to write, not content to output. Copy ONLY the text inside the Start/End markers (tailored per the rules below) into the generated protocol text. NEVER copy the plain instructional sentences themselves into the output — follow them as instructions instead (e.g. an "If X, state Y" instruction means output Y when X applies, not the instruction sentence).
The clinical intake payload may include pre-written standard template language for Section 7 topics under "discontinuation_rules" (and may also include related standard text in "template_language_fields" for consistency across safety sections). When the following discontinuation_rules fields are non-empty, you MUST use them as the starting point (copy + lightly tailor for internal consistency) rather than rewriting from scratch:
- Permanent discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- Temporary discontinuation: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
- Rechallenge: {{discontinuation_rules.rechallenge}}
- Participant discontinuation/withdrawal: {{discontinuation_rules.participant_discontinuation_withdrawal}}
- Loss to follow-up: {{discontinuation_rules.management_loss_to_followup}}
If any are empty, write standard regulator-aligned language consistent with ICH GCP and the rest of the protocol.

**SECTION 7.1.2 DECISION TREE (MANDATORY WHEN PRESENT)**
Intake decisions: {{discontinuation_rules.temporary_discontinuation_decisions}}
- If temporary_discontinuation_allowed is "no" AND not_described_output_mode is "omit": OMIT subsection 7.1.2 entirely (no heading, no placeholder).
- If temporary_discontinuation_allowed is "no" AND not_described_output_mode is "statement": under 7.1.2 use EXACTLY the temporary discontinuation field text (typically "No protocol-defined temporary discontinuation procedures are specified.").
- If temporary_discontinuation_allowed is "yes": when temporary_discontinuation_trial_intervention is non-empty, copy it EXACTLY under 7.1.2. Do NOT invent interruption durations, restart criteria, monitoring, or dose rules. This text was authored only from protocol source documents.
- Never invent temporary discontinuation criteria from CPT examples when protocol source was required.

**SECTION 7.1.3 DECISION TREE (MANDATORY WHEN PRESENT)**
Intake decisions: {{discontinuation_rules.rechallenge_decisions}}
- When discontinuation_rules.rechallenge is non-empty, copy it EXACTLY under **7.1.3 Rechallenge** as a single narrative paragraph. Do NOT invent restart criteria, sponsor approvals, committee responsibilities, monitoring, laboratory thresholds, or dose modifications.
- Do NOT duplicate detailed liver-specific restart/rechallenge text reserved for **7.1.3.1**; only brief cross-references are allowed when present in the authored text.
- If rechallenge_after_temporary_discontinuation is "no", the field should be the fixed not-permitted statement — copy it EXACTLY.

**PATIENT LIBRARY — LIVER EVENT STOPPING (VERBATIM WHEN PRESENT)**
When the following fields are non-empty, you MUST copy-paste them EXACTLY under the matching subsection. Do NOT paraphrase, summarize, or invent alternate stopping thresholds:
- For **7.1.1.1 Liver event stopping criteria**, use EXACTLY: {{template_language_fields.liver_event_stopping_criteria}}
- Immediately after that narrative, include the flowchart Mermaid verbatim inside a \`\`\`mermaid fenced block (or preserve a \`<pre class="mermaid">\` block if already HTML): {{template_language_fields.liver_event_stopping_flowchart_mermaid}}
- For **7.1.3.1 Trial intervention restart or rechallenge after liver stopping criteria are met**, use EXACTLY: {{template_language_fields.liver_restart_rechallenge}}
These fields are assembled from the TransCelerate Patient Library liver decision tree. If empty, omit 7.1.1.1 / 7.1.3.1 or state not applicable only when liver stopping is clearly out of scope.


**FIELD REFERENCES:**

**7.1 Discontinuation of Trial Intervention for Individual Participants**

**7.1.1 Permanent Discontinuation of Trial Intervention**
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Permanent Discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- Efficacy Assessments: {{study_assessments.efficacy_assessments}}
- Safety Assessments: {{study_assessments.safety_assessments_general}}
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Safety Analysis: {{statistical_considerations.safety_analysis}}
- Analysis Sets: {{statistical_considerations.analysis_sets}}

**7.1.2 Temporary Discontinuation of Trial Intervention**
- Temporary Discontinuation: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
- Concomitant Medications Allowed: {{study_treatments.concomitant_medications_allowed}}
- Study Assessments: {{study_assessments}}

**7.1.3 Rechallenge**
- Rechallenge: {{discontinuation_rules.rechallenge}}
- Temporary Discontinuation: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}

**7.2 Participant Discontinuation or Withdrawal from the Trial**
- Participant Discontinuation/Withdrawal: {{discontinuation_rules.participant_discontinuation_withdrawal}}
- Informed Consent Rescreening: {{regulatory_requirements.informed_consent_rescreening}}
- Study Assessments: {{study_assessments}}
- Analysis of Primary Objective: {{statistical_considerations.primary_objective_analysis}}
- Analysis of Secondary Objectives: {{statistical_considerations.secondary_objective_analysis}}

**7.3 Management of Loss to Follow-Up**
- Management of Loss to Follow-Up: {{discontinuation_rules.management_loss_to_followup}}
- Efficacy Assessments: {{study_assessments.efficacy_assessments}}
- Safety Assessments: {{study_assessments.safety_assessments_general}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**7. Participant Discontinuation of Trial Intervention and Discontinuation or Withdrawal from Trial**

**7.1 Discontinuation of Trial Intervention for Individual Participants**

**7.1.1 Permanent Discontinuation of Trial Intervention**
Using discontinuation_rules fields and study_assessments, generate content that:
- Describes all medical, safety, and participant-driven reasons for permanently discontinuing the trial intervention
- States the follow-up plan for participants after intervention discontinuation, emphasizing they remain in the trial for follow-up assessments
- References the Schedule of Activities for applicable procedures
- Specifies that the detailed reason(s) for discontinuation must be captured in the eCRF

When liver Patient Library text is present, insert subsection **7.1.1.1 Liver event stopping criteria** by copying the liver fields verbatim (narrative + Mermaid flowchart). Do not invent alternate algorithms.

**Example:**
Participants may be permanently discontinued from the trial intervention for any of the following reasons:
- Unacceptable toxicity, such as the occurrence of a Grade 3 or 4 treatment-related adverse event that does not resolve to Grade 1 or baseline with protocol-specified dose modifications
- Confirmed disease progression as defined in Section [X]
- Participant becomes pregnant
- Participant decision to withdraw consent for trial intervention only
- Investigator decision that discontinuation is in the participant's best medical interest

Participants who permanently discontinue trial intervention will continue to be followed for safety and efficacy assessments as per the protocol until the end of their scheduled participation. Refer to the Schedule of Activities for all applicable procedures. The primary reason for discontinuation of trial intervention will be recorded in the electronic Case Report Form (eCRF).

**7.1.2 Temporary Discontinuation of Trial Intervention**
Follow the Section 7.1.2 decision tree rules above. When content is present in temporary_discontinuation_trial_intervention, copy it EXACTLY as a cohesive paragraph under 7.1.2. Do not invent interruption criteria. If omit mode is selected, skip this subsection entirely.

**7.1.3 Rechallenge**
Follow the Section 7.1.3 decision tree rules above. When content is present in discontinuation_rules.rechallenge, copy it EXACTLY under 7.1.3. Do not invent rechallenge procedures. When liver Patient Library restart text is present, insert subsection **7.1.3.1** by copying {{template_language_fields.liver_restart_rechallenge}} EXACTLY.

If the rechallenge field is empty or "Not Provided", state: "Rechallenge procedures are not defined for this trial. Participants who temporarily discontinue the trial intervention will not be rechallenged."

**Example:**
Rechallenge with the trial intervention after a temporary interruption is permitted as defined in this section. A participant may be rechallenged a maximum of two (2) times. Rechallenge may occur only after the adverse event leading to the interruption has resolved to Grade 1 or baseline, and a safety review by the Investigator confirms it is in the participant's best medical interest.

Upon rechallenge, the intervention will be resumed at the next lower dose level. If the interruption was not due to toxicity, resumption at the same dose level may be considered after consultation with the Medical Monitor. Enhanced safety monitoring will be conducted for the first [X] weeks following rechallenge.

**7.2 Participant Discontinuation or Withdrawal from the Trial**
Using participant_discontinuation_withdrawal and informed_consent_rescreening fields:
- Define the conditions for withdrawal from the trial itself (i.e., revocation of consent for all participation)
- Distinguish clearly between discontinuation of intervention and withdrawal from the trial
- Describe the final assessments to be completed upon withdrawal, referencing the SoA
- Note that a participant can decline final assessments
- Link to the statistical section for how data from withdrawn participants will be handled in the estimand framework

**Example:**
A participant may withdraw from the trial at any time at their own request, or may be withdrawn at the discretion of the Investigator for medical or administrative reasons. Withdrawal from the trial is distinct from discontinuation of trial intervention and is defined as the participant revoking consent for further participation, including any further data collection or follow-up.

The Investigator will make every reasonable effort to complete the End of Study assessments as detailed in the Schedule of Activities for any participant who withdraws from the trial, unless the participant explicitly declines these procedures. The handling of data for participants who withdraw consent will follow the strategy outlined in Section 10.

**7.3 Management of Loss to Follow-Up**
Using management_loss_to_followup field:
- Provide a precise definition of "loss to follow-up" (e.g., number of attempts over a specific period)
- Describe the procedures and tools for minimizing loss to follow-up (e.g., contact logs, reminder systems)
- Emphasize the importance of attempting to maintain follow-up even for participants who have discontinued the trial intervention

If the field is empty or "Not Provided", provide standard language: "A participant will be considered lost to follow-up if the site is unable to make contact after at least three documented attempts over a 30-day period using multiple approved methods. Site staff will maintain regular contact with participants and verify contact information at each visit to minimize loss to follow-up."

**Example:**
A participant will be considered lost to follow-up if the site is unable to make contact after at least three documented attempts over a 30-day period using multiple approved methods (e.g., phone, email, registered mail).

To minimize loss to follow-up, site staff will maintain regular contact with participants and verify contact information at each visit. All contact attempts must be documented in the participant's site records. Retention strategies include reminder calls for upcoming visits, flexible scheduling, and use of remote assessments where appropriate and feasible. Continued follow-up for survival and other key endpoints is critical for all participants, including those who have discontinued trial intervention.

**CONSTRAINTS**
* **Tone and Style:** Maintain a formal, precise, and unambiguous tone consistent with a clinical trial protocol. Adhere strictly to ICH M11 conventions.
* **Completeness:** Ensure every subsection from 7.1 to 7.3 is generated.
* **Cross-Referencing:** Ensure all internal cross-references (e.g., to Section 7.1.3, Schedule of Activities, Section 10) are stated clearly.
* **Missing Data:** If a required data field is missing or empty, provide standard protocol language appropriate for that subsection.
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
  },
  {
    title: '8. TRIAL ASSESSMENTS AND PROCEDURES',
    context: '',
    output: '',
    prompt: `**ROLE**
You are a world-class AI medical writer with deep expertise in oncology clinical trial protocol development. Your specialization is authoring clear, compliant, and precise protocol text that aligns strictly with ICH M11 style and conventions.

**TASK**
You will generate the complete text for "Section 8 (Trial Assessments and Procedures)" of a clinical trial protocol. This task involves transforming structured, fragmented protocol data into a coherent, narrative text.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Endpoints and Estimands):** {{endpoints_and_estimands}}
* **Section 6 & 7 (Intervention and Safety Plans):** {{intervention_and_safety_plans}}
* **Schedule of Activities:** {{schedule_of_activities}}

**FIELD REFERENCES:**

**8.1 Trial Assessments and Procedures Considerations**
- Study Assessments: {{study_assessments}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Study Parts: {{study_overview.study_parts}}

**8.2 Screening/Baseline Assessments and Procedures**
- Screening/Baseline Assessments: {{study_assessments.screening_baseline_assessments}}
- Eligibility Criteria: {{target_population.eligibility_criteria}}
- Conditions Related to Primary Disease: {{target_population.conditions_related_to_primary_disease}}
- Tissue Sample/Procedure Compliance: {{target_population.tissue_sample_procedure_compliance}}
- Patient Performance Status: {{target_population.patient_performance_status}}
- Organ Function/Lab Parameters: {{target_population.organ_function_lab_parameters}}
- Concomitant Meds/Washout: {{target_population.concomitant_meds_washout}}
- Comorbidities & Infections: {{target_population.comorbidities_infections}}
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}
- Study Parts: {{study_overview.study_parts}}

**8.3 Efficacy Assessments and Procedures**
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Exploratory Objectives Endpoints: {{endpoints_objectives.exploratory_objectives_endpoints}}
- Efficacy Assessments: {{study_assessments.efficacy_assessments}}
- Study Parts (for follow-up duration): {{study_overview.study_parts}}

**8.4 Safety Assessments and Procedures**
- Physical Examination: {{study_assessments.physical_examination}}
- Vital Signs: {{study_assessments.vital_signs}}
- Electrocardiograms: {{study_assessments.electrocardiograms}}
- Clinical Laboratory Assessment: {{study_assessments.clinical_laboratory_assessment}}
- Pregnancy Testing: {{study_assessments.pregnancy_testing}}
- Suicidal Ideation Risk Monitoring: {{study_assessments.suicidal_ideation_risk_monitoring}}
- Safety Assessments General: {{study_assessments.safety_assessments_general}}
- Treatment Table: {{study_treatments.treatment_table}}
- Safety Analysis: {{statistical_considerations.safety_analysis}}
- Comorbidities & Infections: {{target_population.comorbidities_infections}}
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}

**8.5 Pharmacokinetics (PK)**
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Pharmacokinetics: {{study_assessments.pharmacokinetics}}
- Other Analyses: {{statistical_considerations.other_analyses}}

**8.6 Biomarkers**
- Exploratory Objectives Endpoints: {{endpoints_objectives.exploratory_objectives_endpoints}}
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Biomarkers: {{study_assessments.biomarkers}}
- Genetics/Pharmacogenomics: {{study_assessments.genetics_pharmacogenomics}}
- Pharmacodynamic Biomarkers: {{study_assessments.pharmacodynamic_biomarkers}}
- Other Biomarkers: {{study_assessments.other_biomarkers}}

**8.7 Immunogenicity Assessments**
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Biomarkers: {{study_assessments.biomarkers}}

**8.8 Medical Resource Utilization and Health Economics**
- Exploratory Objectives Endpoints: {{endpoints_objectives.exploratory_objectives_endpoints}}
- Study Assessments Comments: {{study_assessments.comments}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**8. Trial Assessments and Procedures**

**8.1 Trial Assessments and Procedures Considerations**
Using study_assessments and endpoints_objectives fields, summarize the general principles for conducting all trial assessments:
- State that all assessments must be performed in accordance with the Schedule of Activities
- Explain that safety and efficacy assessments will continue even after treatment discontinuation, as required by the estimand strategy
- Incorporate any specific considerations such as provisions for regional differences, remote assessments, or management of disruptions

**Example:**
All trial assessments and procedures will be conducted in accordance with the Schedule of Activities and must follow Good Clinical Practice guidelines. Safety and efficacy assessments will continue for all participants until the end of their scheduled trial participation, including those who discontinue trial intervention, to support the estimand strategy defined in Section 3. Remote assessments may be utilized when in-person visits are not feasible, with prior approval from the sponsor.


**8.2 Screening/Baseline Assessments and Procedures**
Using screening_baseline_assessments and eligibility_criteria fields, describe the assessments performed:
- During the Screening period to confirm eligibility
- At Baseline (Day 1, prior to first dose) to establish pre-treatment conditions
- Cover key assessments: demographics, medical history, disease confirmation, measurement of baseline tumor burden for stratification, and required laboratory/imaging results for enrollment

**Example:**
Screening assessments will be performed to determine participant eligibility as outlined in Section 5. These include: informed consent, demographics, medical history, physical examination, vital signs, ECOG performance status, 12-lead ECG, clinical laboratory assessments (hematology, chemistry, urinalysis), pregnancy testing (for WOCBP), tumor assessment by CT scan, and collection of archival or fresh tumor tissue for biomarker analysis.

Baseline assessments will be completed on Day 1 prior to the first dose of trial intervention and include: vital signs, physical examination, clinical laboratory assessments, and patient-reported outcomes. These establish the baseline disease and safety parameters against which post-treatment changes will be evaluated.


**8.3 Efficacy Assessments and Procedures**
Using endpoints_objectives and efficacy_assessments fields, describe:
- The primary method for tumor assessment (e.g., imaging type like CT or MRI) and its frequency
- Specify the response criteria to be used (e.g., RECIST v1.1, irRECIST)
- If Patient-Reported Outcomes (PROs) or other Clinical Outcome Assessments (COAs) are used, describe the instrument(s), their administration schedule, and reference their validation
- Detail the procedures for survival follow-up

**Example:**
Tumor response and disease progression will be evaluated by the investigator according to the Response Evaluation Criteria in Solid Tumors (RECIST) version 1.1. Tumor assessments must be performed by computed tomography (CT) of the chest, abdomen, and pelvis. Scans will be performed at Screening and every 8 weeks (±7 days) thereafter until confirmed disease progression, death, or the start of new anti-cancer therapy, whichever occurs first.

Overall survival will be tracked for all participants via follow-up visits or telephone contact every 12 weeks after treatment discontinuation until death, withdrawal of consent, or study closure.


**8.4 Safety Assessments and Procedures**
Using study_assessments fields, describe the safety monitoring procedures broken into subsections:

**8.4.1 Physical Examination**
Using physical_examination field, describe:
- When full versus targeted physical exams are performed
- How findings are documented and graded

**Example:**
A complete physical examination will be performed at Screening and at the End of Study visit. Targeted physical examinations, focusing on systems relevant to known toxicities, will be performed at all other visits as specified in the Schedule of Activities. All findings will be documented in the eCRF, and any clinically significant abnormalities will be recorded as adverse events.

**8.4.2 Vital Signs**
Using vital_signs field, list the parameters to be collected and their collection frequency, including any specific pre- or post-dose timings.

**Example:**
Vital signs assessments will include blood pressure, heart rate, respiratory rate, and body temperature. Vital signs will be collected at all visits as specified in the Schedule of Activities. Pre-dose vital signs will be collected prior to investigational product administration on dosing days.

**8.4.3 Electrocardiograms (ECGs)**
Using electrocardiograms field, specify the timing of 12-lead ECG collection, and details regarding central or local reading and archiving procedures.

**Example:**
Twelve-lead ECGs will be obtained at Screening, Baseline, and at specified timepoints throughout the trial as detailed in the Schedule of Activities. ECGs will be read locally by a qualified physician and findings documented in the eCRF. Tracings will be retained in the participant's source documentation.

**8.4.4 Clinical Laboratory Assessments**
Using clinical_laboratory_assessment field, describe:
- The types of laboratory tests (hematology, chemistry, urinalysis)
- Whether analyzed at a central or local lab
- Reference the appendix for a full list of parameters
- Mention procedures for handling out-of-range values if specified

**Example:**
Clinical laboratory assessments will include hematology, serum chemistry, and urinalysis as detailed in Appendix 1. All samples will be analyzed at a central laboratory unless otherwise specified. Results will be reviewed by the Investigator, and any values outside the reference range will be flagged and assessed for clinical significance. Abnormal results that are clinically significant will be followed until resolution or stabilization.

**8.4.5 Pregnancy Testing**
Using pregnancy_testing field, state who requires testing and the frequency.

**Example:**
Serum or urine pregnancy testing will be performed for all women of childbearing potential at Screening and prior to each treatment cycle as specified in the Schedule of Activities. Any positive pregnancy test will result in immediate permanent discontinuation of trial intervention as outlined in Section 7.

**8.4.6 Suicidal Ideation and Behavior Risk Monitoring**
Using suicidal_ideation_risk_monitoring field, if indicated, describe the scale used, assessment frequency, and the justification for its inclusion.

If the field is empty or "Not Provided", state: "Suicidal ideation and behavior risk monitoring is not required for this trial."

**Example:**
Suicidal ideation and behavior will be assessed using the Columbia-Suicide Severity Rating Scale (C-SSRS) at Screening, Baseline, and at each treatment visit as specified in the Schedule of Activities. This assessment is included due to the potential for CNS-related adverse events with the investigational product.


**8.5 Pharmacokinetics (PK)**
Using pharmacokinetics and other_analyses fields, describe:
- The PK sample collection, including the sample matrix (e.g., plasma, serum) and the collection timepoints
- Reference the laboratory manual or appendix for detailed sample processing, storage, and shipping instructions
- List the key PK parameters that will be calculated from the data (e.g., Cmax, AUC₀-t, T½)

**Example:**
Pharmacokinetic samples will be collected to characterize the PK profile of the investigational product. Blood samples (approximately 5 mL) will be collected in EDTA tubes at pre-dose and at 1, 2, 4, 8, and 24 hours post-dose on Day 1 and Day 15 of Cycle 1. Sample processing, storage, and shipping procedures are detailed in the Laboratory Manual (Appendix 2). Plasma concentrations will be analyzed using a validated LC-MS/MS method, and PK parameters including Cmax, Tmax, AUC₀-t, and terminal half-life will be calculated using non-compartmental analysis.


**8.6 Biomarkers**
Using biomarker-related fields, create subsections as needed:

**8.6.1 Genetics, Genomics, Pharmacogenetics, and Pharmacogenomics** (if applicable)
Using genetics_pharmacogenomics field, describe:
- Sample type (e.g., blood, FFPE tissue)
- Collection timepoints
- Whether mandatory or optional
- Assay methods or platforms

**8.6.2 Pharmacodynamic Biomarkers** (if applicable)
Using pharmacodynamic_biomarkers field, describe:
- Target pathway markers
- Sample timepoints and scientific justification
- Tissue types and frequency
- Mandatory/optional status

**8.6.3 Other Biomarkers** (if applicable)
Using other_biomarkers field, describe any additional exploratory biomarkers.

**Example:**
**8.6.1 Pharmacogenomics**
A single blood sample (10 mL) will be collected at Screening for pharmacogenomic analysis. This sample is optional and requires separate consent. DNA will be extracted and analyzed for genetic variants that may influence drug metabolism or treatment response. Sample handling procedures are detailed in the Laboratory Manual.

**8.7 Immunogenicity Assessments**
Using biomarkers and endpoints_objectives fields, if the investigational product is a biologic:
- Describe the procedures for assessing immunogenicity
- Detail the collection schedule for anti-drug antibody (ADA) samples
- Briefly mention the assay methodology and reference the lab manual

If immunogenicity testing is not applicable, state: "Immunogenicity assessments are not applicable for this trial."

**Example:**
Anti-drug antibody (ADA) samples will be collected to assess the potential development of immunogenicity to the investigational product. Blood samples (5 mL) will be collected pre-dose at Baseline, Cycle 3, Cycle 6, and at the End of Treatment visit. Samples will be analyzed using a validated tiered approach including screening, confirmation, titer, and neutralizing antibody assays. Detailed sample handling procedures are provided in the Laboratory Manual.

**8.8 Medical Resource Utilization and Health Economics**
Using exploratory_objectives_endpoints field, if health economics is included in trial objectives:
- Describe the data being collected for health economic analysis
- Specify what is collected (e.g., hospitalizations, concomitant medications, outpatient visits)
- Specify the method of collection (e.g., ePRO, CRF) and the frequency

If health economics is not included, state: "Medical resource utilization and health economics data will not be collected in this trial."

**Example:**
Healthcare resource utilization data will be collected to support health economic analyses. Data will include all hospitalizations, emergency department visits, outpatient clinic visits, and concomitant medications. Information will be captured in the eCRF at each protocol visit and during follow-up contacts.

**CONSTRAINTS**
* **Tone:** Maintain a formal, objective, and scientific tone suitable for regulatory document writing (ICH M11).
* **Data Handling:** If specific information is not available in the input data, state: [Information for this assessment was not provided in the source data]
* **Minimize Duplication:** Do not replicate the SoA table in prose. Summarize purpose and methodology and refer to the SoA for detailed schedule
* **Acronyms:** Define all non-standard acronyms upon first use. Common clinical trial acronyms (ECG, MRI, CT, AE, SAE, PRO, PK, PD) do not require definition
* **Cross-referencing:** Include cross-references to other protocol sections as appropriate (e.g., "as detailed in Section 12.1")
* **No Meta-Commentary:** Do not include any instructions, placeholders, or comments in your final output. Generate only the protocol text.
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
  },
  {
    title: '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
    context: '',
    output: '',
    prompt: `**ROLE**
You are an expert medical writer specializing in the creation of compliant, clear, and consistent clinical trial documentation. Your expertise includes ICH-GCP guidelines, global regulatory requirements (e.g., FDA, EMA, NMPA), and the standard structure of clinical trial protocols.

**TASK**
You will generate the complete text for "Section 9: Adverse Events, Serious Adverse Events, Product Complaints, Pregnancy and Postpartum Information, and Special Safety Situations" of a clinical trial protocol. This involves synthesizing information from various source documents into a cohesive, clear, and regulatory-compliant protocol section.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Intercurrent Events):** {{section_3_intercurrent_events}}
* **Section 6 (Intervention Safety):** {{section_6_intervention_safety}}
* **Section 7 (Discontinuation):** {{section_7_discontinuation}}
* **Section 10 (Data Analysis/Estimands):** {{section_10_data_analysis}}

**TEMPLATE LANGUAGE (AUTHORITATIVE BASE TEXT WHEN PRESENT)**
CRITICAL — these fields come from the CPT (TransCelerate) template library, which mixes two kinds of text in the same block: (a) actual content to copy, marked between \`<Start of common text>\`/\`<End of common text>\` or \`<Start of example text>\`/\`<End of example text>\`, and (b) plain instructional sentences above/between those markers (e.g. "State the importance of...", "Describe procedures for...", "If no committees are involved, state...") that are DIRECTIONS for what to write, not content to output. Copy ONLY the text inside the Start/End markers (tailored per the rules below) into the generated protocol text. NEVER copy the plain instructional sentences themselves into the output — follow them as instructions instead (e.g. an "If X, state Y" instruction means output Y when X applies, not the instruction sentence).
The clinical intake payload may include pre-written standard template language in "template_language_fields". When these fields are non-empty, you MUST use them as the base text for the matching subsections (copy + lightly tailor for internal consistency), instead of rewriting:
- 9.1.1 AE definition: {{template_language_fields.definition_adverse_event}}
- 9.1.2 SAE definition: {{template_language_fields.definition_serious_adverse_event}}
- 9.1.3 Product complaints: {{template_language_fields.definitions_product_complaints}}
- 9.2 Timing & procedures: {{template_language_fields.ae_reporting_timing_procedures}}
- 9.2.1 Timing: {{template_language_fields.ae_reporting_timing}}
- 9.2.2 Collection procedure: {{template_language_fields.ae_reporting_collection_procedure}}
- 9.2.3 Reporting: {{template_language_fields.ae_reporting_reporting}}
- 9.2.4 AESI: {{template_language_fields.ae_reporting_special_interest}}
- 9.2.5 Disease-related outcomes not qualifying as AEs/SAEs: {{template_language_fields.ae_reporting_disease_related_events}}
- 9.3 Pregnancy & postpartum: {{template_language_fields.pregnancy_postpartum_information}}
- 9.3.1 Participants who become pregnant: {{template_language_fields.participants_pregnant_during_trial}}
- 9.4 Special safety situations: {{template_language_fields.special_safety_situations}}
If a template field is empty, write standard regulator-aligned language and tailor using the structured inputs below.


**FIELD REFERENCES:**

**9.1 Definitions**
- Study Type: {{study_overview.study_type_dropdown}}
- Preferred Short Title: {{study_identification.preferred_short_title}}
- Conditions Related to Primary Disease: {{target_population.conditions_related_to_primary_disease}}
- Study Parts: {{study_overview.study_parts}}
- Permanent Discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- FDA Pregnancy Risk Categories: {{study_treatments.fda_pregnancy_risk_categories}}
- Treatment Table: {{study_treatments.treatment_table}}

**9.2 Timing and Procedures for Collection and Reporting**
- Study Parts (for collection period): {{study_overview.study_parts}}
- Safety Assessments General: {{study_assessments.safety_assessments_general}}
- Safety Analysis: {{statistical_considerations.safety_analysis}}
- Interim Analyses: {{statistical_considerations.interim_analyses}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}

**9.3 Pregnancy and Postpartum Information**
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}
- FDA Pregnancy Risk Categories: {{study_treatments.fda_pregnancy_risk_categories}}
- Treatment Table: {{study_treatments.treatment_table}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Permanent Discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}

**9.4 Special Safety Situations**
- Temporary Discontinuation: {{discontinuation_rules.temporary_discontinuation_trial_intervention}}
- Permanent Discontinuation: {{discontinuation_rules.permanent_discontinuation_trial_intervention}}
- Treatment Table: {{study_treatments.treatment_table}}
- Concomitant Medications Prohibited: {{study_treatments.concomitant_medications_prohibited}}
- Risks of Investigational: {{background_information.risks_investigational}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**9. Adverse Events, Serious Adverse Events, Product Complaints, Pregnancy and Postpartum Information, and Special Safety Situations**

**9.1 Definitions**

**9.1.1 Definitions of Adverse Events**
Using study_type, conditions_related_to_primary_disease, and treatment_table fields, generate content that:
- States the standard ICH-GCP E6(R2) definition of an Adverse Event
- Incorporates any trial-specific clarifications
- Provides illustrative examples of what qualifies and does not qualify as an AE for this specific trial
- Clearly lists any disease-related events that are not to be reported as AEs
- Integrates any regional reporting nuances

**Example:**
An Adverse Event (AE) is any untoward medical occurrence in a participant administered a pharmaceutical product and which does not necessarily have a causal relationship with this treatment (ICH E6(R2)).

For this trial, disease progression as defined by RECIST v1.1 criteria will not be reported as an AE unless the Investigator deems the nature, severity, or frequency to be inconsistent with the expected clinical course. Expected signs and symptoms of the underlying disease that do not worsen beyond baseline will also not be reported as AEs unless they become clinically significant.


**9.1.2 Definitions of Serious Adverse Events**
State the standard ICH E2A definition of a Serious Adverse Event, including the list of criteria.

**Example:**
A Serious Adverse Event (SAE) is any adverse event that:
- Results in death
- Is life-threatening
- Requires inpatient hospitalization or prolongation of existing hospitalization
- Results in persistent or significant disability/incapacity
- Is a congenital anomaly/birth defect
- Is an important medical event that may jeopardize the participant or require intervention to prevent one of the outcomes listed above

Prolonged hospitalization is defined as an extension of an existing hospital stay by more than 24 hours.

**9.1.3 Definitions of Product Complaints**
Provide a comprehensive definition of a product complaint, covering issues with drug formulation, labeling, packaging, and stability.

**Example:**
A product complaint is any written, electronic, or oral communication that alleges deficiencies related to the identity, quality, durability, reliability, safety, effectiveness, or performance of the investigational product, including issues with packaging, labeling, storage, or contamination.

**9.1.3.1 Definitions of Medical Device Product Complaints** (if applicable)
If a medical device is part of the trial intervention, define device malfunctions, use errors, and failures.

If no device is specified, omit this subsection or state: "Not applicable - no medical device component in this trial."


**9.2 Timing and Procedures for Collection and Reporting**
Generate a summary describing the collection and reporting requirements. Consider creating a table with these columns: Event Type (AE, SAE, AESI, Pregnancy, Product Complaint), Collection Start, Collection End, Timeline to Report to Sponsor, and Method of Reporting.

Populate with specific details from the study_parts and safety_assessments fields, cross-referencing the Schedule of Activities.


**9.2.1 Timing**
Using study_parts field, specify:
- Start of AE/SAE collection period (e.g., from signing of informed consent)
- End of collection period (e.g., end of safety follow-up period)
- Cross-reference to Schedule of Activities

**Example:**
Adverse event collection will begin from the time the participant signs informed consent and will continue until 30 days after the last dose of trial intervention, or until resolution of all ongoing AEs, whichever is later.

**9.2.2 Collection Procedures**
Using safety_assessments_general and safety_analysis fields, describe:

**Identification:** Methods for identifying events (e.g., clinical observation, laboratory abnormalities, participant self-report)

**Severity:** State the grading scale to be used (e.g., CTCAE v5.0) and describe the grades (Mild, Moderate, Severe, Life-threatening, Death)

**Causality:** Describe the method and categories for assessing causality with the investigational product (e.g., Related, Possibly Related, Not Related)

**Recording:** Specify that all events must be recorded in source documents and entered into the EDC

**Follow-up:** Detail the requirement to follow all AEs and SAEs until resolution, stabilization, or the event is otherwise explained

**Example:**
All adverse events will be graded for severity using the NCI Common Terminology Criteria for Adverse Events (CTCAE) version 5.0. The Investigator will assess the relationship of each AE to the trial intervention using the categories: Related, Possibly Related, Unlikely Related, or Not Related. All AEs and SAEs must be documented in source documents and entered into the electronic data capture system within 24 hours of the Investigator becoming aware of the event. AEs will be followed until resolution or return to baseline, or until the event is deemed stable or chronic.

**9.2.3 Reporting**
Specify the primary reporting method and backup method, and state the reporting timelines for different events.

**Example:**
All SAEs must be reported to the sponsor within 24 hours of the Investigator becoming aware of the event. The primary method of reporting is through the electronic data capture (EDC) system. In cases where the EDC is unavailable, SAEs should be reported via email to the Medical Monitor. Non-serious AEs will be reported via the EDC according to the normal eCRF completion schedule.


**9.2.3.1 Regulatory Reporting Requirements**
Describe:
- Investigator's role: immediate reporting to sponsor and IRB
- Sponsor's role: timelines for submitting reports to regulatory authorities
- Reference applicable global standards (e.g., CIOMS, FDA Form 3500A, EudraVigilance)

**Example:**
The Investigator must report all SAEs to the sponsor and the local Institutional Review Board/Independent Ethics Committee within the timeframes specified. The sponsor will report suspected unexpected serious adverse reactions (SUSARs) to relevant regulatory authorities according to local regulatory requirements, including FDA regulations (21 CFR Part 312) and EU Clinical Trials Regulation guidelines.

**9.2.4 Adverse Events of Special Interest**
Using treatment_table and risks_investigational fields, list and define any AESIs based on:
- Known risks from the Investigator's Brochure
- Risks identified in previous studies
- Required monitoring frequency and methods
- Whether adjudication is planned

If no specific AESIs are defined, state: "No Adverse Events of Special Interest have been defined for this trial."

**Example:**
The following Adverse Events of Special Interest (AESIs) have been identified based on the known safety profile of the investigational product:
- Hepatotoxicity (ALT/AST >3× ULN)
- QTc prolongation (QTcF >500 ms or increase from baseline >60 ms)
- Severe infusion reactions

All AESIs will be reported to the sponsor within 48 hours and will be reviewed by an independent adjudication committee.

**9.2.5 Disease-Related Events or Outcomes Not Qualifying as AEs or SAEs**
Using conditions_related_to_primary_disease field, list events expected in the natural course of disease that will not be reported as AEs.

**Example:**
The following disease-related events are expected in the natural course of the underlying disease and will not be reported as AEs unless the Investigator determines the nature, severity, or frequency to be unusual:
- Disease progression as defined by RECIST v1.1
- Expected symptoms of advanced cancer (e.g., fatigue, pain, weight loss) at Grade 1 or stable from baseline


**9.3 Pregnancy and Postpartum Information**

**9.3.1 Participants Who Become Pregnant During the Trial**
Using reproductive_status_contraception, fda_pregnancy_risk_categories, and discontinuation fields, describe:
- Procedures for confirming pregnancy and reporting it to the sponsor
- Follow-up requirements for the participant and pregnancy outcome
- Policy on treatment discontinuation (reference Section 7)
- Monitoring duration (e.g., through delivery and postpartum period)
- Data to be collected: pregnancy outcome, infant health status, breastfeeding exposure, pregnant participant/partner or foetus, neonate or infant

**Example:**
Any participant who becomes pregnant during the trial must immediately discontinue trial intervention and notify the Investigator. The pregnancy must be reported to the sponsor within 24 hours. The participant will be followed for pregnancy outcome, including any complications and neonatal status, through delivery and at least 6 weeks postpartum. The pregnancy outcome must be documented in the eCRF regardless of whether the participant continues in the trial for follow-up assessments.

**9.3.2 Participants Whose Partners Become Pregnant During the Trial**
Describe the investigator's responsibility to attempt to collect pregnancy outcome information, conditional on obtaining the partner's consent. Specify data to be collected and any changes to the male participant's trial participation.

**Example:**
If a male participant's partner becomes pregnant during or within 90 days after the participant's last dose of trial intervention, the Investigator should attempt to collect pregnancy outcome information with the consent of the pregnant partner. Information to be collected includes estimated delivery date, pregnancy outcome, and neonatal status. The male participant may continue in the trial unless otherwise specified.

**9.4 Special Safety Situations**
Using discontinuation_rules, treatment_table, concomitant_medications_prohibited, and risks_investigational fields, create a list of non-AE events that require reporting to the sponsor. Include at a minimum:
- Overdose (accidental or intentional)
- Medication errors or dispensing errors
- Misuse or off-label use
- Exposure during pregnancy or breastfeeding (including via semen)
- Occupational exposure
- Suspected transmission of an infectious agent
- Suspected product tampering

For each situation, describe the reporting timeline and method, and how they are documented.

**Example:**
The following situations must be reported to the sponsor even if they do not constitute an AE:

**Overdose:** Any dose exceeding the protocol-specified dose must be reported within 24 hours, whether accidental or intentional. The participant will be monitored according to the overdose management procedures in Section 6.5.

**Medication Errors:** Any error in prescribing, dispensing, or administering the investigational product must be documented and reported within 24 hours.

**Exposure During Pregnancy:** Any exposure to the investigational product during pregnancy (including via semen) or breastfeeding must be reported immediately.

**Occupational Exposure:** Any unintentional exposure of non-trial participants (e.g., healthcare workers, family members) must be reported within 24 hours.

**CONSTRAINTS**
* **Adherence to Standards:** Strictly adhere to standard ICH definitions (ICH E6(R2), ICH E2A) unless explicitly overridden by trial-specific requirements
* **Internal Consistency:** Ensure all timelines, definitions, and procedures are consistent throughout the section
* **Missing Data:** If specific details are not available in the input data, state: [Information to be supplied by sponsor]
* **Conditional Generation:** Only generate subsections if relevant context is provided (e.g., device complaints only if device is part of intervention)
* **No Meta-Commentary:** Generate only the protocol text itself, without explanations or comments
* **Cross-Referencing:** Reference other sections appropriately (e.g., Section 3, 6, 7, 10, Schedule of Activities)
* **Tone:** Maintain formal, precise, and unambiguous regulatory tone`
  },
  {
    title: '10. STATISTICAL CONSIDERATIONS',
    context: '',
    output: '',
    useChunkedGeneration: true,
    chunks: {
      chunk1: `**ROLE**
You are an expert medical writer and biostatistician specializing in oncology clinical trial protocol development. Your writing must align with ICH M11 style and conventions, and your statistical descriptions must adhere to the principles of ICH E9 and its addendum, E9(R1).

**TASK**
Your task is to generate the complete "Statistical Considerations" section (Section 10) for a clinical trial protocol. You will use the provided inputs to write comprehensive, professional, and regulatory-compliant text for each subsection.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Objectives and Estimands):** {{section_3_content}}
* **Section 6 (Trial Intervention):** {{section_6_content}}
* **Section 7 (Discontinuation/Withdrawal Handling):** {{section_7_content}}

**FIELD REFERENCES:**

**10.1 General Considerations**
* Analysis Sets: {{statistical_considerations.analysis_sets}}
* Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
* Interim Analyses: {{statistical_considerations.interim_analyses}}

**10.2 Analysis Sets**
* Analysis Sets: {{statistical_considerations.analysis_sets}}
* Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
* Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}
* Safety Analysis: {{statistical_considerations.safety_analysis}}
* Exploratory Analysis: {{statistical_considerations.exploratory_analysis}}

**10.3 Analyses of Demographics and Other Baseline Variables**
* Screening/Baseline Assessments: {{study_assessments.screening_baseline_assessments}}
* Patient Performance Status: {{target_population.patient_performance_status}}
* Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}
* Study Parts (for stratification factors): {{study_overview.study_parts}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**10. Statistical Considerations**

**10.1 General Considerations**
Using statistical_considerations and primary_objective_analysis fields:
* State whether a Statistical Analysis Plan (SAP) will be developed separately
* Summarize the primary analysis framework (e.g., intent-to-treat)
* Specify the timing of the main analyses (e.g., final analysis after database lock)
* State that all planned analyses align with ICH E9 and E9(R1)

**Example:**
A comprehensive Statistical Analysis Plan (SAP) will be developed as a separate document and will be finalized before the database is locked for the final analysis. The SAP will provide expanded, technical details for the statistical methods summarized in this protocol.

The primary analysis framework for this trial will adhere to the intent-to-treat (ITT) principle, where all randomized participants are included in the analysis according to the treatment arm to which they were assigned. The final analysis will be conducted after the last participant has completed the trial, all data have been collected and cleaned, and the clinical trial database has been formally locked.

All planned analyses are designed in accordance with the principles outlined in the International Council for Harmonisation (ICH) guidance E9, "Statistical Principles for Clinical Trials," and its addendum, E9(R1), "Estimands and Sensitivity Analysis in Clinical Trials."


**10.2 Analysis Sets**
Using analysis_sets and analysis fields, define each analysis set:
* Full Analysis Set (FAS), Per-Protocol Set (PPS), Safety Set
* Link each set to appropriate estimands (e.g., FAS for treatment policy estimand)
* Clarify which set will be used for primary, secondary, and safety analyses

**Example:**
The following analysis sets will be used in this trial:

**Full Analysis Set (FAS):** The FAS will include all randomized participants, analyzed according to the treatment arm to which they were assigned, regardless of the treatment actually received. The FAS is the primary analysis set for all efficacy analyses and directly supports the treatment policy estimand defined in Section 3.

**Per-Protocol Set (PPS):** The PPS will include all participants in the FAS who have no major protocol deviations that could impact the evaluation of treatment efficacy. The PPS will be used for sensitivity analyses.

**Safety Analysis Set:** The safety analysis set will include all participants who received at least one dose of trial intervention, analyzed according to the treatment actually received. This set will be used for all safety summaries and analyses.


**10.3 Analyses of Demographics and Other Baseline Variables**
Using screening_baseline_assessments, patient_performance_status, and study_parts fields:
* Describe the summary statistics that will be used (n, mean, SD, median, range for continuous; frequency and percentage for categorical)
* List key demographic and baseline variables to be summarized (age, sex, race, ECOG performance status, biomarker status)
* Identify the timepoint for baseline measurement (at screening or randomization)
* Indicate how stratification variables will be summarized by treatment group

**Example:**
Demographic and baseline characteristics will be summarized for all randomized participants by treatment arm. Continuous variables (e.g., age, weight, baseline tumor burden) will be summarized using descriptive statistics including n, mean, standard deviation, median, minimum, and maximum. Categorical variables (e.g., sex, race, ECOG performance status, disease stage) will be summarized using frequency counts and percentages.

All baseline variables will be measured at Screening unless otherwise specified in the Schedule of Activities. Stratification factors used in the randomization process will be tabulated by treatment arm to verify balance.

**CONSTRAINTS**
* **Strict Adherence to Inputs:** Base all generated content strictly on the information provided in the input data fields
* **Handling Missing Sections:** If information for a specific objective type (e.g., secondary, exploratory) is not provided or is stated as not applicable, write "Not applicable" and omit sub-sections
* **Repetitive Structures:** For sections 10.4 and 10.5, generate a unique and complete set of subsections for each primary or secondary objective
* **Professional Tone:** Use formal, scientific tone suitable for regulatory submission. Avoid conversational language and placeholder text
* **Consistency:** Ensure perfect alignment between estimands, analysis sets, handling of intercurrent events, and primary analysis methods
* **No Meta-Commentary:** Generate only the protocol text itself, without explanations or comments
* **ICH E9/E9(R1) Compliance:** Ensure all statistical descriptions adhere to ICH E9 and E9(R1) principles
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`,

      chunk2: `**ROLE**
You are an expert medical writer and biostatistician specializing in oncology clinical trial protocol development. Your writing must align with ICH M11 style and conventions, and your statistical descriptions must adhere to the principles of ICH E9 and its addendum, E9(R1).

**TASK**
Your task is to generate the complete "Statistical Considerations" section (Section 10) for a clinical trial protocol. You will use the provided inputs to write comprehensive, professional, and regulatory-compliant text for each subsection.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Objectives and Estimands):** {{section_3_content}}
* **Section 6 (Trial Intervention):** {{section_6_content}}
* **Section 7 (Discontinuation/Withdrawal Handling):** {{section_7_content}}

**FIELD REFERENCES:**

**10.4 Analyses Associated with the Primary Objective(s)**
* Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
* Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
* Analysis Sets: {{statistical_considerations.analysis_sets}}
* Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
* Rationale for Estimands: {{background_information.rationale_for_estimands}}

**10.5 Analyses Associated with the Secondary Objective(s)**
* Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
* Key Secondary Objectives Endpoints: {{endpoints_objectives.key_secondary_objectives_endpoints}}
* Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}

**10.6 Analyses Associated with the Exploratory Objective(s)**
* Exploratory Objectives Endpoints: {{endpoints_objectives.exploratory_objectives_endpoints}}
* Exploratory Analysis: {{statistical_considerations.exploratory_analysis}}
* Biomarkers: {{study_assessments.biomarkers}}
* Genetics/Pharmacogenomics: {{study_assessments.genetics_pharmacogenomics}}
* Pharmacodynamic Biomarkers: {{study_assessments.pharmacodynamic_biomarkers}}
* Other Biomarkers: {{study_assessments.other_biomarkers}}

**10.7 Safety Analyses**
* Safety Analysis: {{statistical_considerations.safety_analysis}}
* Physical Examination: {{study_assessments.physical_examination}}
* Vital Signs: {{study_assessments.vital_signs}}
* Electrocardiograms: {{study_assessments.electrocardiograms}}
* Clinical Laboratory Assessment: {{study_assessments.clinical_laboratory_assessment}}
* Safety Assessments General: {{study_assessments.safety_assessments_general}}

**10.8 Other Analyses**
* Study Parts: {{study_overview.study_parts}}
* Patient Performance Status: {{target_population.patient_performance_status}}
* Conditions Related to Primary Disease: {{target_population.conditions_related_to_primary_disease}}
* Other Analyses: {{statistical_considerations.other_analyses}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**10.4 Analyses Associated with the Primary Objective(s)**
Using primary_objective_endpoints and primary_objective_analysis fields, repeat the following structure for each primary objective:

**10.4.1 Primary Objective #**

**10.4.1.1 Statistical Analysis Method**
* State the null (H0) and alternative (H1) hypotheses
* Specify the statistical model (e.g., stratified Cox proportional hazards model, ANCOVA, logistic regression)
* List any covariates, stratification factors, or random effects included in the model
* Clarify the statistical test parameters, including one- or two-sided testing and the alpha level for significance

**Example:**
The null hypothesis (H0) is that there is no difference in progression-free survival (PFS) between the investigational arm and the control arm. The alternative hypothesis (H1) is that the investigational arm has superior PFS compared to the control arm.

The primary analysis will use a stratified log-rank test to compare PFS between treatment arms, stratified by ECOG performance status (0 vs 1) and prior lines of therapy (1 vs ≥2). A stratified Cox proportional hazards model will be used to estimate the hazard ratio and its 95% confidence interval. The test will be two-sided with an overall Type I error rate (alpha) of 0.05.

**10.4.1.2 Handling of Data in Relation to Primary Estimand(s)**
For each relevant intercurrent event, describe the specific analysis strategy:
* Ensure the described strategy directly corresponds to the estimand framework defined in Section 3

**Example:**
The primary estimand uses a treatment policy strategy for all intercurrent events, including treatment discontinuation due to toxicity, initiation of new anti-cancer therapy, or death. Under this strategy, data collected after the occurrence of an intercurrent event will be included in the analysis. Participants who discontinue trial intervention will continue to be followed for progression and survival as specified in the Schedule of Activities.

**10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s)**
* Describe the primary method for handling missing data
* Justify the choice of method and state the underlying assumptions (e.g., Missing at Random [MAR])

**Example:**
The primary analysis method (Cox proportional hazards regression) naturally accommodates censoring for participants without progression or death events. Participants will be censored at the date of their last adequate tumor assessment. Missing data for the primary endpoint are assumed to be Missing at Random (MAR). Sensitivity analyses will explore alternative assumptions regarding missing data.

**10.4.1.4 Sensitivity Analysis**
* Describe planned sensitivity analyses to assess the robustness of the primary analysis results
* Explain how the assumptions or methods of each sensitivity analysis differ from the primary analysis

**Example:**
The following sensitivity analyses will be conducted to assess the robustness of the primary analysis:
1. Analysis using the Per-Protocol Set to evaluate the impact of major protocol deviations
2. Analysis censoring participants at the time of initiation of subsequent anti-cancer therapy (hypothetical strategy)
3. Tipping point analysis to assess the impact of different missing data assumptions

**10.4.1.5 Supplementary Analysis**
* Describe any planned supplementary analyses related to the primary objective
* State explicitly that these analyses are exploratory or descriptive and not for confirmatory testing

**Example:**
Supplementary analyses for the primary objective will include:
* Kaplan-Meier plots of PFS by treatment arm
* Descriptive summaries of time to progression and time to death as separate endpoints
* Landmark analyses at 6 and 12 months
These analyses are descriptive and hypothesis-generating in nature.


**10.5 Analyses Associated with the Secondary Objective(s)**
Using secondary_objectives_endpoints and secondary_objective_analysis fields, repeat the same structure as 10.4 for each secondary objective:
* Statistical methods
* Intercurrent event handling
* Missing data approach
* Sensitivity and supplementary analyses

If no secondary objectives exist, state: "Not applicable."

**Example:**
**10.5.1 Secondary Objective 1: Overall Survival**

**10.5.1.1 Statistical Analysis Method**
Overall survival will be analyzed using a stratified log-rank test and stratified Cox proportional hazards regression model, using the same stratification factors as the primary analysis. The test will be two-sided with alpha = 0.05.

**10.6 Analyses Associated with the Exploratory Objective(s)**
Using exploratory_objectives_endpoints and exploratory_analysis fields:
* Describe planned exploratory analyses (e.g., correlation of biomarkers with outcomes, analysis of patient-reported outcomes)
* State that these analyses are hypothesis-generating
* Mention the types of methods that may be used (e.g., graphical displays, summary statistics, correlation coefficients)

**Example:**
Exploratory analyses will include correlation of pharmacodynamic biomarkers with clinical outcomes, descriptive summaries of patient-reported outcome measures, and exploratory subgroup analyses by biomarker status. These analyses are hypothesis-generating in nature and will use descriptive statistics, graphical methods, and exploratory correlation analyses (e.g., Spearman correlation coefficients). Results will be interpreted cautiously given the exploratory nature and lack of multiplicity control.

**10.7 Safety Analyses**
Using safety_analysis and study_assessments fields:
* Describe how Adverse Events (AEs), Serious Adverse Events (SAEs), and AESIs will be coded (e.g., MedDRA) and summarized
* Define the treatment-emergent period for AEs
* Describe how laboratory abnormalities and vital signs will be summarized
* State whether formal between-group comparisons of safety data are planned

**Example:**
Safety analyses will be conducted on the Safety Analysis Set. All adverse events will be coded using the Medical Dictionary for Regulatory Activities (MedDRA) version [X.X] and summarized by system organ class and preferred term. Treatment-emergent adverse events (TEAEs) are defined as events that start on or after the first dose of trial intervention and up to 30 days after the last dose.

AEs will be summarized by treatment arm using frequency counts and percentages, overall and by maximum severity grade. SAEs and AEs leading to discontinuation will be summarized separately. Laboratory abnormalities will be summarized using shift tables showing changes from baseline to worst post-baseline value. Vital signs and ECG parameters will be summarized using descriptive statistics and shift tables.


**10.8 Other Analyses**
Using study_parts, patient_performance_status, conditions_related_to_primary_disease, and other_analyses fields:
* Describe any pre-specified subgroup analyses (e.g., by age group, geographic region, biomarker status, stratification factors)
* State whether interaction tests will be performed to assess for heterogeneity of treatment effect across subgroups
* Discuss the approach to interpreting subgroup analyses, noting their exploratory nature and the potential impact of multiplicity

**Example:**
Pre-specified subgroup analyses will be conducted for the primary endpoint to explore potential heterogeneity of treatment effect across the following subgroups:
* Age (<65 years vs ≥65 years)
* ECOG performance status (0 vs 1)
* Prior lines of therapy (1 vs ≥2)
* Geographic region

Forest plots will be presented showing the treatment effect estimate and 95% confidence interval for each subgroup. Interaction tests will be performed to formally assess heterogeneity. Given the exploratory nature of these analyses and the lack of multiplicity adjustment, results should be interpreted with caution and considered hypothesis-generating.

**CONSTRAINTS**
* **Strict Adherence to Inputs:** Base all generated content strictly on the information provided in the input data fields
* **Handling Missing Sections:** If information for a specific objective type (e.g., secondary, exploratory) is not provided or is stated as not applicable, write "Not applicable" and omit sub-sections
* **Repetitive Structures:** For sections 10.4 and 10.5, generate a unique and complete set of subsections for each primary or secondary objective
* **Professional Tone:** Use formal, scientific tone suitable for regulatory submission. Avoid conversational language and placeholder text
* **Consistency:** Ensure perfect alignment between estimands, analysis sets, handling of intercurrent events, and primary analysis methods
* **No Meta-Commentary:** Generate only the protocol text itself, without explanations or comments
* **ICH E9/E9(R1) Compliance:** Ensure all statistical descriptions adhere to ICH E9 and E9(R1) principles
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`,

      chunk3: `**ROLE**
You are an expert medical writer and biostatistician specializing in oncology clinical trial protocol development. Your writing must align with ICH M11 style and conventions, and your statistical descriptions must adhere to the principles of ICH E9 and its addendum, E9(R1).

**TASK**
Your task is to generate the complete "Statistical Considerations" section (Section 10) for a clinical trial protocol. You will use the provided inputs to write comprehensive, professional, and regulatory-compliant text for each subsection.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Objectives and Estimands):** {{section_3_content}}
* **Section 6 (Trial Intervention):** {{section_6_content}}
* **Section 7 (Discontinuation/Withdrawal Handling):** {{section_7_content}}

**FIELD REFERENCES:**

**10.9 Interim Analyses**
* Interim Analyses: {{statistical_considerations.interim_analyses}}
* Committees: {{regulatory_requirements.committees}}

**10.10 Multiplicity Adjustments**
* Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
* Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}
* Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
* Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}

**10.11 Sample Size Determination**
* Sample Size Determination: {{statistical_considerations.sample_size_determination}}
* Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
* Efficacy Assessments: {{study_assessments.efficacy_assessments}}
* Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
* Disease Epidemiology: {{background_information.disease_epidemiology}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**10.9 Interim Analyses**
Using interim_analyses and committees fields:
* State whether any formal interim analyses for efficacy or futility are planned
* If planned, specify the trigger (e.g., after a certain number of events or participants)
* Describe the statistical stopping boundaries (e.g., O'Brien-Fleming alpha-spending function)
* Identify the body responsible for reviewing unblinded results (e.g., independent Data Monitoring Committee)
* Describe measures to maintain trial integrity and control the overall Type I error rate

**Example:**
One interim analysis for futility will be conducted when approximately 50% of the planned PFS events have occurred. The analysis will be reviewed by an independent Data Monitoring Committee (DMC), which will have access to unblinded data. The trial may be stopped early for futility if the conditional power to demonstrate a statistically significant treatment effect falls below 20%.

To protect the overall Type I error rate, a group sequential design with an O'Brien-Fleming spending function will be used. No efficacy stopping boundary is planned at the interim analysis. Trial personnel, investigators, and participants will remain blinded to the interim results to maintain trial integrity.

**10.10 Multiplicity Adjustments**
Using primary_objective_analysis, secondary_objective_analysis, and endpoints fields:
* Describe the strategy for controlling the overall Type I error rate if there are multiple primary or secondary endpoints, or multiple comparisons
* Specify the method to be used (e.g., hierarchical testing procedure, Hochberg procedure, graphical approach)
* If applicable, provide a clear testing schema

**Example:**
This trial has a single primary endpoint; therefore, no multiplicity adjustment is required for the primary analysis. Secondary endpoints will be tested using a hierarchical testing procedure in the following order: (1) Overall Survival, (2) Objective Response Rate, (3) Duration of Response. Each endpoint will be tested at the 0.05 significance level only if the preceding endpoint in the hierarchy achieved statistical significance. This procedure maintains strong control of the family-wise error rate at 0.05.

**10.11 Sample Size Determination**
Using sample_size_determination, discontinuation_trial_intervention_individual, efficacy_assessments, primary_objective_endpoints, and disease_epidemiology fields:
* Restate the key assumptions used for the sample size calculation, justifying each one with references to prior studies or clinical rationale
* Include: expected treatment effect, desired statistical power, overall Type I error rate (alpha), assumed variance/event rates/control group performance, anticipated dropout or discontinuation rate
* Explain how intercurrent events were factored into the sample size calculation
* If simulations were used, state that a detailed report is available separately or in an appendix

**Example:**
The sample size for this trial was determined based on the primary objective of demonstrating superior progression-free survival (PFS) in the investigational arm compared to the control arm.

**Assumptions:**
* Median PFS in the control arm: 6 months (based on historical data from [Reference])
* Expected hazard ratio: 0.67 (representing a 50% improvement in median PFS to 9 months in the investigational arm)
* Statistical power: 90%
* Type I error rate (alpha): 0.05 (two-sided)
* Accrual period: 24 months
* Minimum follow-up: 12 months
* Dropout/loss to follow-up rate: 10%

Based on these assumptions, approximately 240 PFS events are required. Assuming an exponential distribution and accounting for dropout, a total of 300 participants will be randomized (150 per arm). This sample size provides 90% power to detect the specified treatment effect using a two-sided log-rank test at the 0.05 significance level.

**CONSTRAINTS**
* **Strict Adherence to Inputs:** Base all generated content strictly on the information provided in the input data fields
* **Handling Missing Sections:** If information for a specific objective type (e.g., secondary, exploratory) is not provided or is stated as not applicable, write "Not applicable" and omit sub-sections
* **Repetitive Structures:** For sections 10.4 and 10.5, generate a unique and complete set of subsections for each primary or secondary objective
* **Professional Tone:** Use formal, scientific tone suitable for regulatory submission. Avoid conversational language and placeholder text
* **Consistency:** Ensure perfect alignment between estimands, analysis sets, handling of intercurrent events, and primary analysis methods
* **No Meta-Commentary:** Generate only the protocol text itself, without explanations or comments
* **ICH E9/E9(R1) Compliance:** Ensure all statistical descriptions adhere to ICH E9 and E9(R1) principles
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
    },
    prompt: `**ROLE**
You are an expert medical writer and biostatistician specializing in oncology clinical trial protocol development. Your writing must align with ICH M11 style and conventions, and your statistical descriptions must adhere to the principles of ICH E9 and its addendum, E9(R1).

**TASK**
Your task is to generate the complete "Statistical Considerations" section (Section 10) for a clinical trial protocol. You will use the provided inputs to write comprehensive, professional, and regulatory-compliant text for each subsection.

**INPUT DATA**
You will be provided with structured data from the protocol planning form and contextual summaries from other protocol sections:

**CONTEXTUAL SECTIONS:** Summaries of related protocol sections to ensure consistency.
* **Section 3 (Objectives and Estimands):** {{section_3_content}}
* **Section 6 (Trial Intervention):** {{section_6_content}}
* **Section 7 (Discontinuation/Withdrawal Handling):** {{section_7_content}}

**FIELD REFERENCES:**

**10.1 General Considerations**
- Analysis Sets: {{statistical_considerations.analysis_sets}}
- Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
- Interim Analyses: {{statistical_considerations.interim_analyses}}

**10.2 Analysis Sets**
- Analysis Sets: {{statistical_considerations.analysis_sets}}
- Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
- Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}
- Safety Analysis: {{statistical_considerations.safety_analysis}}
- Exploratory Analysis: {{statistical_considerations.exploratory_analysis}}

**10.3 Analyses of Demographics and Other Baseline Variables**
- Screening/Baseline Assessments: {{study_assessments.screening_baseline_assessments}}
- Patient Performance Status: {{target_population.patient_performance_status}}
- Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}
- Study Parts (for stratification factors): {{study_overview.study_parts}}

**10.4 Analyses Associated with the Primary Objective(s)**
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
- Analysis Sets: {{statistical_considerations.analysis_sets}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Rationale for Estimands: {{background_information.rationale_for_estimands}}

**10.5 Analyses Associated with the Secondary Objective(s)**
- Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}
- Key Secondary Objectives Endpoints: {{endpoints_objectives.key_secondary_objectives_endpoints}}
- Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}

**10.6 Analyses Associated with the Exploratory Objective(s)**
- Exploratory Objectives Endpoints: {{endpoints_objectives.exploratory_objectives_endpoints}}
- Exploratory Analysis: {{statistical_considerations.exploratory_analysis}}
- Biomarkers: {{study_assessments.biomarkers}}
- Genetics/Pharmacogenomics: {{study_assessments.genetics_pharmacogenomics}}
- Pharmacodynamic Biomarkers: {{study_assessments.pharmacodynamic_biomarkers}}
- Other Biomarkers: {{study_assessments.other_biomarkers}}

**10.7 Safety Analyses**
- Safety Analysis: {{statistical_considerations.safety_analysis}}
- Physical Examination: {{study_assessments.physical_examination}}
- Vital Signs: {{study_assessments.vital_signs}}
- Electrocardiograms: {{study_assessments.electrocardiograms}}
- Clinical Laboratory Assessment: {{study_assessments.clinical_laboratory_assessment}}
- Safety Assessments General: {{study_assessments.safety_assessments_general}}

**10.8 Other Analyses**
- Study Parts: {{study_overview.study_parts}}
- Patient Performance Status: {{target_population.patient_performance_status}}
- Conditions Related to Primary Disease: {{target_population.conditions_related_to_primary_disease}}
- Other Analyses: {{statistical_considerations.other_analyses}}

**10.9 Interim Analyses**
- Interim Analyses: {{statistical_considerations.interim_analyses}}
- Committees: {{regulatory_requirements.committees}}

**10.10 Multiplicity Adjustments**
- Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
- Secondary Objective Analysis: {{statistical_considerations.secondary_objective_analysis}}
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Secondary Objectives Endpoints: {{endpoints_objectives.secondary_objectives_endpoints}}

**10.11 Sample Size Determination**
- Sample Size Determination: {{statistical_considerations.sample_size_determination}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Efficacy Assessments: {{study_assessments.efficacy_assessments}}
- Primary Objective Endpoints: {{endpoints_objectives.primary_objective_endpoints}}
- Disease Epidemiology: {{background_information.disease_epidemiology}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**10. Statistical Considerations**

**10.1 General Considerations**
Using statistical_considerations and primary_objective_analysis fields:
- State whether a Statistical Analysis Plan (SAP) will be developed separately
- Summarize the primary analysis framework (e.g., intent-to-treat)
- Specify the timing of the main analyses (e.g., final analysis after database lock)
- State that all planned analyses align with ICH E9 and E9(R1)

**Example:**
A comprehensive Statistical Analysis Plan (SAP) will be developed as a separate document and will be finalized before the database is locked for the final analysis. The SAP will provide expanded, technical details for the statistical methods summarized in this protocol.

The primary analysis framework for this trial will adhere to the intent-to-treat (ITT) principle, where all randomized participants are included in the analysis according to the treatment arm to which they were assigned. The final analysis will be conducted after the last participant has completed the trial, all data have been collected and cleaned, and the clinical trial database has been formally locked.

All planned analyses are designed in accordance with the principles outlined in the International Council for Harmonisation (ICH) guidance E9, "Statistical Principles for Clinical Trials," and its addendum, E9(R1), "Estimands and Sensitivity Analysis in Clinical Trials."


**10.2 Analysis Sets**
Using analysis_sets and analysis fields, define each analysis set:
- Full Analysis Set (FAS), Per-Protocol Set (PPS), Safety Set
- Link each set to appropriate estimands (e.g., FAS for treatment policy estimand)
- Clarify which set will be used for primary, secondary, and safety analyses

**Example:**
The following analysis sets will be used in this trial:

**Full Analysis Set (FAS):** The FAS will include all randomized participants, analyzed according to the treatment arm to which they were assigned, regardless of the treatment actually received. The FAS is the primary analysis set for all efficacy analyses and directly supports the treatment policy estimand defined in Section 3.

**Per-Protocol Set (PPS):** The PPS will include all participants in the FAS who have no major protocol deviations that could impact the evaluation of treatment efficacy. The PPS will be used for sensitivity analyses.

**Safety Analysis Set:** The safety analysis set will include all participants who received at least one dose of trial intervention, analyzed according to the treatment actually received. This set will be used for all safety summaries and analyses.


**10.3 Analyses of Demographics and Other Baseline Variables**
Using screening_baseline_assessments, patient_performance_status, and study_parts fields:
- Describe the summary statistics that will be used (n, mean, SD, median, range for continuous; frequency and percentage for categorical)
- List key demographic and baseline variables to be summarized (age, sex, race, ECOG performance status, biomarker status)
- Identify the timepoint for baseline measurement (at screening or randomization)
- Indicate how stratification variables will be summarized by treatment group

**Example:**
Demographic and baseline characteristics will be summarized for all randomized participants by treatment arm. Continuous variables (e.g., age, weight, baseline tumor burden) will be summarized using descriptive statistics including n, mean, standard deviation, median, minimum, and maximum. Categorical variables (e.g., sex, race, ECOG performance status, disease stage) will be summarized using frequency counts and percentages.

All baseline variables will be measured at Screening unless otherwise specified in the Schedule of Activities. Stratification factors used in the randomization process will be tabulated by treatment arm to verify balance.


**10.4 Analyses Associated with the Primary Objective(s)**
Using primary_objective_endpoints and primary_objective_analysis fields, repeat the following structure for each primary objective:

**10.4.1 Primary Objective #**

**10.4.1.1 Statistical Analysis Method**
- State the null (H0) and alternative (H1) hypotheses
- Specify the statistical model (e.g., stratified Cox proportional hazards model, ANCOVA, logistic regression)
- List any covariates, stratification factors, or random effects included in the model
- Clarify the statistical test parameters, including one- or two-sided testing and the alpha level for significance

**Example:**
The null hypothesis (H0) is that there is no difference in progression-free survival (PFS) between the investigational arm and the control arm. The alternative hypothesis (H1) is that the investigational arm has superior PFS compared to the control arm.

The primary analysis will use a stratified log-rank test to compare PFS between treatment arms, stratified by ECOG performance status (0 vs 1) and prior lines of therapy (1 vs ≥2). A stratified Cox proportional hazards model will be used to estimate the hazard ratio and its 95% confidence interval. The test will be two-sided with an overall Type I error rate (alpha) of 0.05.

**10.4.1.2 Handling of Data in Relation to Primary Estimand(s)**
For each relevant intercurrent event, describe the specific analysis strategy:
- Ensure the described strategy directly corresponds to the estimand framework defined in Section 3

**Example:**
The primary estimand uses a treatment policy strategy for all intercurrent events, including treatment discontinuation due to toxicity, initiation of new anti-cancer therapy, or death. Under this strategy, data collected after the occurrence of an intercurrent event will be included in the analysis. Participants who discontinue trial intervention will continue to be followed for progression and survival as specified in the Schedule of Activities.

**10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s)**
- Describe the primary method for handling missing data
- Justify the choice of method and state the underlying assumptions (e.g., Missing at Random [MAR])

**Example:**
The primary analysis method (Cox proportional hazards regression) naturally accommodates censoring for participants without progression or death events. Participants will be censored at the date of their last adequate tumor assessment. Missing data for the primary endpoint are assumed to be Missing at Random (MAR). Sensitivity analyses will explore alternative assumptions regarding missing data.

**10.4.1.4 Sensitivity Analysis**
- Describe planned sensitivity analyses to assess the robustness of the primary analysis results
- Explain how the assumptions or methods of each sensitivity analysis differ from the primary analysis

**Example:**
The following sensitivity analyses will be conducted to assess the robustness of the primary analysis:
1. Analysis using the Per-Protocol Set to evaluate the impact of major protocol deviations
2. Analysis censoring participants at the time of initiation of subsequent anti-cancer therapy (hypothetical strategy)
3. Tipping point analysis to assess the impact of different missing data assumptions

**10.4.1.5 Supplementary Analysis**
- Describe any planned supplementary analyses related to the primary objective
- State explicitly that these analyses are exploratory or descriptive and not for confirmatory testing

**Example:**
Supplementary analyses for the primary objective will include:
- Kaplan-Meier plots of PFS by treatment arm
- Descriptive summaries of time to progression and time to death as separate endpoints
- Landmark analyses at 6 and 12 months
These analyses are descriptive and hypothesis-generating in nature.


**10.5 Analyses Associated with the Secondary Objective(s)**
Using secondary_objectives_endpoints and secondary_objective_analysis fields, repeat the same structure as 10.4 for each secondary objective:
- Statistical methods
- Intercurrent event handling
- Missing data approach
- Sensitivity and supplementary analyses

If no secondary objectives exist, state: "Not applicable."

**Example:**
**10.5.1 Secondary Objective 1: Overall Survival**

**10.5.1.1 Statistical Analysis Method**
Overall survival will be analyzed using a stratified log-rank test and stratified Cox proportional hazards regression model, using the same stratification factors as the primary analysis. The test will be two-sided with alpha = 0.05.
**SECTION 10.5 HTML STRUCTURE (MANDATORY — same pattern as 10.4):**
Output exactly ONE parent heading for this block, then nested objective subsections only:
- <h2>10.5 Analyses Associated with the Secondary Objective(s)</h2>
- Repeat the same structure as 10.4 for each secondary objective/endpoint listed in study inputs. If none: state "Not applicable." and omit nested subsections.
- For each secondary objective/endpoint, generate ONLY as <h3> headings nested under 10.5 (never use <h2> for 10.5.1, 10.5.2, etc.):
  - <h3>10.5.X [Objective/Endpoint Title]</h3>
  - <h3>10.5.X.1 Statistical Analysis Method</h3>
  - <h3>10.5.X.2 Handling of Data in Relation to the Secondary Estimand(s)</h3>
  - <h3>10.5.X.3 Handling of Missing Data in Relation to the Secondary Estimand(s)</h3>
  - <h3>10.5.X.4 Sensitivity Analyses</h3>
  - <h3>10.5.X.5 Supplementary Analyses</h3>
- Create one complete 10.5.X block (with all five 10.5.X.1–10.5.X.5 subsections) per distinct secondary objective/endpoint in the inputs. Do not invent additional objectives.
- Do NOT output standalone <h2> sections numbered 10.5.1, 10.5.2, or 10.5.3.

**10.6 Analyses Associated with the Exploratory Objective(s)**
Using exploratory_objectives_endpoints and exploratory_analysis fields:
- Describe planned exploratory analyses (e.g., correlation of biomarkers with outcomes, analysis of patient-reported outcomes)
- State that these analyses are hypothesis-generating
- Mention the types of methods that may be used (e.g., graphical displays, summary statistics, correlation coefficients)

**Example:**
Exploratory analyses will include correlation of pharmacodynamic biomarkers with clinical outcomes, descriptive summaries of patient-reported outcome measures, and exploratory subgroup analyses by biomarker status. These analyses are hypothesis-generating in nature and will use descriptive statistics, graphical methods, and exploratory correlation analyses (e.g., Spearman correlation coefficients). Results will be interpreted cautiously given the exploratory nature and lack of multiplicity control.

**10.7 Safety Analyses**
Using safety_analysis and study_assessments fields:
- Describe how Adverse Events (AEs), Serious Adverse Events (SAEs), and AESIs will be coded (e.g., MedDRA) and summarized
- Define the treatment-emergent period for AEs
- Describe how laboratory abnormalities and vital signs will be summarized
- State whether formal between-group comparisons of safety data are planned

**Example:**
Safety analyses will be conducted on the Safety Analysis Set. All adverse events will be coded using the Medical Dictionary for Regulatory Activities (MedDRA) version [X.X] and summarized by system organ class and preferred term. Treatment-emergent adverse events (TEAEs) are defined as events that start on or after the first dose of trial intervention and up to 30 days after the last dose.

AEs will be summarized by treatment arm using frequency counts and percentages, overall and by maximum severity grade. SAEs and AEs leading to discontinuation will be summarized separately. Laboratory abnormalities will be summarized using shift tables showing changes from baseline to worst post-baseline value. Vital signs and ECG parameters will be summarized using descriptive statistics and shift tables.
    


**10.8 Other Analyses**
Using study_parts, patient_performance_status, conditions_related_to_primary_disease, and other_analyses fields:
- Describe any pre-specified subgroup analyses (e.g., by age group, geographic region, biomarker status, stratification factors)
- State whether interaction tests will be performed to assess for heterogeneity of treatment effect across subgroups
- Discuss the approach to interpreting subgroup analyses, noting their exploratory nature and the potential impact of multiplicity

**Example:**
Pre-specified subgroup analyses will be conducted for the primary endpoint to explore potential heterogeneity of treatment effect across the following subgroups:
- Age (<65 years vs ≥65 years)
- ECOG performance status (0 vs 1)
- Prior lines of therapy (1 vs ≥2)
- Geographic region

Forest plots will be presented showing the treatment effect estimate and 95% confidence interval for each subgroup. Interaction tests will be performed to formally assess heterogeneity. Given the exploratory nature of these analyses and the lack of multiplicity adjustment, results should be interpreted with caution and considered hypothesis-generating.

**10.9 Interim Analyses**
Using interim_analyses and committees fields:
- State whether any formal interim analyses for efficacy or futility are planned
- If planned, specify the trigger (e.g., after a certain number of events or participants)
- Describe the statistical stopping boundaries (e.g., O'Brien-Fleming alpha-spending function)
- Identify the body responsible for reviewing unblinded results (e.g., independent Data Monitoring Committee)
- Describe measures to maintain trial integrity and control the overall Type I error rate

**Example:**
One interim analysis for futility will be conducted when approximately 50% of the planned PFS events have occurred. The analysis will be reviewed by an independent Data Monitoring Committee (DMC), which will have access to unblinded data. The trial may be stopped early for futility if the conditional power to demonstrate a statistically significant treatment effect falls below 20%.

To protect the overall Type I error rate, a group sequential design with an O'Brien-Fleming spending function will be used. No efficacy stopping boundary is planned at the interim analysis. Trial personnel, investigators, and participants will remain blinded to the interim results to maintain trial integrity.

**10.10 Multiplicity Adjustments**
Using primary_objective_analysis, secondary_objective_analysis, and endpoints fields:
- Describe the strategy for controlling the overall Type I error rate if there are multiple primary or secondary endpoints, or multiple comparisons
- Specify the method to be used (e.g., hierarchical testing procedure, Hochberg procedure, graphical approach)
- If applicable, provide a clear testing schema

**Example:**
This trial has a single primary endpoint; therefore, no multiplicity adjustment is required for the primary analysis. Secondary endpoints will be tested using a hierarchical testing procedure in the following order: (1) Overall Survival, (2) Objective Response Rate, (3) Duration of Response. Each endpoint will be tested at the 0.05 significance level only if the preceding endpoint in the hierarchy achieved statistical significance. This procedure maintains strong control of the family-wise error rate at 0.05.

**10.11 Sample Size Determination**
Using sample_size_determination, discontinuation_trial_intervention_individual, efficacy_assessments, primary_objective_endpoints, and disease_epidemiology fields:
- Restate the key assumptions used for the sample size calculation, justifying each one with references to prior studies or clinical rationale
- Include: expected treatment effect, desired statistical power, overall Type I error rate (alpha), assumed variance/event rates/control group performance, anticipated dropout or discontinuation rate
- Explain how intercurrent events were factored into the sample size calculation
- If simulations were used, state that a detailed report is available separately or in an appendix

**Example:**
The sample size for this trial was determined based on the primary objective of demonstrating superior progression-free survival (PFS) in the investigational arm compared to the control arm.

**Assumptions:**
- Median PFS in the control arm: 6 months (based on historical data from [Reference])
- Expected hazard ratio: 0.67 (representing a 50% improvement in median PFS to 9 months in the investigational arm)
- Statistical power: 90%
- Type I error rate (alpha): 0.05 (two-sided)
- Accrual period: 24 months
- Minimum follow-up: 12 months
- Dropout/loss to follow-up rate: 10%

Based on these assumptions, approximately 240 PFS events are required. Assuming an exponential distribution and accounting for dropout, a total of 300 participants will be randomized (150 per arm). This sample size provides 90% power to detect the specified treatment effect using a two-sided log-rank test at the 0.05 significance level.

**CONSTRAINTS**
* **Strict Adherence to Inputs:** Base all generated content strictly on the information provided in the input data fields
* **Handling Missing Sections:** If information for a specific objective type (e.g., secondary, exploratory) is not provided or is stated as not applicable, write "Not applicable" and omit sub-sections
* **Repetitive Structures:** For sections 10.4 and 10.5, generate a unique and complete set of subsections for each primary or secondary objective
* **Professional Tone:** Use formal, scientific tone suitable for regulatory submission. Avoid conversational language and placeholder text
* **Consistency:** Ensure perfect alignment between estimands, analysis sets, handling of intercurrent events, and primary analysis methods
* **No Meta-Commentary:** Generate only the protocol text itself, without explanations or comments
* **ICH E9/E9(R1) Compliance:** Ensure all statistical descriptions adhere to ICH E9 and E9(R1) principles
* **Output Format:** Generate the content in HTML format with proper HTML tags for headings, paragraphs, lists, and tables. Use <h1>, <h2>, <h3> for headings, <p> for paragraphs, <ul>/<ol> for lists, and <table> for tables.`
  },
  {
    title: '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
    context: '',
    output: '',
    prompt: `**ROLE**
You are an expert medical writer and regulatory affairs specialist with deep expertise in oncology clinical trials. You are a master of authoring clinical trial protocols that are fully compliant with the International Council for Harmonisation (ICH) M11 style and conventions, as well as ICH E6(R2) Good Clinical Practice (GCP).

**TASK**
Your task is to generate the complete text for "Section 11: Trial Oversight and Other General Considerations" of a clinical trial protocol. You will incorporate the provided contextual information and adhere to the specified instructions for each subsection.

**INPUT DATA**
You will be provided with structured data from the protocol planning form:

**CONTEXTUAL SECTIONS:** Reference to other protocol sections for consistency.
* **Section 5.6 (Screening):** For rescreening consent
* **Section 7 (Discontinuation):** For participant transition
* **Section 10 (Statistical Analysis):** For database lock considerations

**TEMPLATE LANGUAGE (AUTHORITATIVE BASE TEXT WHEN PRESENT)**
CRITICAL — these fields come from the CPT (TransCelerate) template library, which mixes two kinds of text in the same block: (a) actual content to copy, marked between \`<Start of common text>\`/\`<End of common text>\` or \`<Start of example text>\`/\`<End of example text>\`, and (b) plain instructional sentences above/between those markers (e.g. "State the importance of...", "Describe procedures for...", "If no committees are involved, state...") that are DIRECTIONS for what to write, not content to output. Copy ONLY the text inside the Start/End markers (tailored per the rules below) into the generated protocol text. NEVER copy the plain instructional sentences themselves into the output — follow them as instructions instead (e.g. an "If X, state Y" instruction means output Y when X applies, not the instruction sentence).
Section 11 content is primarily sourced from "regulatory_requirements.*" fields (which may already contain standard “Current vs Updates” template language). When a regulatory_requirements field is non-empty, you MUST copy-paste it verbatim under the matching subsection heading (HTML format). Do NOT paraphrase or rewrite. Only lightly adjust bracketed placeholders or study-specific cross-references when required for internal consistency.

Additionally, ensure consistency with any safety-related standard language in "template_language_fields" (e.g., Section 9 definitions/reporting/pregnancy language) if those fields are populated; do not contradict them.


**FIELD REFERENCES:**

**11.1 Regulatory and Ethical Considerations**
- Regulatory Ethical Considerations: {{regulatory_requirements.regulatory_ethical_considerations}}
- Informed Consent Rescreening: {{regulatory_requirements.informed_consent_rescreening}}

**11.2 Trial Oversight**
**11.2.1 Investigator Responsibilities**
- Investigator Responsibilities: {{regulatory_requirements.investigator_responsibilities}}

**11.2.2 Sponsor Responsibilities**
- Sponsor Responsibilities: {{regulatory_requirements.sponsor_responsibilities}}

**11.3 Informed Consent Process**
- Informed Consent Rescreening: {{regulatory_requirements.informed_consent_rescreening}}
- Informed Consent Remaining Samples: {{regulatory_requirements.informed_consent_remaining_samples}}
- Reproductive Status/Contraception: {{target_population.reproductive_status_contraception}}

**11.4 Committees**
- Committees: {{regulatory_requirements.committees}}
- Interim Analyses: {{statistical_considerations.interim_analyses}}

**11.5 Insurance and Indemnity**
- Insurance Indemnity: {{regulatory_requirements.insurance_indemnity}}

**11.6 Risk-Based Quality Management**
- Risk Based Quality Management: {{regulatory_requirements.risk_based_quality_management}}

**11.7 Data Governance**
- Data Governance: {{regulatory_requirements.data_governance}}
- Data Protection: {{regulatory_requirements.data_protection}}

**11.8 Data Protection**
- Data Protection: {{regulatory_requirements.data_protection}}

**11.9 Source Reports**
- Source Reports: {{regulatory_requirements.source_data}}
- Study Assessments: {{study_assessments}}

**11.10 Protocol Deviations**
- Protocol Deviations: {{regulatory_requirements.protocol_deviations}}

**11.11 Early Site Closure**
- Early Site Closure: {{regulatory_requirements.early_site_closure}}
- Discontinuation (Individual): {{discontinuation_rules.discontinuation_trial_intervention_individual}}
- Primary Objective Analysis: {{statistical_considerations.primary_objective_analysis}}
- Safety Analysis: {{statistical_considerations.safety_analysis}}

**11.12 Data Dissemination**
- Data Dissemination: {{regulatory_requirements.data_dissemination}}
- Preferred Short Title: {{study_identification.preferred_short_title}}

**INSTRUCTIONS & OUTPUT STRUCTURE**

**11. Trial Oversight and Other General Considerations**

**11.1 Regulatory and Ethical Considerations**
Using regulatory_ethical_considerations field:
- State that the trial will comply with Declaration of Helsinki, CIOMS ethical guidelines, ICH E6(R2) GCP, and all applicable country-specific laws and regulations
- Incorporate any trial-specific ethical considerations (e.g., vulnerable populations)
- State that protocol and ICF will be submitted to IRB/IEC for approval

If the field is empty or "Not Provided", use standard regulatory text.

**Example:**
This trial will be conducted in compliance with the ethical principles that have their origin in the Declaration of Helsinki, the International Council for Harmonisation of Technical Requirements for Pharmaceuticals for Human Use (ICH) E6(R2) Good Clinical Practice (GCP) guidelines, the ethical guidelines from the Council for International Organizations of Medical Sciences (CIOMS), and all applicable national and local laws and regulations of the countries in which the trial is conducted.

The protocol, Informed Consent Form(s) (ICF), and any other required trial-related documents will be submitted to an Institutional Review Board (IRB) or Independent Ethics Committee (IEC) for review and approval prior to trial initiation at each site.


**11.2 Trial Oversight**
Provide an overview of oversight responsibilities using the subsections below.

**11.2.1 Investigator Responsibilities**
Using investigator_responsibilities field, detail responsibilities for:
- Delegation of duties
- Participant safety
- GCP compliance
- Source reports maintenance
- Reporting of AEs and protocol deviations

If field is empty, use standard investigator responsibilities text.

**Example:**
The Principal Investigator at each site is responsible for the overall conduct of the trial and must ensure that the trial is conducted in accordance with this protocol, GCP, and all applicable regulatory requirements. Key responsibilities include:
- Ensuring only qualified, trained personnel perform trial-related procedures as documented on a delegation log
- Protecting the rights, safety, and welfare of trial participants
- Maintaining adequate and accurate source documentation
- Ensuring timely and accurate completion of all case report forms
- Reporting all adverse events and protocol deviations to the sponsor and IRB/IEC as required
- Ensuring proper storage, handling, and accountability of investigational product

**11.2.2 Sponsor Responsibilities**
Using sponsor_responsibilities field, detail responsibilities for:
- Trial design and protocol compliance
- Safety monitoring
- Data oversight
- CRO/vendor oversight (if applicable)

If field is empty, use standard sponsor responsibilities text.

**Example:**
The sponsor is responsible for the overall design, conduct, and oversight of this trial. Key responsibilities include:
- Ensuring the trial is designed and conducted in accordance with the protocol, GCP, and applicable regulations
- Providing oversight of Contract Research Organizations (CROs) and other vendors
- Monitoring participant safety through ongoing review of safety data
- Ensuring data quality, integrity, and security
- Providing the investigational product and all necessary trial materials
- Ensuring timely reporting of safety information to regulatory authorities and ethics committees


**11.3 Informed Consent Process**
Using informed_consent_rescreening and informed_consent_remaining_samples fields, describe the consent process:
- Who will obtain consent and how
- Use of Legally Authorized Representatives (LARs) or assent
- Provisions for multilingual versions and comprehension checks
- Statement about voluntary participation and right to withdraw

**Example:**
Informed consent will be obtained by the Principal Investigator or a qualified designee before any trial-related procedures are performed. The Investigator will explain the trial purpose, procedures, risks, and benefits to the participant using language understandable to the participant. For participants unable to provide consent, a legally authorized representative may provide consent on their behalf in accordance with local regulations.

The ICF will be available in all locally relevant languages. Participants will have adequate time to review the ICF, ask questions, and make an informed decision about participation. Participation is voluntary, and participants may withdraw from the trial at any time without penalty or loss of benefits to which they are otherwise entitled.

**11.3.1 Informed Consent for Rescreening**
Using informed_consent_rescreening field, state whether a new ICF will be required for rescreening. Cross-reference Section 5.6.

If field is empty, use standard rescreening consent language.

**Example:**
Participants who require rescreening as described in Section 5.6 must provide new informed consent if the rescreening occurs more than 30 days after the initial consent signature. For rescreening within 30 days, the original ICF remains valid.

**11.3.2 Informed Consent for Use of Remaining Samples in Exploratory Research**
Using informed_consent_remaining_samples field, describe the process for optional sample use consent.

If field is empty or "Not Provided", state: "Not applicable - no optional exploratory research planned for this trial."

**Example:**
Consent for optional exploratory research on remaining biological samples will be collected via a separate, optional section within the main Informed Consent Form. Participants may decline to provide consent for this optional research without any impact on their eligibility or participation in the main trial. Samples from participants who do not consent will be destroyed according to the protocol-specified timeline.


**11.4 Committees**
Using committees and interim_analyses fields, list and briefly describe each oversight committee.

If no committees are involved, state: "Not applicable - no oversight committees are established for this trial."

**Example:**
The following independent committees have been established to provide oversight for this trial:

**Data Monitoring Committee (DMC):** An independent DMC will monitor accumulating safety and efficacy data and make recommendations to the sponsor regarding trial continuation, modification, or termination. The DMC charter is maintained in the Trial Master File.

**Endpoint Adjudication Committee:** An independent committee of experts will adjudicate all primary endpoint events to ensure consistent and unbiased assessment. The charter defining adjudication procedures is maintained in the Trial Master File.

**11.5 Insurance and Indemnity**
Using insurance_indemnity field, summarize:
- Sponsor-provided participant insurance (e.g., for trial-related injury)
- Indemnity for investigators and trial staff
- Country-specific legal requirements if relevant

If field is empty or "Not Provided", use standard insurance language.

**Example:**
The sponsor will provide insurance coverage for participants in accordance with local legal requirements for compensation in the event of trial-related injury. Investigators and trial staff will be indemnified by the sponsor for claims arising from the conduct of the trial in accordance with the protocol and applicable regulations. Details of insurance and indemnity provisions are provided in the Clinical Trial Agreement between the sponsor and each site.

**11.6 Risk-Based Quality Management**
Using risk_based_quality_management field, describe:
- Critical-to-quality factors (e.g., informed consent, randomization, blinding, primary endpoint assessment)
- Risk mitigation strategies (e.g., centralized monitoring, targeted source data verification)
- Reference to a separate quality management plan if applicable

If field is empty or "Not Provided", use standard quality management language.

**Example:**
This trial will employ a risk-based approach to quality management in accordance with ICH E6(R2). Critical-to-quality factors have been identified, including proper informed consent documentation, accurate randomization, maintenance of blinding, and reliable primary endpoint assessment. Risk mitigation strategies include centralized statistical monitoring, targeted source data verification for critical data points, and real-time data review. A detailed Quality Management Plan describes the specific risk mitigation activities and is maintained in the Trial Master File.

**11.7 Data Governance**
Using data_governance field, summarize:
- Data integrity and traceability methods
- Monitoring and data transfer processes
- Access control, audit trails, data retention policy

If field is empty or "Not Provided", use standard data governance language.

**Example:**
Trial data will be collected via a validated electronic data capture (EDC) system that ensures data integrity, security, and traceability. All data entries and changes will be tracked via comprehensive audit trails. Access to the EDC system will be controlled through unique user credentials and role-based permissions. Data transfers between systems will be validated and documented. Trial data will be retained in accordance with ICH E6(R2) requirements and applicable regulations, typically for at least 25 years after trial completion or as required by local laws, whichever is longer.

**11.8 Data Protection**
Using data_protection field, describe:
- Measures for privacy and confidentiality of participant data
- Compliance with applicable regulations (e.g., GDPR, HIPAA)
- Procedures for data breach detection and response

If field is empty or "Not Provided", use standard data protection language.

**Example:**
Participant confidentiality will be maintained in accordance with applicable data protection regulations, including the European Union General Data Protection Regulation (GDPR) and the United States Health Insurance Portability and Accountability Act (HIPAA), as applicable. Participants will be identified in the trial database by a unique participant identification number rather than by name. Personal identifiable information will be stored separately from clinical data with restricted access. In the event of a suspected or confirmed data breach, the sponsor will follow established procedures for investigation, notification, and remediation in accordance with applicable legal and regulatory requirements.


**11.9 Source Reports**
Using source_data and study_assessments fields:

**Introduction:**
Explain importance of source reports for participant safety and regulatory compliance

**Investigator Expectations:**
- Maintain accurate, contemporaneous records
- Ensure access to authorized monitors and auditors

**Trial Monitor Expectations:**
- Source reports verification, query resolution
- Ongoing assessment of protocol and GCP compliance

**Identification of Source Reports:**
- List examples (e.g., lab reports, ECG printouts, clinic notes, imaging reports)
- Reference location of complete source reports list

If field is empty or "Not Provided", use standard source reports language.

**Example:**
Source reports are the original documents, data, and records from which trial data are obtained. Source reports are essential for verifying the accuracy and completeness of data reported in the case report forms and are critical for participant safety and regulatory compliance.

The Investigator is responsible for maintaining accurate and contemporaneous source documentation for all trial participants. Source reports must be available for review by trial monitors, auditors, and regulatory inspectors.

Trial monitors will perform source reports verification to confirm that data entered into the eCRF are accurate, complete, and consistent with source documents. Monitors will also assess ongoing compliance with the protocol and GCP.

Examples of source reports for this trial include: laboratory reports, ECG printouts, imaging reports, clinic visit notes, informed consent forms, drug accountability logs, and adverse event documentation. A complete list of source reports is provided in the Source Reports Identification Plan maintained in the Trial Master File.

**11.10 Protocol Deviations**
Using protocol_deviations field, describe:
- How deviations will be detected and documented
- Review and classification process (e.g., major vs minor)
- Reporting responsibilities to sponsor and IRBs

If field is empty or "Not Provided", use standard protocol deviation language.

**Example:**
Protocol deviations are any departures from the approved protocol. All deviations must be documented promptly by the site, reviewed by the sponsor, and classified according to their potential impact on participant safety, data integrity, or trial conduct.

Major deviations (those that may significantly affect participant safety or the integrity of trial data) must be reported to the sponsor within 24 hours and to the IRB/IEC according to local requirements. Minor deviations will be documented in the participant's source records and reported to the sponsor via routine monitoring activities. The sponsor will maintain a centralized deviation log and will implement corrective and preventive actions as needed.

**11.11 Early Site Closure**
Using early_site_closure and discontinuation fields:

**Decision Rights for Site Closure:**
State that both sponsor and investigator may initiate early closure

**Criteria for Early Closure:**
List potential criteria (e.g., enrollment failure, safety concerns, noncompliance, administrative reasons)

**Responsibilities Following Closure:**
- Notification of IRB, regulatory agencies
- Participant transition plan and medical follow-up
- Data closeout procedures

If field is empty or "Not Provided", use standard early closure language.

**Example:**
A trial site may be closed early at the discretion of the sponsor or the Principal Investigator. Criteria for early site closure include but are not limited to: failure to meet enrollment targets, repeated or significant non-compliance with the protocol or GCP, safety concerns, or administrative reasons.

In the event of early site closure, the Investigator must promptly notify the IRB/IEC and provide a plan for transitioning participants to alternative care or to another trial site. The sponsor will ensure appropriate medical follow-up for all participants and will complete all required regulatory notifications. Data from closed sites will be included in the final analysis unless the site is closed due to data integrity concerns.

**11.12 Data Dissemination**
Using data_dissemination and preferred_short_title fields:
- State policy on trial registration (e.g., ClinicalTrials.gov, EudraCT)
- Describe results disclosure policy (e.g., journal publication, conference presentation)
- Note compliance with trial transparency standards (e.g., WHO, ICMJE)

If field is empty or "Not Provided", use standard dissemination language.

**Example:**
This trial will be registered on ClinicalTrials.gov and/or other appropriate public trial registries prior to enrollment of the first participant, in accordance with the International Committee of Medical Journal Editors (ICMJE) requirements and applicable regulatory requirements.

Results of this trial will be disclosed through peer-reviewed publication and/or presentation at scientific conferences, regardless of outcome. Summary results will be posted on ClinicalTrials.gov and other applicable registries within 12 months of trial completion, in compliance with FDA Amendment Act (FDAAA) requirements and other applicable transparency regulations. Individual participant data will not be shared publicly but may be made available for qualified research purposes in accordance with the sponsor's data sharing policy.

**CONSTRAINTS**
* **Tone and Style:** Maintain a formal, objective, and authoritative tone aligned with ICH M11 standard for clinical trial protocols
* **Do Not Invent Information:** Only use the information provided in the input data fields
* **Placeholder Handling:** If a field is empty or contains "Not Provided", use standard regulatory language appropriate for that subsection
* **Cross-Referencing:** When appropriate, cross-reference other sections (e.g., "as detailed in Section 5.6")
* **Completeness:** Address every subsection from 11.1 to 11.12
* **No Meta-Commentary:** Generate only the protocol text itself, without explanations or comments`    
  },
  {
    title: '12. APPENDIX: SUPPORTING DETAILS',
    context: '',
    output: '',
    prompt:  `Use inputs from:
    The clinical laboratory manual
    
    
    Global regulatory affairs documentation
    
    
    The trial change control tracker or protocol version history

    IMPORTANT — uploaded appendix documents from Clinical Intake:
    If clinical intake appendices include contentHtml (uploaded PDF/Word/image content), treat that HTML as authoritative source material for the matching appendix subsection. Preserve tables, text, and <img> tags. Do not strip images. Generate or fill only subsections that lack uploaded content; clearly state "Not applicable." where empty.
    
    
    Generate each subsection using the structure below. If not applicable, retain the section header and clearly state "Not applicable." All tables should be included in an editable format where possible.
    12.1 Clinical Laboratory Tests
    Prompt:
    From:
    The trial's Schedule of Activities
    
    
    Lab test specifications
    
    
    Safety monitoring requirements
    
    
    Generate a table listing parameters included in each laboratory test panel. Include:
    Panel name (e.g., Hematology, Chemistry, Coagulation, Urinalysis)
    
    
    Specific tests under each panel
    
    
    Units of measurement (if standardized)
    
    
    Method of analysis (if relevant or non-standard)
    
    
    Calculation formulas (e.g., for eGFR, CrCl) with references
    
    
    Example table format:
    Panel
    Test
    Unit
    Notes/Calculation
    Hematology
    Hemoglobin
    g/dL
    
    
    Chemistry
    Creatinine
    mg/dL
    Used to calculate eGFR
    Coagulation
    PT/INR
    Seconds/INR
    
    
    Urinalysis
    Protein, Glucose, pH
    Qualitative
    Dipstick method
    
    12.2 Country/Region-Specific Differences
    Prompt:
    From:
    Regulatory team feedback
    
    
    Country-specific IRB/EC requirements
    
    
    Contraception or documentation rules that deviate from global protocol
    
    
    If applicable, list:
    The country/region
    
    
    The specific regulatory requirement
    
    
    The protocol section affected
    
    
    Any changes or additions that apply only locally (e.g., different contraception duration, document retention period)
    
    
    Format example:
    [United States]
    - Requirement: Consent process must include HIPAA-specific language.
    - Protocol Clarification: Section 11.3 (Informed Consent Process) to include HIPAA reference for U.S. sites only.
    
    [Japan]
    - Requirement: Document retention for 30 years (vs. 25 globally).
    - Protocol Clarification: Section 11.1 (Regulatory and Ethical Considerations) includes this under Japan-specific addendum.
    
    If there are no differences, write: Not applicable.
    12.3 Prior Protocol Amendment(s)
    Prompt:
    From:
    Protocol version history
    
    
    Sponsor protocol tracking logs
    
    
    Choose the applicable starting statement:
    If no amendments:
     "Not applicable. This protocol has not been amended."
    
    
    If this is the first amendment:
     "Not applicable. This is the first protocol amendment."
    
    
    If prior amendments exist:
     Begin with:
     "This protocol has been amended previously. The Protocol Amendment Summary of Changes for the current amendment is located directly before the Table of Contents. Prior amendment(s) to this protocol are summarized in Table 12-1."
    
    
    Create the following table with caption and in-text cross-reference:
    Caption: Table 12-1: Summary of Protocol Amendments
    In-text (required before the table): write a title-specific sentence that starts with the table label, e.g. "Table 12-1 summarizes the protocol amendments for this study." (never "Key details are summarized…" or "the table below").
    Document
    Sponsor Approval Date
    Approximate Enrollment When Approved
    Amendment 2
    YYYY-MM-DD
    70% globally
    Amendment 1
    YYYY-MM-DD
    30% globally
    Original Protocol
    YYYY-MM-DD
    0
    
    Then include a summary of changes per amendment (also caption each additional table as Table 12-2, Table 12-3, … and cross-reference each formally in the preceding text):
    Overview of Changes in Amendment 2 (YYYY-MM-DD)
    
    | Description of Change                      | Rationale for Change                     | Section # and Name                 |
    |-------------------------------------------|------------------------------------------|-----------------------------------|
    | Clarified inclusion criterion related to BMI | Improve enrollment accuracy             | Section 5.2 Inclusion Criteria    |
    | Added exploratory biomarker objective      | Align with emerging scientific interest | Section 3.3 Exploratory Objectives|
    
    Repeat the table for additional amendments in reverse chronological order.

    12.4 Additional Supporting Materials
    Prompt:
    From:
    Clinical Intake appendices row titled 12.4 (uploaded PDF/Word/image contentHtml)
    
    
    If uploaded appendix materials exist for 12.4, include that authoritative HTML (preserve tables, text, and images). If none, write: Not applicable.

    **SCOPE LIMIT**
    * Generate subsections 12.1 through 12.4 in this section.
    * Do NOT generate Section 13 (Glossary of Terms and Abbreviations) or Section 14 (References) here — those are separate protocol sections.
    * End this section after 12.4 Additional Supporting Materials.   ` 
  },
  {
    title: '13. APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS',
    context: '',
    output: '',
    prompt: `Generate Section 13: Appendix – Glossary of Terms and Abbreviations.

Create a comprehensive glossary listing all technical terms, abbreviations, acronyms, and protocol-specific phrases used throughout the protocol. Use a tabular format:

| Term/Abbreviation | Definition |
|---|---|
| AE | Adverse Event |
| GCP | Good Clinical Practice |

Include regulatory, scientific, protocol-specific, and statistical abbreviations. Define every abbreviation used in the protocol. If not applicable, state "Not applicable."`,
  },
  {
    title: '14. APPENDIX: REFERENCES',
    context: '',
    output: '',
    prompt: `You are an expert medical writer specializing in oncology and clinical trial protocol writing, aligning with ICH M11 style and conventions.

# TASK

Generate **Section 14: APPENDIX – REFERENCES** of a clinical trial protocol.

This is the **only** protocol section that may contain the full bibliographic reference list.

---

# PURPOSE

Compile a complete **AMA (American Medical Association) style numbered reference list** for all sources cited with in-text numbered citations anywhere in the protocol—especially citations from **Section 2: INTRODUCTION**, and any other sections that use numbered AMA in-text citations.

---

# CITATION / FORMAT REQUIREMENTS (AMA STYLE)

- Use numbered references in the same order as first appearance of citation numbers in the protocol text (1, 2, 3, …).
- Each reference number must correspond to only one reference.
- Reuse the same number for the same source whenever it was cited again in the text.
- Format each entry in AMA style (authors, article/title, journal or source, year, volume/issue/pages, DOI or URL when available).
- Include authentic, traceable sources only: peer-reviewed publications, clinical practice guidelines, regulatory documents, and recognized scientific organizations (WHO, FDA, EMA, NCCN, ESMO, ASCO, NIH, CDC), plus Investigator's Brochure references when cited.
- Do not fabricate citations or invent bibliographic details.
- Do not use author-date or Harvard-style listings as the primary format.
- Do not use Vancouver/ICMJE as a substitute if AMA can be applied; prefer AMA to match Section 2 in-text citations.

---

# CONTENT REQUIREMENTS

- Title the section appropriately (e.g., **14 APPENDIX: REFERENCES** or **References**).
- Provide the full numbered list only—no introductory essay.
- Align reference numbers with the in-text citation numbers used in Section 2 and other cited sections (from protocol context / CLINICAL INTAKE DATA / previously generated section text when available).
- Include Investigator's Brochure references, pivotal publications, and regulatory guidance cited in the protocol text.
- If no references are cited anywhere in the protocol, state "Not applicable."

---

# FINAL QUALITY CHECK

✓ This section contains the complete AMA-style numbered reference list for the protocol  
✓ No duplicate full reference lists belong in Section 2 or other narrative sections  
✓ Numbering matches in-text citations (especially Section 2)  
✓ References are authentic and traceable  
✓ Output is suitable for direct inclusion in the protocol  

IMPORTANT: Return only Section 14 content. Do NOT wrap the output in markdown code fences.`,
  },
];

/**
 * Get predefined sections with enhanced context that includes both clinical intake data and template language settings.
 * This integration allows the AI to generate sections that follow the selected template language preferences.
 * 
 * @param {string} projectId - The project ID to fetch data for
 * @returns {Array} Array of predefined sections with enhanced context
 */
export const getPredefinedSections = async (projectId) => {
  try {
    // Fetch latest clinical intake data for the specific project
    const latestData = await fetchLatestClinicalIntakeData(projectId);
    const formattedContext = formatClinicalIntakeForContext(latestData);

    // Fetch template language data - use project default template for field-level template selections
    const templateLanguageData = await templateLanguageService.getTemplateLanguageDataByProject(projectId);
    
    console.log('Raw Clinical Intake Data:', JSON.stringify(latestData, null, 2));
    console.log('Formatted Context:', formattedContext);
    console.log('Template Language Data:', templateLanguageData);

    // Create enhanced context that includes both clinical intake and template language
    let enhancedContext = formattedContext;
    
    // Check for field-level template selections in clinical intake data
    const fieldTemplateSelections = latestData?.fieldTemplateSelections || {};
    const hasFieldTemplates = Object.keys(fieldTemplateSelections).length > 0;
    
    if (hasFieldTemplates && templateLanguageData && templateLanguageData.data) {
      enhancedContext += '\n\n--- FIELD-LEVEL TEMPLATE SELECTIONS ---\n';
      enhancedContext += `Fields using template data:\n`;
      
      // List fields that are using template data
      Object.entries(fieldTemplateSelections).forEach(([fieldPath, useTemplate]) => {
        if (useTemplate) {
          enhancedContext += `- ${fieldPath}: Using template data\n`;
        }
      });
      enhancedContext += '\n';
      
      // Add template language content for relevant sections
      Object.entries(templateLanguageData.data).forEach(([title, data]) => {
        if (data.content) {
          enhancedContext += `${title}:\n${data.content}\n\n`;
        }
        if (data.userSuggestion) {
          enhancedContext += `${title} - User Suggestion:\n${data.userSuggestion}\n\n`;
        }
      });
    } else if (templateLanguageData && templateLanguageData.data) {
      // Fallback to default template language data if no field-level template selections
      enhancedContext += '\n\n--- TEMPLATE LANGUAGE SETTINGS ---\n';
      enhancedContext += `Selected Template: ${templateLanguageData.template}\n\n`;
      
      // Add template language content for relevant sections
      Object.entries(templateLanguageData.data).forEach(([title, data]) => {
        if (data.content) {
          enhancedContext += `${title}:\n${data.content}\n\n`;
        }
        if (data.userSuggestion) {
          enhancedContext += `${title} - User Suggestion:\n${data.userSuggestion}\n\n`;
        }
      });
    }

    // Return sections with the enhanced context
    const sectionsWithContext = DEFAULT_SECTIONS.map(section => ({
      ...section,
      context: enhancedContext
    }));
    
    // Debug logging for chunked generation
    const section4 = sectionsWithContext.find(s => s.title === '4. TRIAL DESIGN');
    console.log('🔍 getPredefinedSections - Section 4 details:', {
      title: section4?.title,
      useChunkedGeneration: section4?.useChunkedGeneration,
      hasChunks: !!section4?.chunks,
      chunkKeys: section4?.chunks ? Object.keys(section4.chunks) : 'no chunks',
      fullSection: section4
    });
    
    // Also check the original DEFAULT_SECTIONS
    const originalSection4 = DEFAULT_SECTIONS.find(s => s.title === '4. TRIAL DESIGN');
    console.log('🔍 getPredefinedSections - Original Section 4 details:', {
      title: originalSection4?.title,
      useChunkedGeneration: originalSection4?.useChunkedGeneration,
      hasChunks: !!originalSection4?.chunks,
      chunkKeys: originalSection4?.chunks ? Object.keys(originalSection4.chunks) : 'no chunks'
    });
    
    return sectionsWithContext;
  } catch (error) {
    console.error('Error getting predefined sections:', error);
    // Return default sections with empty context if there's an error
    return DEFAULT_SECTIONS;
  }
};

// Section titles/prompts/chunking config only — no clinical intake or
// template language data mixed into `context` (unlike getPredefinedSections
// above). Used by the "Generate from USDM" path, which sources its context
// exclusively from an uploaded USDM catalogue, not the clinical intake form.
export const getDefaultProtocolSections = () => DEFAULT_SECTIONS;
